import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { customerFacingEdgeName, customerFacingSiteName } from '@aot-aiops/shared';
import { FindingSummary } from './finding-label';
import {
  BookOpen,
  Bot,
  BriefcaseBusiness,
  Building2,
  ChevronRight,
  CircleAlert,
  FileText,
  GitBranch,
  HardDrive,
  HeartPulse,
  LayoutDashboard,
  LogOut,
  Menu,
  MapPinned,
  Maximize2,
  MonitorUp,
  Network,
  Search,
  ScrollText,
  Server,
  PlugZap,
  Settings,
  ShieldCheck,
  TrendingUp,
  X,
} from 'lucide-react';
import { api, ApiError } from './api';
import { ReportsView } from './reports-view';
import {
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  formatDate,
  HealthIndicator,
  LoadingState,
  MetricCard,
  PageHeader,
  parseApiDate,
  PlatformBadge,
  relativeTime,
  SectionCard,
  StatusBadge,
  statusTone,
  Tabs,
  TrendIndicator,
} from './components';
import { customerFacingEntityName } from './presentation';
import { AotProductLockup, ThemeQuickToggle } from './brand';
import {
  applyBranding,
  DEFAULT_BRANDING,
  TenantBrandLockup,
  type PublicBranding,
} from './branding';
import {
  boundedInventoryPage,
  buildOperationalEvents,
  deriveHealthFactors,
  matchesIncidentFilter,
  matchesServiceHealth,
  parsePortalRoute,
  portalPath,
  similarIncidents,
  type CommandResult,
} from './intelligence';
import {
  ContextActionMenu,
  FreshnessIndicator,
  HealthBreakdown,
  IncidentFactStrip,
  MaintenanceAction,
  OperationalTimeline,
  RelationshipBreadcrumb,
  SavedViewMenu,
  TopologyAction,
  TopologyView,
} from './operational-components';
import {
  applyThemeToDocument,
  nextExplicitTheme,
  observeSystemTheme,
  persistThemePreference,
  readThemePreference,
  resolveTheme,
  type ResolvedTheme,
  type ThemePreference,
} from './theme';
import { useLiveRefresh, type RefreshMode } from './live-refresh';
import { isSameDetailSelection, readSiteDetailTab, writeSiteDetailState } from './view-state';
import { createTimezoneState, setActiveTimeZone } from './timezone';
import {
  NetworkTrafficChart,
  TimeSeriesChart,
  displayMetricUnit,
  formatMetricValue,
  humanBytes,
  metricUnit,
  humanBitsPerSecond,
  interfaceBitsPerSecond,
  metricLabel,
  temperatureSensorLabel,
  type ChartOverlay,
} from './metric-chart';
import {
  baselineMaturity,
  behaviorMetricSummaries,
  canonicalMetricId,
  humanCapabilityState,
  humanDuration,
  networkSummary,
} from './asset-observability';
import { AssetVisual, setRuntimeAssetVisualRegistry } from './asset-visuals';
import {
  assetNavigation,
  assetSectionForTab,
  goldenMetricIds,
  inEvidenceWindow,
  type EvidenceWindow,
} from './asset-experience';
import {
  AIOpsValuePanel,
  AssetTimeContext,
  InvestigationWorkspace,
  MonitoringCoveragePanel,
  PopulationOutliersPanel,
  PredictiveRiskPanel,
} from './asset-experience-components';
import { guestFilesystemRows } from './guest-filesystem';

const SecuritySettings = lazy(async () => {
  const module = await import('./security-settings');
  return { default: module.SecuritySettings };
});

const CommandPalette = lazy(async () => {
  const module = await import('./command-palette');
  return { default: module.CommandPalette };
});

const WhiteLabelSettings = lazy(async () => {
  const module = await import('./white-label');
  return { default: module.WhiteLabelSettings };
});

const TurnstileWidget = lazy(async () => {
  const module = await import('./turnstile');
  return { default: module.TurnstileWidget };
});

const CustomerOnboarding = lazy(async () => {
  const module = await import('./customer-onboarding');
  return { default: module.CustomerOnboarding };
});
const LogsView = lazy(async () => {
  const module = await import('./observability');
  return { default: module.LogsView };
});
const IntegrationsView = lazy(async () => {
  const module = await import('./observability');
  return { default: module.IntegrationsView };
});
const MapsView = lazy(async () => {
  const module = await import('./observability');
  return { default: module.MapsView };
});
const EdgesView = lazy(async () => {
  const module = await import('./edge-management');
  return { default: module.EdgesView };
});
const SiteMonitoringSetup = lazy(async () => {
  const module = await import('./site-monitoring');
  return { default: module.SiteMonitoringSetup };
});
import './styles.css';

type View =
  | 'Overview'
  | 'Sites'
  | 'Edges'
  | 'Assets'
  | 'Logs'
  | 'Integrations'
  | 'Maps'
  | 'Topology'
  | 'Incidents'
  | 'Services'
  | 'Capacity'
  | 'Reports'
  | 'OpsAI'
  | 'Knowledge'
  | 'Settings'
  | 'AOT Operations'
  | 'Customer Onboarding'
  | 'NOC Wallboard';
import { UsersAccess, AcceptInvitation, arrivalInvitationToken, PendingMemberships, WorkspaceSwitcher, CreateCustomerForm } from './user-access';
import { roleLabel } from '@aot-aiops/shared';
import { FederatedButtons } from './sign-in-methods';

type Session = {
  user: {
    id: string;
    email: string;
    role: string;
    timezoneMode?: 'AUTO_BROWSER' | 'WORKSPACE_DEFAULT' | 'SPECIFIC';
    timeZone?: string | null;
  };
  tenant: { id: string; name?: string; timeZone?: string; accessClass?: string };
  security?: { mfaEnrollmentRequired: boolean };
};
type Dashboard = {
  health: string;
  siteCount: number;
  assetCount: number;
  openIncidents: number;
  criticalCount: number;
  warningCount: number;
  agents: any[];
  findings: any[];
};
type NavigateOptions = {
  question?: string;
  detailId?: string;
  topologyAssetId?: string;
  topologyServiceId?: string;
  context?: 'dashboard' | 'incident' | 'asset' | 'service' | 'capacity';
  query?: Record<string, string | number | boolean | undefined>;
  replace?: boolean;
};
type Navigate = (view: View, options?: NavigateOptions) => void;

const primaryNavigation: Array<{
  view: View;
  label?: string;
  icon: React.ComponentType<{ size?: number }>;
}> = [
  { view: 'Overview', icon: LayoutDashboard },
  { view: 'Services', icon: HeartPulse },
  { view: 'Sites', icon: Building2 },
  { view: 'Edges', icon: Server },
  { view: 'Assets', icon: Network },
  { view: 'Maps', icon: MapPinned },
  { view: 'Logs', icon: ScrollText },
  { view: 'Integrations', icon: PlugZap },
  { view: 'Incidents', icon: CircleAlert },
  { view: 'Topology', icon: GitBranch },
  { view: 'Capacity', icon: TrendingUp },
  { view: 'Reports', icon: FileText },
  { view: 'OpsAI', icon: Bot },
  { view: 'Knowledge', icon: BookOpen },
  { view: 'Settings', icon: Settings },
];

const aotNavigation = [
  primaryNavigation[0]!,
  { view: 'AOT Operations' as View, label: 'Customers', icon: BriefcaseBusiness },
  ...primaryNavigation.slice(1, -1),
  { view: 'NOC Wallboard' as View, label: 'Wallboard', icon: MonitorUp },
  primaryNavigation.at(-1)!,
];

function currentEnvironment() {
  const trusted = document.documentElement.dataset.environment;
  if (trusted) return trusted;
  if (window.location.hostname === 'ops.aot.ph') return 'production';
  if (
    window.location.hostname === 'staging.ops.aot.ph' ||
    window.location.hostname.endsWith('.staging.ops.aot.ph')
  )
    return 'staging';
  return 'local';
}
const isStaging = () => currentEnvironment() === 'staging';
// Internal validation records are available only through authorized diagnostics,
// never through ordinary customer operational views.
const includeDemo = () => false;

function assetPlatformSource(item: any) {
  const source = String(item.source ?? 'unknown').toLowerCase();
  const type = String(item.assetType ?? item.asset_type ?? '').toLowerCase();
  return source !== 'demo'
    ? source
    : type.startsWith('proxmox')
      ? 'proxmox'
      : type.startsWith('vmware')
        ? 'vmware'
        : type.startsWith('hyperv')
          ? 'hyperv'
          : 'demo';
}

function AssetPlatform({ item }: { item: any }) {
  const source = String(item.source ?? 'unknown').toLowerCase();
  const platform = assetPlatformSource(item);
  return (
    <span className="badge-stack">
      <PlatformBadge source={platform} />
      {source === 'demo' && platform !== 'demo' && <PlatformBadge source="demo" />}
    </span>
  );
}

function Login({
  onLogin,
  resolvedTheme,
  onToggleTheme,
  branding,
}: {
  onLogin: (session: Session) => void;
  resolvedTheme: ResolvedTheme;
  onToggleTheme: () => void;
  branding: PublicBranding;
}) {
  const resetToken = new URLSearchParams(window.location.search).get('reset_token');
  const [mode, setMode] = useState<'login' | 'forgot' | 'reset' | 'mfa'>(
    resetToken ? 'reset' : 'login',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileRevision, setTurnstileRevision] = useState(0);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const receiveTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get('federation');
    if (!result) return;
    window.history.replaceState(null, '', '/');
    if (result === 'mfa') void api<{pendingToken:string}>('/v1/auth/federation/result', {method:'POST'})
      .then(v => {setPendingToken(v.pendingToken);setMode('mfa');})
      .catch(() => setError('The provider sign-in expired. Please sign in again.'));
    else if (result === 'membership_required') setMessage('Your sign-in identity is recognized. Use a workspace invitation to continue. Signing in does not grant workspace access.');
    else setError(result === 'link_existing_account' ? 'Sign in to your existing account, then connect this provider in Security settings.' : 'Provider sign-in could not be completed. Check your invitation, workspace policy, or configured sign-in methods.');
  }, []);
  useEffect(() => {
    api<{ turnstileSiteKey: string | null }>('/v1/auth/config')
      .then((result) => setTurnstileSiteKey(result.turnstileSiteKey))
      .catch(() => setTurnstileSiteKey(null));
  }, []);
  function resetChallenge() {
    setTurnstileToken('');
    setTurnstileRevision((value) => value + 1);
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (mode === 'forgot') {
        const result = await api<{ message: string }>('/v1/auth/password-reset/request', {
          method: 'POST',
          body: JSON.stringify({ email, turnstileToken: turnstileToken || undefined }),
        });
        setMessage(result.message);
        resetChallenge();
      } else if (mode === 'reset') {
        if (password !== confirmPassword) throw new Error('The new passphrases do not match.');
        await api('/v1/auth/password-reset/complete', {
          method: 'POST',
          body: JSON.stringify({
            token: resetToken,
            password,
            turnstileToken: turnstileToken || undefined,
          }),
        });
        window.history.replaceState(null, '', '/');
        setPassword('');
        setConfirmPassword('');
        setMode('login');
        setMessage('Password changed. Sign in with your new passphrase.');
        resetChallenge();
      } else if (mode === 'mfa') {
        const result = await api<{ csrfToken: string }>('/v1/auth/mfa/challenge', {
          method: 'POST',
          body: JSON.stringify({
            pendingToken,
            ...(useRecovery ? { recoveryCode: mfaCode } : { code: mfaCode }),
            turnstileToken: turnstileToken || undefined,
          }),
        });
        sessionStorage.setItem('aot_csrf', result.csrfToken);
        onLogin(await api<Session>('/v1/auth/me'));
      } else {
        const result = await api<{
          csrfToken?: string;
          mfaRequired?: boolean;
          pendingToken?: string;
        }>('/v1/auth/login', {
          method: 'POST',
          body: JSON.stringify({ email, password, turnstileToken: turnstileToken || undefined }),
        });
        if (result.mfaRequired && result.pendingToken) {
          setPendingToken(result.pendingToken);
          setPassword('');
          setMode('mfa');
          resetChallenge();
        } else if (result.csrfToken) {
          sessionStorage.setItem('aot_csrf', result.csrfToken);
          onLogin(await api<Session>('/v1/auth/me'));
        }
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to sign in');
      resetChallenge();
    } finally {
      setBusy(false);
    }
  }
  const heading =
    mode === 'forgot'
      ? 'Recover your account'
      : mode === 'reset'
        ? 'Choose a new password'
        : mode === 'mfa'
          ? 'Verify your sign-in'
          : 'Sign in';
  const description =
    mode === 'forgot'
      ? 'Enter your account email. The response is intentionally identical for every address.'
      : mode === 'reset'
        ? 'Use a long passphrase of at least 15 characters. Existing sessions will be signed out.'
        : mode === 'mfa'
          ? 'Use your authenticator or one unused recovery code to complete sign-in.'
          : 'Continue to your operations workspace.';
  const turnstileAction =
    mode === 'mfa' ? 'mfa_challenge' : mode === 'login' ? 'login' : 'password_reset';
  return (
    <main className="login-shell">
      <div className="login-theme-control">
        <ThemeQuickToggle resolvedTheme={resolvedTheme} onToggle={onToggleTheme} />
      </div>
      <section className="login-brand" aria-label={`${branding.displayName} product introduction`}>
        {branding.tenantDomain ? (
          <TenantBrandLockup branding={branding} dark={resolvedTheme === 'dark'} />
        ) : (
          <AotProductLockup />
        )}
        <h1>{branding.loginTitle ?? 'Operational clarity for every site.'}</h1>
        <p>
          {branding.loginSubtitle ??
            'Edge-aware infrastructure monitoring and grounded operations intelligence.'}
        </p>
      </section>
      <section className="login-panel">
        <form onSubmit={submit} className="login-card">
          <div>
            <p className="eyebrow">
              {branding.tenantDomain ? branding.displayName.toUpperCase() : 'AOT OpsAI'}
            </p>
            <h2>{heading}</h2>
            <p>{description}</p>
          </div>
          {mode === 'login' && <FederatedButtons />}
          {(mode === 'login' || mode === 'forgot') && (
            <label>
              Email address
              <input
                autoFocus
                autoComplete="username"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </label>
          )}
          {mode === 'login' && (
            <label>
              Password
              <input
                autoComplete="current-password"
                type="password"
                minLength={12}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
          )}
          {mode === 'reset' && (
            <>
              <label>
                New passphrase
                <input
                  autoFocus
                  autoComplete="new-password"
                  type="password"
                  minLength={15}
                  maxLength={128}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </label>
              <label>
                Confirm new passphrase
                <input
                  autoComplete="new-password"
                  type="password"
                  minLength={15}
                  maxLength={128}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                />
              </label>
            </>
          )}
          {mode === 'mfa' && (
            <>
              <label>
                {useRecovery ? 'Recovery code' : 'Six-digit authenticator code'}
                <input
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  pattern={useRecovery ? '[A-Za-z2-7-]{16,32}' : '[0-9]{6}'}
                  maxLength={useRecovery ? 32 : 6}
                  value={mfaCode}
                  onChange={(event) =>
                    setMfaCode(
                      useRecovery
                        ? event.target.value.toUpperCase()
                        : event.target.value.replace(/\D/g, ''),
                    )
                  }
                  required
                />
              </label>
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  setUseRecovery((value) => !value);
                  setMfaCode('');
                }}
              >
                {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
              </button>
            </>
          )}
          <Suspense fallback={<div className="turnstile-placeholder" aria-hidden="true" />}>
            <TurnstileWidget
              key={`${turnstileAction}-${turnstileRevision}`}
              siteKey={turnstileSiteKey}
              action={turnstileAction}
              theme={resolvedTheme}
              onToken={receiveTurnstileToken}
            />
          </Suspense>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          {message && (
            <p className="form-success" role="status">
              {message}
            </p>
          )}
          <button className="button primary" disabled={busy}>
            {busy
              ? 'Working…'
              : mode === 'forgot'
                ? 'Send recovery instructions'
                : mode === 'reset'
                  ? 'Reset password'
                  : mode === 'mfa'
                    ? 'Verify and sign in'
                    : 'Sign in securely'}
          </button>
          {mode === 'login' && (
            <button
              type="button"
              className="text-button"
              onClick={() => {
                setMode('forgot');
                setError('');
                setMessage('');
                resetChallenge();
              }}
            >
              Forgot password?
            </button>
          )}
          {(mode === 'forgot' || mode === 'reset') && (
            <button
              type="button"
              className="text-button"
              onClick={() => {
                setMode('login');
                setError('');
                setMessage('');
                resetChallenge();
              }}
            >
              Return to sign in
            </button>
          )}
        </form>
      </section>
    </main>
  );
}

function OverviewView({
  navigate,
  refreshVersion = 0,
}: {
  navigate: Navigate;
  refreshVersion?: number;
}) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const load = async () => {
    setError('');
    try {
      const [
        dashboard,
        sites,
        assets,
        incidents,
        findings,
        forecasts,
        agents,
        maintenance,
        behavioralFindings,
        services,
      ] = await Promise.all([
        api<Dashboard>(`/v1/dashboard?includeDemo=${includeDemo()}`),
        api<any>('/v1/sites'),
        api<any>(`/v1/assets?includeDemo=${includeDemo()}&limit=100`),
        api<any>('/v1/incidents'),
        api<any>('/v1/capacity/findings').catch(() => ({ items: [] })),
        api<any>('/v1/capacity/forecasts').catch(() => ({ items: [] })),
        api<any>('/v1/operations/agents'),
        api<any>('/v1/operations/maintenance'),
        api<any>('/v1/intelligence/findings').catch(() => ({ items: [] })),
        api<any>('/v1/services').catch(() => ({ items: [] })),
      ]);
      const siteHealth = await Promise.all(
        sites.items.map((site: any) => api<any>(`/v1/sites/${site.id}/health`)),
      );
      setData({
        dashboard,
        sites: sites.items,
        siteHealth,
        assets: assets.items,
        incidents: incidents.items,
        findings: findings.items,
        forecasts: forecasts.items,
        agents: agents.items,
        maintenance: maintenance.items,
        behavioralFindings: behavioralFindings.items,
        services: services.items,
        loadedAt: new Date().toISOString(),
      });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Operational state is unavailable.');
    }
  };
  useEffect(() => {
    load();
  }, [refreshVersion]);
  if (error && !data) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <LoadingState rows={8} />;

  const openIncidents = data.incidents.filter((item: any) => item.status !== 'recovered');
  const onlineAgents = data.agents.filter((agent: any) => agent.status === 'online');
  const criticalIncidents = openIncidents.filter((item: any) => item.severity === 'critical');
  const impairedServices = data.services.filter((service: any) =>
    ['critical', 'degraded'].includes(service.healthState),
  );
  const operationalEvents = buildOperationalEvents(data);
  const healthFactors = deriveHealthFactors({
    health: data.dashboard.health,
    incidents: data.incidents,
    findings: data.findings,
    agents: data.agents,
    assets: data.assets,
  });
  const attention = [
    ...openIncidents.map((item: any) => ({ ...item, kind: 'Incident', at: item.updatedAt })),
    ...data.findings.map((item: any) => ({ ...item, kind: 'Capacity', at: item.lastDetectedAt })),
    ...data.behavioralFindings.map((item: any) => ({
      ...item,
      title: item.summary,
      kind: 'Behavior',
      at: item.lastDetectedAt,
    })),
    ...data.agents
      .filter((agent: any) => agent.status !== 'online')
      .map((agent: any) => ({
        title: `${customerFacingEdgeName(agent)} is ${agent.status}`,
        severity: 'warning',
        status: agent.status,
        kind: 'Edge',
        at: agent.lastHeartbeatAt,
      })),
  ].sort((a: any, b: any) => {
    const rank: Record<string, number> = { critical: 0, warning: 1 };
    return (
      (rank[a.severity] ?? 2) - (rank[b.severity] ?? 2) ||
      String(b.at ?? '').localeCompare(String(a.at ?? ''))
    );
  });
  const platformFor = (asset: any) => {
    const type = String(asset.assetType ?? '');
    if (type.startsWith('proxmox')) return 'proxmox';
    if (type.startsWith('vmware')) return 'vmware';
    if (type.startsWith('hyperv')) return 'hyperv';
    return null;
  };
  const virtualization = ['proxmox', 'vmware', 'hyperv']
    .map((platform) => {
      const assets = data.assets.filter((asset: any) => platformFor(asset) === platform);
      const hostTypes =
        platform === 'proxmox'
          ? ['proxmox-node']
          : platform === 'vmware'
            ? ['vmware-host']
            : ['hyperv-host'];
      const guestTypes =
        platform === 'proxmox'
          ? ['proxmox-vm', 'proxmox-lxc']
          : platform === 'vmware'
            ? ['vmware-vm']
            : ['hyperv-vm'];
      const health = assets.some((asset: any) => asset.health === 'critical')
        ? 'critical'
        : assets.some((asset: any) => asset.health === 'warning')
          ? 'warning'
          : 'healthy';
      return {
        platform,
        assets,
        hosts: assets.filter((asset: any) => hostTypes.includes(asset.assetType)).length,
        guests: assets.filter((asset: any) => guestTypes.includes(asset.assetType)).length,
        storageWarnings: assets.filter(
          (asset: any) =>
            /storage|datastore|csv/.test(asset.assetType) &&
            ['warning', 'critical'].includes(asset.health),
        ).length,
        health,
      };
    })
    .filter((item) => item.assets.length);

  const attentionTarget = (item: any): { view: View; options: NavigateOptions; href: string } => {
    if (item.kind === 'Incident') {
      return {
        view: 'Incidents',
        options: { detailId: item.id },
        href: portalPath('Incidents', item.id),
      };
    }
    if (item.kind === 'Capacity') {
      return {
        view: 'Capacity',
        options: { query: { asset: item.assetId } },
        href: portalPath('Capacity', undefined, undefined, { asset: item.assetId }),
      };
    }
    if (item.kind === 'Behavior' && item.assetId) {
      return {
        view: 'Assets',
        options: { detailId: item.assetId, query: { tab: 'Behavior' } },
        href: portalPath('Assets', item.assetId, undefined, { tab: 'Behavior' }),
      };
    }
    return {
      view: 'Settings',
      options: { query: { section: 'Edge Agents' } },
      href: portalPath('Settings', undefined, undefined, { section: 'Edge Agents' }),
    };
  };

  return (
    <>
      <PageHeader
        eyebrow="OPERATIONS OVERVIEW"
        title="Service operations overview"
        description="Business-service impact, current attention, incidents, sites, capacity, and infrastructure health from the latest reported evidence."
        actions={
          <div className="page-action-stack">
            <HealthBreakdown health={data.dashboard.health} factors={healthFactors} />
            <FreshnessIndicator value={data.loadedAt} onRefresh={load} />
          </div>
        }
      />
      <section className="scorecard enterprise-scorecard" aria-label="Operational summary">
        <MetricCard
          label="Infrastructure health"
          value={data.dashboard.health}
          status={data.dashboard.health}
          detail="Current platform status"
        />
        <MetricCard
          label="Open incidents"
          value={openIncidents.length}
          detail={`${data.dashboard.warningCount} warning`}
          href={portalPath('Incidents', undefined, undefined, { status: 'open' })}
          onNavigate={() => navigate('Incidents', { query: { status: 'open' } })}
        />
        <MetricCard
          label="Critical"
          value={criticalIncidents.length}
          status={criticalIncidents.length ? 'critical' : 'healthy'}
          detail="Requires priority review"
          href={portalPath('Incidents', undefined, undefined, {
            status: 'open',
            severity: 'critical',
          })}
          onNavigate={() =>
            navigate('Incidents', { query: { status: 'open', severity: 'critical' } })
          }
        />
        <MetricCard
          label="Sites"
          value={data.sites.length}
          detail="Active workspace"
          href={portalPath('Sites')}
          onNavigate={() => navigate('Sites')}
        />
        <MetricCard
          label="Assets"
          value={data.assets.length}
          detail="Connected inventory"
          href={portalPath('Assets')}
          onNavigate={() => navigate('Assets')}
        />
        <MetricCard
          label="Edge agents"
          value={`${onlineAgents.length}/${data.agents.length}`}
          status={onlineAgents.length === data.agents.length ? 'healthy' : 'warning'}
          detail="Online / enrolled"
          href={portalPath('Settings', undefined, undefined, { section: 'Edge Agents' })}
          onNavigate={() => navigate('Settings', { query: { section: 'Edge Agents' } })}
        />
        <MetricCard
          label="Services at risk"
          value={impairedServices.length}
          status={
            data.services.some((service: any) => service.healthState === 'critical')
              ? 'critical'
              : data.services.some((service: any) => service.healthState === 'degraded')
                ? 'warning'
                : 'healthy'
          }
          detail={`${data.services.length} business services configured`}
          href={portalPath('Services', undefined, undefined, { health: 'attention' })}
          onNavigate={() => navigate('Services', { query: { health: 'attention' } })}
        />
        <MetricCard
          label="Capacity findings"
          value={data.findings.length}
          status={
            data.findings.some((item: any) => item.severity === 'critical')
              ? 'critical'
              : data.findings.length
                ? 'warning'
                : 'healthy'
          }
          detail="Open capacity findings"
          href={portalPath('Capacity', undefined, undefined, { attention: true })}
          onNavigate={() => navigate('Capacity', { query: { attention: true } })}
        />
      </section>
      <SectionCard
        title="Business service health"
        description="Customer impact based on connected infrastructure relationships."
        action={
          <button className="text-button" onClick={() => navigate('Services')}>
            Open services <ChevronRight size={15} />
          </button>
        }
      >
        {data.services.length ? (
          <div className="service-health-strip">
            {data.services.slice(0, 8).map((service: any) => (
              <button
                key={service.id}
                onClick={() => navigate('Services', { detailId: service.id })}
              >
                <HealthIndicator
                  status={service.healthState}
                  label={customerFacingEntityName(service.name)}
                />
                <small>
                  {service.criticality} criticality · {service.componentCount} components
                </small>
              </button>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No business services configured"
            description="Infrastructure monitoring continues normally. Add services to expose customer impact and SLOs."
          />
        )}
      </SectionCard>
      <div className="dashboard-grid">
        <SectionCard
          title="Current attention"
          description="Existing incidents, capacity findings, and Edge warnings in operational order."
          className="span-12 current-attention-card"
          action={
            <button className="text-button" onClick={() => navigate('Incidents')}>
              View incidents <ChevronRight size={15} />
            </button>
          }
        >
          {attention.length ? (
            <div className="attention-list">
              {attention.slice(0, 7).map((item: any, index: number) => (
                <a
                  key={`${item.kind}-${item.id ?? index}`}
                  href={attentionTarget(item).href}
                  onClick={(event) => {
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    )
                      return;
                    event.preventDefault();
                    const target = attentionTarget(item);
                    navigate(target.view, target.options);
                  }}
                >
                  <i
                    className={`attention-marker tone-${statusTone(item.severity ?? item.status)}`}
                  />
                  <span>
                    <strong>{customerFacingEntityName(item.title)}</strong>
                    <small>
                      {item.kind} · {formatDate(item.at)}
                    </small>
                  </span>
                  <StatusBadge value={item.severity ?? item.status} />
                </a>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No current attention items"
              description="No open incidents, capacity risks, or offline Edge agents are reported."
            />
          )}
        </SectionCard>
        <div className="span-12">
          <OperationalTimeline
            events={operationalEvents}
            onAskOpsAI={() =>
              navigate('OpsAI', {
                question: 'What happened in the last 24 hours?',
                context: 'dashboard',
              })
            }
            onOpenEvent={(event) => {
              if (event.target === 'incident') navigate('Incidents', { detailId: event.targetId });
              else if (event.target === 'asset') navigate('Assets', { detailId: event.targetId });
              else if (event.target === 'site') navigate('Sites', { detailId: event.targetId });
              else if (event.target === 'edge')
                navigate('Settings', { query: { section: 'Edge Agents' } });
            }}
          />
        </div>
        <SectionCard
          title="Behavioral findings"
          description="Current deviations from each asset's learned expected range."
          className="span-5"
          action={
            <button className="text-button" onClick={() => navigate('Assets')}>
              Review assets <ChevronRight size={15} />
            </button>
          }
        >
          {data.behavioralFindings.length ? (
            <div className="behavior-finding-list">
              {data.behavioralFindings.slice(0, 6).map((item: any) => (
                <a
                  key={item.id}
                  href={portalPath('Assets', item.assetId, undefined, { tab: 'Behavior' })}
                  onClick={(event) => {
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    )
                      return;
                    event.preventDefault();
                    navigate('Assets', { detailId: item.assetId, query: { tab: 'Behavior' } });
                  }}
                >
                  <HealthIndicator status={item.severity} />
                  <span>
                    <strong>{item.assetName}</strong>
                    <small>
                      {metricLabel(item.metric)} · {humanCapabilityState(item.kind)} · score {Number(item.score).toFixed(0)}
                    </small>
                  </span>
                  <StatusBadge value={item.confidence} />
                </a>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No behavioral deviations"
              description="No current finding is outside a reliable expected range."
            />
          )}
        </SectionCard>
        <SectionCard
          title="Site health"
          description="Compact status across the active workspace."
          className="span-5"
          action={
            <button className="text-button" onClick={() => navigate('Sites')}>
              All sites <ChevronRight size={15} />
            </button>
          }
        >
          <div className="site-list">
            {data.sites.map((site: any, index: number) => {
              const health = data.siteHealth[index];
              const counts = Object.fromEntries(
                (health.assets ?? []).map((item: any) => [item.health, Number(item.count)]),
              );
              const overall = counts.critical
                ? 'critical'
                : counts.warning
                  ? 'warning'
                  : counts.healthy
                    ? 'healthy'
                    : 'unknown';
              const siteAssets = data.assets.filter((asset: any) => asset.siteId === site.id);
              const siteIncidents = openIncidents.filter(
                (incident: any) => incident.siteId === site.id,
              );
              return (
                <a
                  key={site.id}
                  href={portalPath('Sites', site.id)}
                  onClick={(event) => {
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    )
                      return;
                    event.preventDefault();
                    navigate('Sites', { detailId: site.id });
                  }}
                >
                  <HealthIndicator status={overall} />
                  <span>
                    <strong>{customerFacingSiteName(site)}</strong>
                    <small>
                      {siteAssets.length} assets · {siteIncidents.length} open · Edge agent status
                      available separately
                    </small>
                  </span>
                  <ChevronRight size={16} />
                </a>
              );
            })}
          </div>
        </SectionCard>
        <SectionCard
          title="Virtualization"
          description="Inventory counts and reported health by platform."
          className="span-5"
        >
          {virtualization.length ? (
            <div className="platform-summary">
              {virtualization.map((item) => (
                <div key={item.platform}>
                  <PlatformBadge source={item.platform} />
                  <HealthIndicator status={item.health} />
                  <dl>
                    <div>
                      <dt>Hosts</dt>
                      <dd>{item.hosts}</dd>
                    </div>
                    <div>
                      <dt>Guests</dt>
                      <dd>{item.guests}</dd>
                    </div>
                    <div>
                      <dt>Storage warnings</dt>
                      <dd>{item.storageWarnings}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No virtualization inventory"
              description="Connect a supported read-only virtualization collector to populate this section."
            />
          )}
        </SectionCard>
        <SectionCard
          title="Capacity outlook"
          description="Forecasts based on observed utilization and trend confidence."
          className="span-7"
          action={
            <button className="text-button" onClick={() => navigate('Capacity')}>
              Open capacity <ChevronRight size={15} />
            </button>
          }
        >
          {data.forecasts.length ? (
            <DataTable label="Capacity outlook">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Current</th>
                  <th>Trend</th>
                  <th>Threshold</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {data.forecasts.slice(0, 5).map((item: any) => (
                  <tr key={`${item.assetId}-${item.metric}`}>
                    <td>
                      <strong>{item.assetName}</strong>
                      <small>
                        <AssetPlatform item={item} />
                      </small>
                    </td>
                    <td>
                      {formatMetricValue(
                        Number(item.currentValue),
                        metricUnit(String(item.metric)),
                      )}
                    </td>
                    <td>
                      <TrendIndicator value={item.growthPerDay} />
                    </td>
                    <td>
                      {item.estimatedThresholdAt
                        ? formatDate(item.estimatedThresholdAt, false)
                        : 'Insufficient data'}
                    </td>
                    <td>
                      <StatusBadge
                        value={item.state === 'RISING' ? 'warning' : item.state}
                        label={item.state}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <EmptyState
              title="Insufficient capacity history"
              description="Forecasts appear after enough daily observations are available."
            />
          )}
        </SectionCard>
        <SectionCard
          title="Recent incidents"
          description="Latest incident lifecycle activity."
          className="span-7"
        >
          {data.incidents.length ? (
            <DataTable label="Recent incidents">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Summary</th>
                  <th>State</th>
                  <th>Detected</th>
                </tr>
              </thead>
              <tbody>
                {data.incidents.slice(0, 6).map((item: any) => (
                  <tr key={item.id}>
                    <td>
                      <StatusBadge value={item.severity} />
                    </td>
                    <td>
                      <a
                        className="entity-link"
                        href={portalPath('Incidents', item.id)}
                        onClick={(event) => {
                          if (
                            event.button !== 0 ||
                            event.metaKey ||
                            event.ctrlKey ||
                            event.shiftKey ||
                            event.altKey
                          )
                            return;
                          event.preventDefault();
                          navigate('Incidents', { detailId: item.id });
                        }}
                      >
                        {customerFacingEntityName(item.title)}
                      </a>
                      <small>Operational incident</small>
                    </td>
                    <td>
                      <StatusBadge value={item.status} />
                    </td>
                    <td>{formatDate(item.openedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <EmptyState
              title="No incidents recorded"
              description="Operational incidents will appear here when monitored conditions change state."
            />
          )}
        </SectionCard>
        <SectionCard
          title="Edge health"
          description="Version and heartbeat posture for enrolled agents."
          className="span-5"
        >
          <div className="edge-list">
            {data.agents.slice(0, 6).map((agent: any) => (
              <div key={agent.id}>
                <HealthIndicator status={agent.status} />
                <span>
                  <strong>{customerFacingEdgeName(agent)}</strong>
                  <small>
                    {agent.version} · {relativeTime(agent.lastHeartbeatAt)}
                  </small>
                </span>
                <StatusBadge value={agent.upgradeState} />
              </div>
            ))}
          </div>
        </SectionCard>
        <SectionCard className="span-12 opsai-entry">
          <div className="opsai-mark">
            <Bot size={22} />
          </div>
          <div>
            <p className="eyebrow">GROUNDED OPERATIONS INTELLIGENCE</p>
            <h2>Ask OpsAI about your infrastructure</h2>
            <p>
              Read-only explanations grounded in the operational evidence available to your
              workspace.
            </p>
          </div>
          <div className="question-chips">
            {[
              'What needs attention right now?',
              'Which storage is approaching capacity?',
              'What happened in the last 24 hours?',
              'Which systems have recurring issues?',
            ].map((question) => (
              <button
                key={question}
                onClick={() => navigate('OpsAI', { question, context: 'dashboard' })}
              >
                {question}
              </button>
            ))}
          </div>
        </SectionCard>
      </div>
    </>
  );
}

function SiteTimezoneConfiguration({
  site,
  role,
  onSaved,
}: {
  site: any;
  role: string;
  onSaved(): Promise<void>;
}) {
  const [timezoneMode, setTimezoneMode] = useState(
    site.timezoneMode === 'SPECIFIC' ? 'SPECIFIC' : 'INHERIT_WORKSPACE',
  );
  const [timezone, setTimezone] = useState(site.timezone ?? site.effectiveTimezone ?? 'UTC');
  const [message, setMessage] = useState('');
  const canWrite = role === 'tenant_owner' || role === 'tenant_admin' || role === 'aot_admin' || role === 'aot_operator';
  const zones = [
    'Asia/Manila',
    'Asia/Tokyo',
    'America/New_York',
    'America/Los_Angeles',
    'Europe/London',
    'UTC',
  ];
  useEffect(() => {
    setTimezoneMode(site.timezoneMode === 'SPECIFIC' ? 'SPECIFIC' : 'INHERIT_WORKSPACE');
    setTimezone(site.timezone ?? site.effectiveTimezone ?? 'UTC');
    setMessage('');
  }, [site.id, site.timezoneMode, site.timezone, site.effectiveTimezone]);
  async function save(event: React.FormEvent) {
    event.preventDefault();
    setMessage('Saving Site timezone…');
    try {
      await api(`/v1/sites/${site.id}/location`, {
        method: 'PATCH',
        body: JSON.stringify({
          address: site.address ?? '',
          latitude: site.latitude ?? null,
          longitude: site.longitude ?? null,
          siteType: site.siteType ?? '',
          timezoneMode,
          timezone: timezoneMode === 'SPECIFIC' ? timezone : null,
        }),
      });
      await onSaved();
      setMessage('Site timezone saved.');
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : 'Site timezone could not be saved.');
    }
  }
  return (
    <div className="settings-form site-timezone-configuration">
      <div>
        <p className="eyebrow">REGIONAL &amp; TIME</p>
        <h3>Site timezone</h3>
        <p>
          <strong>{site.effectiveTimezone ?? 'UTC'}</strong>
          {' · '}
          {site.timezoneMode === 'SPECIFIC'
            ? 'Site override'
            : `Inherited from ${site.workspaceName ?? 'workspace'}`}
        </p>
      </div>
      {canWrite ? (
        <form className="settings-form" onSubmit={save}>
          <label>
            Timezone behavior
            <select value={timezoneMode} onChange={(event) => setTimezoneMode(event.target.value)}>
              <option value="INHERIT_WORKSPACE">Inherit workspace timezone</option>
              <option value="SPECIFIC">Override for this Site</option>
            </select>
          </label>
          {timezoneMode === 'SPECIFIC' && (
            <label>
              Site timezone
              <select value={timezone} onChange={(event) => setTimezone(event.target.value)}>
                {zones.map((zone) => (
                  <option key={zone}>{zone}</option>
                ))}
              </select>
            </label>
          )}
          <button className="button primary">Save Site timezone</button>
        </form>
      ) : (
        <p>Ask a workspace administrator to change the Site timezone.</p>
      )}
      {message && (
        <p className="toast" role="status">
          {message}
        </p>
      )}
    </div>
  );
}

function SitesView({
  initialSiteId,
  navigate,
  role,
  refreshVersion = 0,
}: {
  initialSiteId?: string;
  navigate: Navigate;
  role: string;
  refreshVersion?: number;
}) {
  const [data, setData] = useState<any>(null);
  const [selected, setSelected] = useState<any>(null);
  const selectedSiteIdRef = useRef<string | undefined>(undefined);
  const loadSequenceRef = useRef(0);
  const [tab, setTab] = useState(() => readSiteDetailTab(window.location.search));
  const [error, setError] = useState('');
  const load = async () => {
    const loadSequence = ++loadSequenceRef.current;
    try {
      const [sites, assets, incidents, agents, maintenance] = await Promise.all([
        api<any>('/v1/sites'),
        api<any>(`/v1/assets?includeDemo=${includeDemo()}&limit=100`),
        api<any>('/v1/incidents'),
        api<any>('/v1/operations/agents'),
        api<any>('/v1/operations/maintenance'),
      ]);
      const health = await Promise.all(
        sites.items.map((site: any) => api<any>(`/v1/sites/${site.id}/health`)),
      );
      const payload = {
        sites: sites.items,
        assets: assets.items,
        incidents: incidents.items,
        agents: agents.items,
        maintenance: maintenance.items,
        health,
      };
      if (loadSequence !== loadSequenceRef.current) return;
      setData(payload);
      const initialIndex = sites.items.findIndex((site: any) => site.id === initialSiteId);
      if (initialIndex >= 0)
        await openSite(
          sites.items[initialIndex],
          initialIndex,
          payload,
          false,
          isSameDetailSelection(selectedSiteIdRef.current, initialSiteId),
        );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Site data unavailable.');
    }
  };
  useEffect(() => {
    load();
  }, [refreshVersion]);
  async function openSite(
    site: any,
    index: number,
    source = data,
    updateRoute = true,
    preserveUI = false,
  ) {
    const history = await api<any>(`/v1/sites/${site.id}/history?range=24h&resolution=5m`).catch(
      () => ({ items: [] }),
    );
    selectedSiteIdRef.current = site.id;
    setSelected({ site, health: source.health[index], history: history.items });
    if (!preserveUI) setTab(updateRoute ? 'Overview' : readSiteDetailTab(window.location.search));
    if (updateRoute) navigate('Sites', { detailId: site.id });
  }
  function selectSiteTab(nextTab: string) {
    setTab(nextTab);
    const query = writeSiteDetailState(window.location.search, nextTab);
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`,
    );
  }
  if (error && !data) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <LoadingState rows={7} />;
  const selectedAgents = selected
    ? data.agents.filter((agent: any) => agent.siteId === selected.site.id)
    : [];
  const visibleSelectedAgents = selectedAgents.slice(0, 8);
  return (
    <>
      <PageHeader
        eyebrow="SITE OPERATIONS"
        title="Sites"
        description="Site health, Edge state, assets, incidents, history, configuration, and maintenance in one operational view."
      />
      <SectionCard>
        <div className="site-cards">
          {data.sites.map((site: any, index: number) => {
            const counts = Object.fromEntries(
              (data.health[index]?.assets ?? []).map((item: any) => [
                item.health,
                Number(item.count),
              ]),
            );
            const overall = counts.critical
              ? 'critical'
              : counts.warning
                ? 'warning'
                : counts.healthy
                  ? 'healthy'
                  : 'unknown';
            const assets = data.assets.filter((asset: any) => asset.siteId === site.id);
            const incidents = data.incidents.filter(
              (item: any) => item.siteId === site.id && item.status !== 'recovered',
            );
            const agents = data.agents.filter((agent: any) => agent.siteId === site.id);
            const onlineAgents = agents.filter((agent: any) => agent.status === 'online');
            return (
              <button key={site.id} onClick={() => openSite(site, index)}>
                <div>
                  <HealthIndicator status={overall} />
                  <StatusBadge value={site.status} />
                </div>
                <h2>{customerFacingSiteName(site)}</h2>
                <dl>
                  <div>
                    <dt>Assets</dt>
                    <dd>{assets.length}</dd>
                  </div>
                  <div>
                    <dt>Open incidents</dt>
                    <dd>{incidents.length}</dd>
                  </div>
                  <div>
                    <dt>Edge</dt>
                    <dd>
                      {onlineAgents.length}/{agents.length} online
                    </dd>
                  </div>
                </dl>
                <small>Latest site heartbeat {relativeTime(agents[0]?.lastHeartbeatAt)}</small>
              </button>
            );
          })}
        </div>
      </SectionCard>
      {selected && (
        <SectionCard
          className="detail-panel"
          title={customerFacingSiteName(selected.site)}
          description="Site operations detail"
          action={
            <div className="detail-actions">
              <FreshnessIndicator
                value={selectedAgents[0]?.lastHeartbeatAt}
                state={selectedAgents[0]?.status}
              />
              <ContextActionMenu label="Site actions">
                <button onClick={() => navigate('Assets')}>View assets</button>
                <button onClick={() => selectSiteTab('Monitoring Setup')}>Monitoring setup</button>
                <button onClick={() => navigate('Incidents')}>View incidents</button>
                <TopologyAction onClick={() => navigate('Topology')} />
                <MaintenanceAction onClick={() => navigate('Settings')} />
              </ContextActionMenu>
              <button
                className="button secondary"
                onClick={() => {
                  selectedSiteIdRef.current = undefined;
                  setSelected(null);
                  navigate('Sites', { replace: true });
                }}
              >
                Close
              </button>
            </div>
          }
        >
          <Tabs
            items={[
              'Overview',
              'Monitoring Setup',
              'Timeline',
              'Assets',
              'Edge Collectors',
              'Incidents',
              'History',
              'Configuration',
              'Maintenance',
            ]}
            active={tab}
            onChange={selectSiteTab}
            label="Site detail"
          />
          {tab === 'Monitoring Setup' && (
            <Suspense fallback={<LoadingState rows={7} />}>
              <SiteMonitoringSetup
                siteId={selected.site.id}
                role={role}
                refreshVersion={refreshVersion}
                onOpenEdges={(query) => navigate('Edges', { query })}
                onOpenIntegrations={(query) => navigate('Integrations', { query })}
              />
            </Suspense>
          )}
          {tab === 'Overview' && (
            <>
              <HealthBreakdown
                health={
                  selected.health.assets.some((item: any) => item.health === 'critical')
                    ? 'critical'
                    : selected.health.assets.some((item: any) => item.health === 'warning')
                      ? 'warning'
                      : selected.site.status
                }
                factors={deriveHealthFactors({
                  health: selected.site.status,
                  incidents: data.incidents.filter((item: any) => item.siteId === selected.site.id),
                  assets: data.assets.filter((item: any) => item.siteId === selected.site.id),
                })}
              />
              <div className="detail-summary">
                <div>
                  <span>Status</span>
                  <HealthIndicator status={selected.site.status} />
                </div>
                <div>
                  <span>Asset health</span>
                  <strong>
                    {(selected.health.assets ?? [])
                      .map((item: any) => `${item.count} ${item.health}`)
                      .join(' · ') || 'No assets'}
                  </strong>
                </div>
                <div>
                  <span>Site Edge coverage</span>
                  <strong>
                    {selectedAgents.filter((agent: any) => agent.status === 'online').length}/
                    {selectedAgents.length} online
                  </strong>
                </div>
                <div>
                  <span>Latest heartbeat</span>
                  <strong>{relativeTime(selectedAgents[0]?.lastHeartbeatAt)}</strong>
                </div>
              </div>
              <section className="site-edge-status" aria-label="AOT OpsAI Edge status">
                <header>
                  <div>
                    <p className="eyebrow">SITE-LOCAL COLLECTOR AND OBSERVABILITY GATEWAY</p>
                    <h3>AOT OpsAI Edge</h3>
                  </div>
                  <span>{selectedAgents.length} enrolled at this site</span>
                </header>
                {selectedAgents.length ? (
                  <div className="site-edge-grid">
                    {visibleSelectedAgents.map((agent: any) => (
                      <article key={agent.id}>
                        <div className="site-edge-title">
                          <HealthIndicator status={agent.status} />
                          <span>
                            <strong>{customerFacingEdgeName(agent)}</strong>
                            <small>
                              {agent.platform} · {agent.architecture}
                            </small>
                          </span>
                          <StatusBadge value={agent.status} />
                        </div>
                        <dl>
                          <div>
                            <dt>Version / protocol</dt>
                            <dd>
                              {agent.version || 'Unknown'} · {agent.protocolVersion || '—'}
                            </dd>
                          </div>
                          <div>
                            <dt>Last heartbeat</dt>
                            <dd>{relativeTime(agent.lastHeartbeatAt)}</dd>
                          </div>
                          <div>
                            <dt>Collectors</dt>
                            <dd>
                              {agent.collectors} ·{' '}
                              {agent.isStale ? 'Last-known health · telemetry stale · ' : ''}
                              {agent.collectorCurrentState === 'HEALTHY'
                                ? 'Healthy'
                                : agent.collectorCurrentState === 'DEGRADED'
                                  ? `${agent.collectorConsecutiveFailures} consecutive failures`
                                  : agent.collectorCurrentState === 'NOT_CONFIGURED'
                                    ? 'Not configured'
                                    : 'Awaiting current health'}
                              {agent.collectorHistoricalFailures > 0
                                ? ` · ${agent.collectorHistoricalFailures} historical`
                                : ''}
                            </dd>
                          </div>
                          <div>
                            <dt>Spool / dead letters</dt>
                            <dd>
                              {agent.spoolSize} / {agent.deadLetterCount}
                            </dd>
                          </div>
                          <div>
                            <dt>Syslog receiver</dt>
                            <dd>
                              {agent.logReceivers?.syslog?.enabled
                                ? agent.logReceivers.syslog.address ||
                                  'Configured · endpoint restricted'
                                : 'Disabled'}
                            </dd>
                          </div>
                          <div>
                            <dt>SNMP trap receiver</dt>
                            <dd>
                              {agent.logReceivers?.snmpTrap?.enabled
                                ? agent.logReceivers.snmpTrap.address ||
                                  'Configured · endpoint restricted'
                                : 'Disabled'}
                            </dd>
                          </div>
                        </dl>
                      </article>
                    ))}
                    {selectedAgents.length > visibleSelectedAgents.length && (
                      <p className="site-edge-overflow-note">
                        Showing the 8 most recent Edge agents. {selectedAgents.length - 8} more
                        enrolled agents remain available in Edge Agents.
                      </p>
                    )}
                  </div>
                ) : (
                  <EmptyState
                    title="No OpsAI Edge is installed for this site."
                    description="Install a Site Edge inside the monitored network. Multiple Edges may serve distinct network zones without changing asset ownership."
                    action={
                      <button
                        className="button primary"
                        onClick={() => navigate('Edges', { query: { site: selected.site.id } })}
                      >
                        Install Site Edge
                      </button>
                    }
                  />
                )}
              </section>
            </>
          )}
          {tab === 'Timeline' && (
            <OperationalTimeline
              title={`${customerFacingSiteName(selected.site)} activity`}
              siteId={selected.site.id}
              events={buildOperationalEvents({
                incidents: data.incidents.filter((item: any) => item.siteId === selected.site.id),
                maintenance: data.maintenance.filter(
                  (item: any) => item.siteId === selected.site.id,
                ),
              })}
            />
          )}
          {tab === 'Assets' && (
            <CompactAssetTable
              items={data.assets.filter((asset: any) => asset.siteId === selected.site.id)}
            />
          )}
          {tab === 'Edge Collectors' && (
            <div className="site-edge-navigation">
              <div>
                <p className="eyebrow">AOT OPSAI EDGE</p>
                <h3>Site-local collector and observability gateway</h3>
                <p>
                  Install, enroll, inspect listeners, review health, and manage multiple Edges
                  assigned to {customerFacingSiteName(selected.site)}.
                </p>
              </div>
              <button
                className="button primary"
                onClick={() =>
                  navigate('Edges', {
                    detailId: selectedAgents[0]?.id,
                    query: { site: selected.site.id },
                  })
                }
              >
                {selectedAgents.length ? 'Manage Edge Collectors' : 'Install Site Edge'}
              </button>
            </div>
          )}
          {tab === 'Incidents' && (
            <CompactIncidentTable
              items={data.incidents.filter((item: any) => item.siteId === selected.site.id)}
            />
          )}
          {tab === 'History' &&
            (selected.history.length ? (
              <div className="chart-grid">
                {groupHistory(selected.history)
                  .slice(0, 4)
                  .map((group: any) => (
                    <HistoryChart key={group.key} metric={group.label} points={group.points} />
                  ))}
              </div>
            ) : (
              <EmptyState
                title="No five-minute history"
                description="History will appear after the Edge uploads a completed interval."
              />
            ))}
          {tab === 'Configuration' && (
            <div className="stacked-sections">
              <SiteTimezoneConfiguration site={selected.site} role={role} onSaved={load} />
              <EmptyState
                title="Monitoring configuration is managed through AOT OpsAI Edge"
                description="Collector and monitor settings are delivered securely to the Site Edge."
              />
            </div>
          )}
          {tab === 'Maintenance' && (
            <div className="record-list">
              {data.maintenance
                .filter((item: any) => !item.siteId || item.siteId === selected.site.id)
                .map((item: any) => (
                  <div key={item.id}>
                    <span>
                      <strong>{item.reason}</strong>
                      <small>
                        {formatDate(item.startsAt)} – {formatDate(item.endsAt)}
                      </small>
                    </span>
                    <StatusBadge value={item.status ?? item.recurrence} />
                  </div>
                ))}
            </div>
          )}
        </SectionCard>
      )}
    </>
  );
}

function AssetsView({
  onOpsAI,
  initialAssetId,
  initialSelectedId,
  navigate,
  refreshVersion = 0,
  role,
}: {
  onOpsAI: (question: string) => void;
  initialAssetId?: string;
  initialSelectedId?: string;
  navigate: Navigate;
  refreshVersion?: number;
  role: string;
}) {
  const [items, setItems] = useState<any[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(() => {
    const requested = Math.max(
      0,
      Number(new URLSearchParams(window.location.search).get('offset') ?? 0) || 0,
    );
    return Math.floor(requested / 100) * 100;
  });
  const [facets, setFacets] = useState<{ vendors: string[]; models: string[] }>({
    vendors: [],
    models: [],
  });
  const [sites, setSites] = useState<any[]>([]);
  const [serviceOptions, setServiceOptions] = useState<any[]>([]);
  const [outliers, setOutliers] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [tab, setTab] = useState('Overview');
  const [error, setError] = useState('');
  const initialQuery = new URLSearchParams(window.location.search);
  const requestedAssetTab = initialQuery.get('tab');
  const [filters, setFilters] = useState({
    search: initialQuery.get('search') ?? '',
    site: initialQuery.get('site') ?? '',
    type: initialQuery.get('type') ?? '',
    health: initialQuery.get('health') ?? '',
    source: initialQuery.get('platform') ?? '',
    status: initialQuery.get('status') ?? '',
    service: initialQuery.get('service') ?? '',
    vendor: initialQuery.get('vendor') ?? '',
    model: initialQuery.get('model') ?? '',
    sort: initialQuery.get('sort') ?? 'name',
    direction: initialQuery.get('direction') ?? 'asc',
  });
  useEffect(() => {
    Promise.all([api<any>('/v1/sites'), api<any>('/v1/services')]).then(
      ([siteResult, serviceResult]) => {
        setSites(siteResult.items);
        setServiceOptions(serviceResult.items);
      },
    );
    api<any>('/v1/assets/outliers?limit=5')
      .then((result) => setOutliers(result.items ?? []))
      .catch(() => setOutliers([]));
  }, []);
  useEffect(() => {
    const serverFilters = {
      search: filters.search,
      site: filters.site,
      type: filters.type,
      health: filters.health,
      source: filters.source,
      service: filters.service,
      vendor: filters.vendor,
      model: filters.model,
      status: filters.status,
      sort: filters.sort,
      direction: filters.direction,
      offset: String(offset),
      limit: '100',
    };
    const query = new URLSearchParams(
      Object.entries({ ...serverFilters, includeDemo: String(includeDemo()) }).filter(
        ([, value]) => value,
      ),
    );
    api<any>(`/v1/assets?${query}`)
      .then((result) => {
        const bounded = boundedInventoryPage(Number(result.total ?? 0), offset);
        if (bounded.offset !== offset) {
          setOffset(bounded.offset);
          return;
        }
        setItems(result.items);
        setTotal(result.total);
        setFacets(result.facets ?? { vendors: [], models: [] });
        setError('');
      })
      .catch((reason) => {
        setItems((current) => current ?? []);
        setError(reason instanceof Error ? reason.message : 'Asset inventory unavailable.');
      });
  }, [filters, offset, refreshVersion]);
  useEffect(() => {
    if (!initialAssetId) return;
    void openAsset(initialAssetId, false);
  }, [initialAssetId]);
  useEffect(() => {
    if (initialAssetId) return;
    if (initialSelectedId) void openAsset(initialSelectedId, false);
    else setSelected(null);
  }, [initialAssetId, initialSelectedId]);
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.type) params.set('type', filters.type);
    if (filters.site) params.set('site', filters.site);
    if (filters.health) params.set('health', filters.health);
    if (filters.source) params.set('platform', filters.source);
    if (filters.status) params.set('status', filters.status);
    if (filters.service) params.set('service', filters.service);
    if (filters.vendor) params.set('vendor', filters.vendor);
    if (filters.model) params.set('model', filters.model);
    if (filters.search) params.set('search', filters.search);
    if (filters.sort !== 'name') params.set('sort', filters.sort);
    if (filters.direction !== 'asc') params.set('direction', filters.direction);
    if (offset) params.set('offset', String(offset));
    if (selected?.asset?.id && !initialAssetId) params.set('selected', selected.asset.id);
    const query = params.toString();
    if (!initialAssetId)
      window.history.replaceState(window.history.state, '', `/assets${query ? `?${query}` : ''}`);
  }, [filters, offset, selected?.asset?.id, initialAssetId]);
  const visibleItems = items ?? [];
  const inventoryPage = boundedInventoryPage(total, offset);
  const vendorOptions = facets.vendors;
  const modelOptions = facets.models;
  const advancedFilterCount = [
    filters.vendor,
    filters.model,
    filters.source,
    filters.status,
    filters.service,
    filters.sort !== 'name' ? filters.sort : '',
    filters.direction !== 'asc' ? filters.direction : '',
  ].filter(Boolean).length;
  async function openAsset(id: string, updateRoute = true, preserveUI = false) {
    const range = preserveUI
      ? (selected?.historyRange ?? '24h')
      : (initialQuery.get('range') ?? '24h');
    const historyFrom = preserveUI ? selected?.historyFrom : initialQuery.get('from');
    const historyTo = preserveUI ? selected?.historyTo : initialQuery.get('to');
    const customHours =
      historyFrom && historyTo
        ? Math.max(1, Math.min(720, (Date.parse(historyTo) - Date.parse(historyFrom)) / 3_600_000))
        : 24;
    const evidenceHours =
      range === 'custom'
        ? customHours
        : (({ '1h': 1, '6h': 6, '24h': 24, '7d': 168, '30d': 720 } as Record<string, number>)[
            range
          ] ?? 24);
    const resolution = (
      range === 'custom' ? evidenceHours <= 24 : ['1h', '6h', '24h'].includes(range)
    )
      ? '5m'
      : range === '7d' || (range === 'custom' && evidenceHours <= 168)
        ? '1h'
        : '1d';
    const historyQuery = new URLSearchParams({ range, resolution });
    if (range === 'custom' && historyFrom && historyTo) {
      historyQuery.set('from', historyFrom);
      historyQuery.set('to', historyTo);
    }
    const [detail, interfaces, metrics, history, forecasts, behavior, logs, events] =
      await Promise.all([
        api<any>(`/v1/assets/${id}`),
        api<any>(`/v1/assets/${id}/interfaces?limit=25&offset=0`),
        api<any>(`/v1/assets/${id}/metrics`),
        api<any>(`/v1/assets/${id}/history?${historyQuery}`),
        api<any>('/v1/capacity/forecasts').catch(() => ({ items: [] })),
        api<any>(`/v1/assets/${id}/behavior`).catch(() => ({
          baselines: [],
          findings: [],
          events: [],
        })),
        api<any>(
          `/v1/logs?asset=${encodeURIComponent(id)}&hours=${Math.ceil(evidenceHours)}&limit=100`,
        ).catch(() => ({
          items: [],
        })),
        api<any>(`/v1/assets/${id}/events?hours=${Math.ceil(evidenceHours)}`).catch(() => ({
          items: [],
        })),
      ]);
    setSelected({
      ...detail,
      interfaces: interfaces.items,
      interfaceTotal: interfaces.total,
      interfaceOffset: interfaces.offset,
      interfaceLimit: interfaces.limit,
      metrics: metrics.items,
      capabilities: metrics.capabilities,
      timezone: metrics.timezone ?? 'UTC',
      history: history.items,
      forecasts: forecasts.items.filter((item: any) => item.assetId === id),
      behavior,
      logs: logs.items,
      events: events.items,
      historyRange: range,
      historyFrom: range === 'custom' ? historyFrom : undefined,
      historyTo: range === 'custom' ? historyTo : undefined,
    });
    if (!preserveUI)
      setTab(
        requestedAssetTab &&
          [
            'Overview',
            'Metrics',
            'Network',
            'Storage',
            'Interfaces',
            'Hardware',
            'Virtual Machines',
            'Logs',
            'Events',
            'Topology',
            'Map',
            'Configuration',
            'Relationships',
            'Services',
            'History',
            'Behavior',
            'Incidents',
            'Capacity',
            'Investigate',
            'OpsAI',
          ].includes(requestedAssetTab)
          ? requestedAssetTab
          : 'Overview',
      );
    if (updateRoute) {
      const params = new URLSearchParams(window.location.search);
      params.set('selected', id);
      window.history.pushState(window.history.state, '', `/assets?${params}`);
    }
  }
  useEffect(() => {
    if (!refreshVersion || !selected?.asset?.id) return;
    const scrollY = window.scrollY;
    void openAsset(selected.asset.id, false, true).finally(() =>
      window.requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: 'auto' })),
    );
  }, [refreshVersion]);
  useEffect(() => {
    if (!selected?.asset?.id) return;
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab);
    params.set('section', assetSectionForTab(tab));
    params.set('range', selected.historyRange ?? '24h');
    if (selected.historyRange === 'custom' && selected.historyFrom && selected.historyTo) {
      params.set('from', selected.historyFrom);
      params.set('to', selected.historyTo);
    } else {
      params.delete('from');
      params.delete('to');
    }
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params}`);
  }, [tab, selected?.historyRange, selected?.asset?.id]);
  function openFullAsset(id: string) {
    window.history.replaceState(
      { ...(window.history.state ?? {}), assetListScrollY: window.scrollY },
      '',
      `${window.location.pathname}${window.location.search}`,
    );
    sessionStorage.setItem(
      'aot_asset_list_return',
      JSON.stringify({
        assetId: id,
        path: `${window.location.pathname}${window.location.search}`,
        scrollY: window.scrollY,
      }),
    );
    navigate('Assets', { detailId: id });
  }
  function selectAssetFromInventory(id: string) {
    if (window.matchMedia('(max-width: 900px)').matches) {
      openFullAsset(id);
      return;
    }
    const preview = items?.find((item) => item.id === id);
    if (preview) {
      setSelected({
        asset: {
          ...preview,
          asset_type: preview.assetType ?? preview.kind,
          display_name: preview.displayName ?? preview.name,
          site_name: preview.siteName,
          last_seen_at: preview.lastSeenAt,
        },
        children: [],
        incidents: [],
        services: [],
        edgeCollectors: [],
        collector: {
          state: 'NOT_REPORTED',
          name: 'Not reported',
          integration: 'Not reported',
          sourceReference: 'Not reported',
        },
        healthProvenance: null,
        interfaces: [],
        interfaceTotal: 0,
        interfaceOffset: 0,
        interfaceLimit: 25,
        metrics: [],
        history: [],
        forecasts: [],
        behavior: { state: 'NO_DATA', baselines: [], findings: [], events: [] },
        logs: [],
        events: [],
        capabilities: null,
        timezone: 'UTC',
        historyRange: '24h',
      });
    }
    const params = new URLSearchParams(window.location.search);
    params.set('selected', id);
    window.history.pushState(window.history.state, '', `/assets?${params}`);
    void openAsset(id, false).catch((reason) =>
      setError(reason instanceof Error ? reason.message : 'Asset detail unavailable.'),
    );
  }
  function returnToAssetList() {
    try {
      const value = JSON.parse(sessionStorage.getItem('aot_asset_list_return') ?? '{}');
      if (value.assetId === initialAssetId && String(value.path).startsWith('/assets')) {
        sessionStorage.removeItem('aot_asset_list_return');
        window.history.back();
        window.setTimeout(
          () => window.scrollTo({ top: Number(value.scrollY) || 0, behavior: 'auto' }),
          50,
        );
        return;
      }
    } catch {
      sessionStorage.removeItem('aot_asset_list_return');
    }
    navigate('Assets', { replace: true });
  }
  const detail = selected && (
    <AssetDetail
      data={selected}
      tab={tab}
      setTab={setTab}
      close={returnToAssetList}
      onOpsAI={onOpsAI}
      inventory={items ?? []}
      navigate={navigate}
      role={role}
      onInterfacePage={async (nextOffset: number) => {
        const page = await api<any>(
          `/v1/assets/${selected.asset.id}/interfaces?limit=25&offset=${nextOffset}`,
        );
        setSelected((current: any) => ({
          ...current,
          interfaces: page.items,
          interfaceTotal: page.total,
          interfaceOffset: page.offset,
          interfaceLimit: page.limit,
        }));
      }}
      onHistoryRange={async (window: EvidenceWindow) => {
        const range = window.range;
        const customHours =
          window.from && window.to
            ? Math.max(
                1,
                Math.min(720, (Date.parse(window.to) - Date.parse(window.from)) / 3_600_000),
              )
            : 24;
        const evidenceHours =
          range === 'custom'
            ? customHours
            : (({ '1h': 1, '6h': 6, '24h': 24, '7d': 168, '30d': 720 } as Record<string, number>)[
                range
              ] ?? 24);
        const resolution = (
          range === 'custom' ? evidenceHours <= 24 : ['1h', '6h', '24h'].includes(range)
        )
          ? '5m'
          : range === '7d' || (range === 'custom' && evidenceHours <= 168)
            ? '1h'
            : '1d';
        const historyQuery = new URLSearchParams({ range, resolution });
        if (range === 'custom' && window.from && window.to) {
          historyQuery.set('from', window.from);
          historyQuery.set('to', window.to);
        }
        const [history, logs, events] = await Promise.all([
          api<any>(`/v1/assets/${selected.asset.id}/history?${historyQuery}`),
          api<any>(
            `/v1/logs?asset=${encodeURIComponent(selected.asset.id)}&hours=${Math.ceil(evidenceHours)}&limit=100`,
          ).catch(() => ({ items: [] })),
          api<any>(
            `/v1/assets/${selected.asset.id}/events?hours=${Math.ceil(evidenceHours)}`,
          ).catch(() => ({ items: [] })),
        ]);
        setSelected((current: any) => ({
          ...current,
          history: history.items,
          logs: logs.items,
          events: events.items,
          historyRange: range,
          historyFrom: range === 'custom' ? window.from : undefined,
          historyTo: range === 'custom' ? window.to : undefined,
        }));
      }}
    />
  );
  if (initialAssetId)
    return (
      <>
        <PageHeader
          eyebrow="ASSET OPERATIONS"
          title="Asset detail"
          description="Operational workspace with contextual telemetry, events, and insights."
        />
        {selected?.asset?.id === initialAssetId ? detail : <LoadingState rows={8} />}
      </>
    );
  return (
    <>
      <PageHeader
        eyebrow="INFRASTRUCTURE INVENTORY"
        title="Assets"
        description="Dense, searchable infrastructure inventory with platform context and reported operational state."
      />
      <FilterBar
        search={filters.search}
        onSearch={(search) => {
          setOffset(0);
          setFilters({ ...filters, search });
        }}
        placeholder="Search asset, hostname, address, vendor, or model"
      >
        <select
          aria-label="Site"
          value={filters.site}
          onChange={(e) => {
            setOffset(0);
            setFilters({ ...filters, site: e.target.value });
          }}
        >
          <option value="">All sites</option>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>
              {customerFacingSiteName(site)}
            </option>
          ))}
        </select>
        <select
          aria-label="Asset type"
          value={filters.type}
          onChange={(e) => {
            setOffset(0);
            setFilters({ ...filters, type: e.target.value });
          }}
        >
          <option value="">All types</option>
          <option value="network-device">Network device</option>
          <option value="proxmox-node">Proxmox node</option>
          <option value="proxmox-vm">Proxmox VM</option>
          <option value="vmware-host">VMware host</option>
          <option value="vmware-vm">VMware VM</option>
          <option value="vmware-datastore">Datastore</option>
          <option value="hyperv-host">Hyper-V host</option>
          <option value="hyperv-vm">Hyper-V VM</option>
          <option value="hyperv-csv">CSV</option>
          <option value="storage">Storage</option>
          <option value="router">Router</option>
          <option value="switch">Switch</option>
          <option value="server">Server</option>
          <option value="facility-ups">UPS</option>
          <option value="facility-pdu">PDU</option>
          <option value="facility-cooling">Cooling / CRAC</option>
          <option value="facility-sensor">Environmental sensor</option>
          <option value="bacnet-controller">BACnet controller</option>
          <option value="modbus-power-meter">Modbus power meter</option>
        </select>
        <select
          aria-label="Health"
          value={filters.health}
          onChange={(e) => {
            setOffset(0);
            setFilters({ ...filters, health: e.target.value });
          }}
        >
          <option value="">All health</option>
          <option>healthy</option>
          <option>warning</option>
          <option>critical</option>
          <option>unknown</option>
        </select>
        <details className="asset-advanced-filters">
          <summary className="button secondary">
            Filters{advancedFilterCount ? ` (${advancedFilterCount})` : ''}
          </summary>
          <div className="asset-advanced-filter-panel">
            <label>
              Vendor
              <select
                aria-label="Vendor"
                value={filters.vendor}
                onChange={(e) => {
                  setOffset(0);
                  setFilters({ ...filters, vendor: e.target.value, model: '' });
                }}
              >
                <option value="">All vendors</option>
                {vendorOptions.map((vendor) => (
                  <option key={vendor} value={vendor}>
                    {vendor}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Model or family
              <select
                aria-label="Model or family"
                value={filters.model}
                onChange={(e) => {
                  setOffset(0);
                  setFilters({ ...filters, model: e.target.value });
                }}
              >
                <option value="">All models</option>
                {modelOptions.map((model) => (
                  <option key={model} value={model}>
                    {customerFacingEntityName(model)}
                  </option>
                ))}
              </select>
            </label>
            <select
              aria-label="Platform"
              value={filters.source}
              onChange={(e) => {
                setOffset(0);
                setFilters({ ...filters, source: e.target.value });
              }}
            >
              <option value="">All platforms</option>
              <option>snmp</option>
              <option>proxmox</option>
              <option>vmware</option>
              <option>hyperv</option>
            </select>
            <select
              aria-label="Status"
              value={filters.status}
              onChange={(e) => {
                setOffset(0);
                setFilters({ ...filters, status: e.target.value });
              }}
            >
              <option value="">All states</option>
              <option>active</option>
              <option>stale</option>
              <option>inactive</option>
            </select>
            <select
              aria-label="Business service"
              value={filters.service}
              onChange={(e) => {
                setOffset(0);
                setFilters({ ...filters, service: e.target.value });
              }}
            >
              <option value="">All services</option>
              {serviceOptions.map((service) => (
                <option key={service.id} value={service.id}>
                  {customerFacingEntityName(service.name)}
                </option>
              ))}
            </select>
            <select
              aria-label="Sort assets"
              value={filters.sort}
              onChange={(event) => {
                setOffset(0);
                setFilters({ ...filters, sort: event.target.value });
              }}
            >
              <option value="name">Name</option>
              <option value="health">Health</option>
              <option value="lastSeen">Last seen</option>
            </select>
            <select
              aria-label="Sort direction"
              value={filters.direction}
              onChange={(event) => {
                setOffset(0);
                setFilters({ ...filters, direction: event.target.value });
              }}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
            <SavedViewMenu
              storageKey="aot_my_views_assets"
              filters={filters}
              onApply={(saved) => {
                setOffset(0);
                setFilters((current) => ({ ...current, ...saved }));
              }}
            />
          </div>
        </details>
      </FilterBar>
      {[
        ['Vendor', filters.vendor, 'vendor'],
        ['Model', filters.model, 'model'],
        ['Platform', filters.source, 'source'],
        ['Service', serviceOptions.find((item) => item.id === filters.service)?.name, 'service'],
        ['State', filters.status, 'status'],
      ].filter(([, value]) => value).length > 0 && (
        <div className="active-filter-chips" aria-label="Active advanced filters">
          {[
            ['Vendor', filters.vendor, 'vendor'],
            ['Model', filters.model, 'model'],
            ['Platform', filters.source, 'source'],
            [
              'Service',
              serviceOptions.find((item) => item.id === filters.service)?.name,
              'service',
            ],
            ['State', filters.status, 'status'],
          ]
            .filter(([, value]) => value)
            .map(([label, value, key]) => (
              <button
                key={key}
                onClick={() => {
                  setOffset(0);
                  setFilters((current) => ({ ...current, [key as string]: '' }));
                }}
              >
                {label}: {value} <X size={13} />
              </button>
            ))}
        </div>
      )}
      <PopulationOutliersPanel items={outliers} onOpen={openFullAsset} />
      <div className={`asset-master-detail${selected ? ' has-selection' : ''}`}>
        <SectionCard className="asset-list-pane">
          {error ? (
            <ErrorState message={error} />
          ) : !items ? (
            <LoadingState />
          ) : visibleItems.length ? (
            <DataTable label="Assets">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Type / role</th>
                  <th>Site</th>
                  <th>Health</th>
                  <th>Freshness</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item, index) => (
                  <tr
                    key={item.id}
                    data-asset-id={item.id}
                    tabIndex={0}
                    aria-selected={selected?.asset?.id === item.id}
                    className={`asset-selectable-row${selected?.asset?.id === item.id ? ' selected-row' : ''}`}
                    onClick={() => selectAssetFromInventory(item.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        openFullAsset(item.id);
                        return;
                      }
                      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
                      event.preventDefault();
                      const nextIndex = Math.max(
                        0,
                        Math.min(
                          visibleItems.length - 1,
                          index + (event.key === 'ArrowDown' ? 1 : -1),
                        ),
                      );
                      const next = visibleItems[nextIndex];
                      if (!next) return;
                      selectAssetFromInventory(next.id);
                      window.requestAnimationFrame(() =>
                        document
                          .querySelector<HTMLElement>(`[data-asset-id="${next.id}"]`)
                          ?.focus(),
                      );
                    }}
                  >
                    <td>
                      <span className="asset-identity">
                        <AssetVisual asset={item} size={28} status={item.health} />
                        <span>
                          <strong>
                            {customerFacingEntityName(
                              item.displayName ?? item.name,
                              item.source === 'demo',
                            )}
                          </strong>
                          <small>
                            {[item.vendor, customerFacingEntityName(item.model)]
                              .filter(Boolean)
                              .join(' ') ||
                              item.hostname ||
                              'Identity pending'}
                          </small>
                        </span>
                      </span>
                    </td>
                    <td>
                      <strong>{item.assetType ?? item.kind}</strong>
                      <small>{assetPlatformSource(item)}</small>
                    </td>
                    <td>
                      <strong>
                        {item.siteDisplayName ?? customerFacingSiteName({ name: item.siteName })}
                      </strong>
                      <small>{item.serviceNames || 'No service assignment'}</small>
                    </td>
                    <td>
                      <StatusBadge value={item.health} />
                      <small>
                        {item.cpuPercent != null
                          ? `CPU ${Number(item.cpuPercent).toFixed(0)}%`
                          : item.memoryPercent != null
                            ? `Memory ${Number(item.memoryPercent).toFixed(0)}%`
                            : item.primaryInterfaceUtilization != null
                              ? `Link ${Number(item.primaryInterfaceUtilization).toFixed(0)}%`
                              : '—'}
                        {item.highestTemperatureCelsius != null
                          ? ` · ${Number(item.highestTemperatureCelsius).toFixed(1)}°C`
                          : ''}
                        {Number(item.openIncidentCount)
                          ? ` · ${item.openIncidentCount} incident${Number(item.openIncidentCount) === 1 ? '' : 's'}`
                          : ''}
                      </small>
                    </td>
                    <td>
                      <strong>{relativeTime(item.lastSeenAt)}</strong>
                      <small>{item.status || 'active'}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <EmptyState
              title="No matching assets"
              description="Adjust filters or connect infrastructure through an authorized Edge collector."
            />
          )}
          {items && total > 0 && (
            <div className="inventory-pagination">
              <span>
                {inventoryPage.start}–
                {Math.min(inventoryPage.end, inventoryPage.start + visibleItems.length - 1)} of{' '}
                {total} assets · page {inventoryPage.page} of {inventoryPage.pages} · at most{' '}
                {inventoryPage.limit} rendered
              </span>
              <div>
                <button
                  className="button secondary"
                  disabled={!inventoryPage.offset}
                  onClick={() => setOffset(Math.max(0, inventoryPage.offset - inventoryPage.limit))}
                >
                  Previous
                </button>
                <button
                  className="button secondary"
                  disabled={inventoryPage.end >= total}
                  onClick={() => setOffset(inventoryPage.offset + inventoryPage.limit)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </SectionCard>
        {selected ? (
          <AssetInspector
            data={selected}
            onClose={() => {
              setSelected(null);
              const params = new URLSearchParams(window.location.search);
              params.delete('selected');
              window.history.pushState(
                window.history.state,
                '',
                `/assets${params.size ? `?${params}` : ''}`,
              );
            }}
            onOpenFull={() => openFullAsset(selected.asset.id)}
            onOpsAI={onOpsAI}
          />
        ) : (
          <aside className="asset-inspector empty">
            <EmptyState
              title="Select an asset"
              description="The operational summary opens here without moving below the inventory."
            />
          </aside>
        )}
      </div>
    </>
  );
}

function AssetInspector({
  data,
  onClose,
  onOpenFull,
  onOpsAI,
}: {
  data: any;
  onClose: () => void;
  onOpenFull: () => void;
  onOpsAI: (question: string) => void;
}) {
  const asset = data.asset;
  const name = customerFacingEntityName(
    asset.display_name ?? asset.displayName ?? asset.name,
    asset.source === 'demo',
  );
  const primaryMetrics = data.metrics
    .filter((item: any) => /^(cpu|memory|storage|temperature|power|humidity)\./.test(item.name))
    .slice(0, 6);
  const openIncidents = data.incidents.filter((item: any) => item.status !== 'recovered');
  const openBehavior = data.behavior.findings.filter(
    (item: any) => !['recovered', 'closed'].includes(item.state),
  );
  return (
    <aside className="asset-inspector" aria-label={`${name} inspector`}>
      <header>
        <div className="asset-inspector-title">
          <AssetVisual asset={asset} size={58} status={asset.health} />
          <span>
            <small>{asset.vendor || 'Vendor not reported'}</small>
            <h2>{name}</h2>
            <strong>{asset.model || 'Model information unavailable'}</strong>
          </span>
        </div>
        <button className="icon-button" aria-label="Close asset inspector" onClick={onClose}>
          <X size={18} />
        </button>
      </header>
      <div className="asset-inspector-badges">
        <StatusBadge value={asset.health} />
        <StatusBadge value={asset.status} />
      </div>
      <dl className="asset-inspector-facts">
        <div>
          <dt>Type</dt>
          <dd>{asset.asset_type}</dd>
        </div>
        <div>
          <dt>Site</dt>
          <dd>{asset.site_display_name ?? customerFacingSiteName({ name: asset.site_name })}</dd>
        </div>
        <div>
          <dt>Freshness</dt>
          <dd>{relativeTime(asset.last_seen_at)}</dd>
        </div>
        <div>
          <dt>Behavior</dt>
          <dd>
            {openBehavior.length
              ? `${openBehavior.length} active finding${openBehavior.length === 1 ? '' : 's'}`
              : 'Normal baseline'}
          </dd>
        </div>
      </dl>
      <section>
        <h3>Primary metrics</h3>
        {primaryMetrics.length ? (
          <div className="asset-inspector-metrics">
            {primaryMetrics.map((item: any) => (
              <div key={item.name}>
                <span>{item.name.replaceAll('.', ' ')}</span>
                <strong>
                  {Number(item.value).toFixed(1)} {displayMetricUnit(item.unit)}
                </strong>
              </div>
            ))}
          </div>
        ) : (
          <p>No current primary metrics.</p>
        )}
      </section>
      <section>
        <h3>Current incidents</h3>
        {openIncidents.length ? (
          <div className="asset-inspector-incidents">
            {openIncidents.slice(0, 3).map((item: any) => (
              <div key={item.id}>
                <StatusBadge value={item.severity} />
                <span>{customerFacingEntityName(item.title)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p>No current incidents.</p>
        )}
      </section>
      <section>
        <h3>Collected by</h3>
        {data.collector?.state === 'REPORTED' || data.edgeCollectors?.length ? (
          <div className="asset-inspector-incidents">
            {(data.edgeCollectors?.length ? data.edgeCollectors : [data.collector])
              .slice(0, 2)
              .map((edge: any) => (
                <div key={edge.id}>
                  <StatusBadge value={edge.status} />
                  <span>
                    {customerFacingEdgeName(edge)} · {relativeTime(edge.lastHeartbeatAt)}
                  </span>
                </div>
              ))}
          </div>
        ) : (
          <p>Collector not reported.</p>
        )}
      </section>
      <div className="asset-inspector-actions">
        <button className="button primary" onClick={onOpenFull}>
          Open Full Asset
        </button>
        <button
          className="button secondary"
          onClick={() => onOpsAI(`Summarize the operational state and recent issues for ${name}.`)}
        >
          Ask OpsAI
        </button>
      </div>
    </aside>
  );
}

function AssetDetail({
  data,
  tab,
  setTab,
  close,
  onOpsAI,
  inventory,
  navigate,
  onHistoryRange,
  onInterfacePage,
  role,
}: any) {
  const asset = data.asset;
  const name = customerFacingEntityName(
    asset.display_name ?? asset.displayName ?? asset.name,
    asset.source === 'demo',
  );
  let metadata: Record<string, unknown> = {};
  try {
    metadata = JSON.parse(asset.discovered_metadata_json || '{}');
  } catch {
    /* Ignore malformed optional discovery metadata. */
  }
  const guestFilesystems = guestFilesystemRows(metadata);
  const temperatureMetrics = data.metrics.filter((item: any) =>
    item.name.startsWith('temperature.celsius.'),
  );
  const highestTemperature = temperatureMetrics.reduce(
    (highest: any, item: any) => (!highest || item.value > highest.value ? item : highest),
    null,
  );
  const [selectedInterface, setSelectedInterface] = useState<number | null>(
    data.interfaces[0]?.ifIndex ?? null,
  );
  const evidenceWindow: EvidenceWindow = {
    range: data.historyRange ?? '24h',
    from: data.historyFrom,
    to: data.historyTo,
  };
  const behaviorRows = behaviorMetricSummaries({
    metrics: data.metrics,
    history: data.history,
    baselines: data.behavior.baselines,
    findings: data.behavior.findings.filter((item: any) =>
      inEvidenceWindow(item.lastDetectedAt, evidenceWindow),
    ),
    capabilities: data.capabilities,
  });
  const maturity = baselineMaturity(behaviorRows);
  const [selectedBehaviorMetric, setSelectedBehaviorMetric] = useState<string>(
    behaviorRows[0]?.metric ?? 'cpu.percent',
  );
  useEffect(() => {
    if (!data.interfaces.some((item: any) => item.ifIndex === selectedInterface))
      setSelectedInterface(data.interfaces[0]?.ifIndex ?? null);
  }, [data.interfaceOffset, data.interfaces]);
  useEffect(() => {
    if (!behaviorRows.some((item) => item.metric === selectedBehaviorMetric))
      setSelectedBehaviorMetric(behaviorRows[0]?.metric ?? 'cpu.percent');
  }, [data.behavior, data.metrics, data.history]);
  const activeInterface =
    data.interfaces.find((item: any) => item.ifIndex === selectedInterface) ?? data.interfaces[0];
  const isVirtualMachine = /(?:proxmox|vmware|hyperv)-(?:vm|lxc)/.test(asset.asset_type);
  const isHypervisorHost = /(?:proxmox-node|vmware-host|hyperv-host)/.test(asset.asset_type);
  const metricCapability = (id: string) =>
    data.capabilities?.metrics?.find((item: any) => item.id === id);
  const networkCapability = data.capabilities?.areas?.network;
  const storageCapability = data.capabilities?.areas?.storage;
  const hasNetwork = networkCapability && networkCapability.state !== 'UNSUPPORTED';
  const hasDiskIO = storageCapability && storageCapability.state !== 'UNSUPPORTED';
  const network = networkSummary(data.metrics, data.history);
  const uptime = data.metrics.find((item: any) => item.name === 'uptime.seconds');
  const guestFilesystem = metricCapability('guest.filesystem');
  const selectedBehavior =
    behaviorRows.find((item) => item.metric === selectedBehaviorMetric) ?? behaviorRows[0];
  const selectedSeasonalBaseline =
    maturity.historyDays >= 14
      ? data.behavior.baselines.find(
          (item: any) =>
            item.bucket !== 'all' &&
            item.confidence !== 'cold_start' &&
            canonicalMetricId(String(item.metric)) === selectedBehavior?.metric,
        )
      : null;
  const scopedBehaviorFindings = data.behavior.findings.filter((item: any) =>
    inEvidenceWindow(item.lastDetectedAt, evidenceWindow),
  );
  const scopedEvents = data.events.filter((item: any) =>
    inEvidenceWindow(item.occurredAt, evidenceWindow),
  );
  const scopedLogs = data.logs.filter((item: any) =>
    inEvidenceWindow(item.occurredAt, evidenceWindow),
  );
  const scopedIncidents = data.incidents.filter((item: any) =>
    inEvidenceWindow(item.lastDetectedAt ?? item.updatedAt ?? item.openedAt, evidenceWindow),
  );
  const activeBehaviorFindings = scopedBehaviorFindings.filter(
    (item: any) => item.state !== 'recovered',
  );
  const recoveredBehaviorFindings = scopedBehaviorFindings.filter(
    (item: any) => item.state === 'recovered',
  );
  const lastBehaviorAnomaly = [...scopedBehaviorFindings].sort(
    (left: any, right: any) =>
      new Date(right.lastDetectedAt).getTime() - new Date(left.lastDetectedAt).getTime(),
  )[0];
  const [overlaysEnabled, setOverlaysEnabled] = useState(true);
  const [selectedOverlay, setSelectedOverlay] = useState<ChartOverlay | null>(null);
  const chartOverlays: ChartOverlay[] = [
    ...scopedBehaviorFindings.slice(0, 20).map((item: any) => ({
      id: `finding-${item.id}`,
      kind: 'anomaly' as const,
      label: item.summary,
      at: item.firstDetectedAt,
      endAt: item.recoveredAt ?? item.lastDetectedAt,
    })),
    ...scopedIncidents.slice(0, 10).map((item: any) => ({
      id: `incident-${item.id}`,
      kind: 'incident' as const,
      label: item.title,
      at: item.openedAt,
      endAt: item.status === 'recovered' ? item.updatedAt : undefined,
    })),
    ...scopedEvents.slice(0, 20).map((item: any) => ({
      id: `event-${item.id}`,
      kind: (/maintenance/i.test(`${item.category} ${item.summary}`)
        ? 'maintenance'
        : /configuration|change|authentication/i.test(String(item.category))
          ? 'change'
          : 'event') as ChartOverlay['kind'],
      label: item.summary,
      at: item.occurredAt,
    })),
  ];
  const sections = assetNavigation({
    hasNetwork: Boolean(hasNetwork),
    hasStorage: Boolean(hasDiskIO || isVirtualMachine || isHypervisorHost),
    hasInterfaces: data.interfaces.length > 0,
    isHypervisor: isHypervisorHost,
    hasHardware: isHypervisorHost || temperatureMetrics.length > 0,
    hasServices: data.services.length > 0,
  });
  const activeSection = assetSectionForTab(tab);
  const sectionTabs = sections.find((section) => section.name === activeSection)?.tabs ?? [
    'Overview',
  ];
  const goldenIds = goldenMetricIds(asset.asset_type);
  const goldenMetrics = data.metrics
    .filter((item: any) =>
      goldenIds.some(
        (id) => id === item.name || (id === 'temperature.celsius' && item.name.startsWith(id)),
      ),
    )
    .slice(0, 6);
  const recentEvent = scopedEvents[0] ?? scopedBehaviorFindings[0] ?? scopedIncidents[0];
  return (
    <SectionCard
      className="detail-panel"
      title={name}
      description={`${asset.asset_type} · ${
        asset.site_display_name ?? customerFacingSiteName({ name: asset.site_name })
      }`}
      action={
        <div className="detail-actions">
          <FreshnessIndicator value={asset.last_seen_at} state={asset.status} />
          <ContextActionMenu label="Asset actions">
            <TopologyAction onClick={() => navigate('Topology', { topologyAssetId: asset.id })} />
            <button onClick={() => navigate('Incidents', { query: { asset: asset.id } })}>
              View incidents
            </button>
            <button onClick={() => navigate('Capacity', { query: { asset: asset.id } })}>
              View capacity
            </button>
            <button onClick={() => navigate('Maps', { query: { asset: asset.id } })}>
              Show on map
            </button>
            <button
              onClick={() =>
                onOpsAI(`Summarize the operational state and recent issues for ${name}.`)
              }
            >
              Ask OpsAI
            </button>
            <MaintenanceAction onClick={() => navigate('Settings')} />
          </ContextActionMenu>
          <button className="button secondary" onClick={close}>
            Back to assets
          </button>
        </div>
      }
    >
      <Tabs
        items={sections.map((section) => section.name)}
        active={activeSection}
        onChange={(sectionName) =>
          setTab(sections.find((section) => section.name === sectionName)?.tabs[0] ?? 'Overview')
        }
        label="Asset areas"
      />
      {sectionTabs.length > 1 && (
        <Tabs
          items={sectionTabs}
          active={tab}
          onChange={setTab}
          label={`${activeSection} sections`}
        />
      )}
      {activeSection !== 'Configuration' && (
        <AssetTimeContext value={evidenceWindow} onChange={onHistoryRange} />
      )}
      {['Performance', 'Intelligence'].includes(activeSection) && (
        <div className="chart-overlay-control">
          <label>
            <input
              type="checkbox"
              checked={overlaysEnabled}
              onChange={(event) => setOverlaysEnabled(event.target.checked)}
            />{' '}
            Evidence overlays
          </label>
          <small>
            Expected bands, anomalies, incidents, device events, changes, and maintenance are
            layered only when reported.
          </small>
        </div>
      )}
      {selectedOverlay && (
        <div className="overlay-context" role="status">
          <span>
            <strong>{humanCapabilityState(selectedOverlay.kind)}</strong>
            {selectedOverlay.label}
          </span>
          <button className="button secondary" onClick={() => setTab('Investigate')}>
            Investigate
          </button>
          <button aria-label="Close evidence context" onClick={() => setSelectedOverlay(null)}>
            <X size={15} />
          </button>
        </div>
      )}
      <RelationshipBreadcrumb
        siteName={asset.site_display_name ?? customerFacingSiteName({ name: asset.site_name })}
        assets={inventory}
        assetId={asset.id}
        onSelect={(id) => navigate('Assets', { detailId: id })}
      />
      {tab === 'Overview' && (
        <>
          <div className="asset-detail-identity">
            <AssetVisual asset={asset} size={72} status={asset.health} />
            <span>
              <small>{asset.vendor || 'Vendor not reported'}</small>
              <strong>{asset.model || name}</strong>
              <span>
                {asset.asset_type} · {asset.health}
              </span>
            </span>
          </div>
          <HealthBreakdown
            health={asset.health}
            factors={
              data.healthProvenance
                ? [
                    {
                      label: data.healthProvenance.reason,
                      detail: `${data.healthProvenance.source} · first observed ${data.healthProvenance.firstObserved ? formatDate(data.healthProvenance.firstObserved) : 'not reported'}${data.healthProvenance.relatedIncidentId ? ` · incident ${data.healthProvenance.relatedIncidentId}` : ''}${data.healthProvenance.relatedFindingId ? ` · finding ${data.healthProvenance.relatedFindingId}` : ''}`,
                      status: data.healthProvenance.state,
                    },
                  ]
                : deriveHealthFactors({
                    health: asset.health,
                    incidents: data.incidents,
                    interfaces: data.interfaces,
                  })
            }
          />
          <div className="overview-focus-grid">
            <section className="golden-metrics-panel" aria-label="Golden metrics">
              <header>
                <span className="eyebrow">Golden metrics</span>
                <h3>Current operational signals</h3>
              </header>
              <div>
                {goldenMetrics.map((item: any) => (
                  <span key={item.name}>
                    <small>{metricLabel(item.name)}</small>
                    <strong>{formatMetricValue(Number(item.value), item.unit)}</strong>
                    <em>{relativeTime(item.observedAt)}</em>
                  </span>
                ))}
              </div>
            </section>
            <section
              className="overview-priority-panel"
              aria-label="Current operational priorities"
            >
              <div>
                <span>Active anomalies</span>
                <strong>{activeBehaviorFindings.length}</strong>
                <small>
                  {activeBehaviorFindings[0]?.summary ?? 'No active anomaly in this window'}
                </small>
              </div>
              <div>
                <span>Service impact</span>
                <strong>
                  {data.services.length
                    ? data.services
                        .map((service: any) => customerFacingEntityName(service.name))
                        .join(', ')
                    : 'None identified'}
                </strong>
                <small>
                  {data.services.length
                    ? data.services.map((service: any) => service.healthState).join(' · ')
                    : 'Add a business service relationship for impact context'}
                </small>
              </div>
              <div>
                <span>Recent important event / change</span>
                <strong>{recentEvent?.summary ?? recentEvent?.title ?? 'None reported'}</strong>
                <small>
                  {recentEvent
                    ? relativeTime(
                        recentEvent.occurredAt ??
                          recentEvent.lastDetectedAt ??
                          recentEvent.updatedAt,
                      )
                    : `No evidence in ${data.historyRange}`}
                </small>
              </div>
              <div>
                <span>Collector freshness</span>
                <strong>
                  {data.collector?.state === 'REPORTED'
                    ? customerFacingEdgeName(data.collector)
                    : 'Not reported'}
                </strong>
                <small>
                  {data.collector?.lastHeartbeatAt
                    ? relativeTime(data.collector.lastHeartbeatAt)
                    : relativeTime(asset.last_seen_at)}
                </small>
              </div>
            </section>
            <PredictiveRiskPanel forecasts={data.forecasts} history={data.history} />
          </div>
          <MonitoringCoveragePanel data={data} navigate={navigate} setTab={setTab} />
          <details className="operational-context-panel">
            <summary>Collector and platform context</summary>
            <div className="detail-summary">
              {highestTemperature && (
                <div className="thermal-highlight">
                  <span>Highest temperature</span>
                  <strong>
                    {Number(highestTemperature.value).toFixed(
                      Number(highestTemperature.value) % 1 ? 1 : 0,
                    )}
                    °C
                  </strong>
                  <small>
                    {temperatureSensorLabel(highestTemperature.name)} · threshold source{' '}
                    {String(
                      metadata[
                        `temp.${highestTemperature.name.split('.')[2]?.split('_')[0]}.thresholdSource`
                      ] ?? 'NONE',
                    )}
                  </small>
                </div>
              )}
              <div>
                <span>Platform</span>
                <AssetPlatform item={asset} />
              </div>
              <div>
                <span>Status</span>
                <StatusBadge value={asset.status} />
              </div>
              <div>
                <span>Management address</span>
                <strong>{asset.management_ip || 'Not reported'}</strong>
              </div>
              {asset.source === 'proxmox' && asset.asset_type === 'proxmox-vm' && (
                <>
                  <div>
                    <span>Guest Agent</span>
                    <strong>
                      {metadata.guestAgentState === 'RESPONDING'
                        ? 'Healthy'
                        : String(metadata.guestAgentState ?? 'Not configured')}
                    </strong>
                  </div>
                  <div>
                    <span>Guest filesystems</span>
                    <strong>{Number(metadata.guestFilesystemMonitoredCount ?? 0)} monitored</strong>
                  </div>
                  <div>
                    <span>Primary guest IP</span>
                    <strong>{String(metadata.guestPrimaryIP ?? 'Not reported')}</strong>
                  </div>
                </>
              )}
              <div>
                <span>Last seen</span>
                <strong>{formatDate(asset.last_seen_at)}</strong>
              </div>
              <div>
                <span>Collected by</span>
                <strong>
                  {data.collector?.state === 'REPORTED'
                    ? customerFacingEdgeName(data.collector)
                    : 'Not reported'}
                </strong>
              </div>
              <div>
                <span>Integration</span>
                <strong>{data.collector?.integration ?? 'Not reported'}</strong>
              </div>
              <div>
                <span>Source</span>
                <strong>{data.collector?.sourceReference ?? 'Not reported'}</strong>
              </div>
              <div>
                <span>Parent context</span>
                <strong>{asset.parent_name || 'Top-level asset'}</strong>
              </div>
              <div>
                <span>Business services</span>
                <strong className="entity-link-list">
                  {data.services.length
                    ? data.services.map((service: any, index: number) => (
                        <span key={service.id}>
                          {index > 0 && ' · '}
                          <a
                            href={portalPath('Services', service.id)}
                            onClick={(event) => {
                              if (
                                event.button !== 0 ||
                                event.metaKey ||
                                event.ctrlKey ||
                                event.shiftKey ||
                                event.altKey
                              )
                                return;
                              event.preventDefault();
                              navigate('Services', { detailId: service.id });
                            }}
                          >
                            {customerFacingEntityName(service.name)}
                          </a>
                        </span>
                      ))
                    : 'No linked service'}
                </strong>
              </div>
              {uptime && (
                <div>
                  <span>Uptime</span>
                  <strong>{formatMetricValue(Number(uptime.value), uptime.unit)}</strong>
                </div>
              )}
            </div>
          </details>
          {isVirtualMachine && (
            <section className="guest-telemetry-card" aria-label="Guest telemetry">
              <div>
                <span>QEMU Guest Agent</span>
                <strong>
                  {metadata.guestAgentState === 'RESPONDING'
                    ? 'Healthy'
                    : humanCapabilityState(String(metadata.guestAgentState ?? 'NOT_REPORTED'))}
                </strong>
              </div>
              <div>
                <span>Guest filesystems</span>
                <strong>
                  {guestFilesystem?.state === 'SUPPORTED_VIA_GUEST_AGENT'
                    ? 'Monitored'
                    : humanCapabilityState(guestFilesystem?.state)}
                </strong>
              </div>
              <div>
                <span>Guest OS</span>
                <strong>
                  {metadata.guestOSState === 'AVAILABLE'
                    ? String(metadata.guestOSName ?? 'Available')
                    : humanCapabilityState(String(metadata.guestOSState ?? 'NOT_REPORTED'))}
                </strong>
              </div>
              <div>
                <span>Primary guest IP</span>
                <strong>{String(metadata.guestPrimaryIP ?? 'Not reported')}</strong>
              </div>
              <p>
                {guestFilesystem?.state === 'SUPPORTED_VIA_GUEST_AGENT'
                  ? `${Number(metadata.guestFilesystemMonitoredCount ?? 0)} guest filesystem monitored on the normal Edge collection cadence.`
                  : (guestFilesystem?.guidance ??
                    'Guest telemetry capability has not been reported.')}
              </p>
            </section>
          )}
        </>
      )}
      {tab === 'Metrics' &&
        (data.metrics.length || data.history.length ? (
          <>
            <div className="metric-grid">
              {data.metrics.map((item: any) => (
                <div key={item.name}>
                  <span>{metricLabel(item.name)}</span>
                  <strong>{formatMetricValue(Number(item.value), item.unit)}</strong>
                  <small>
                    {parseApiDate(item.observedAt).getTime() < Date.now() - 15 * 60_000
                      ? `Stale · last sample ${formatDate(item.observedAt)}`
                      : relativeTime(item.observedAt)}
                  </small>
                </div>
              ))}
            </div>
            <div className="chart-grid">
              {groupHistory(data.history)
                .slice(0, 8)
                .map((group: any) => {
                  const baseline = data.behavior.baselines.find(
                    (item: any) => item.metric === group.metric && item.bucket === 'all',
                  );
                  return (
                    <TimeSeriesChart
                      key={group.key}
                      metric={group.metric}
                      unit={group.points[0]?.unit}
                      points={group.points}
                      lower={baseline?.lowerBound}
                      upper={baseline?.upperBound}
                      range={data.historyRange}
                      capability={metricCapability(group.metric)?.state}
                      overlays={chartOverlays}
                      overlaysEnabled={overlaysEnabled}
                      onOverlaySelect={setSelectedOverlay}
                    />
                  );
                })}
            </div>
          </>
        ) : (
          <EmptyState
            title="No current metrics"
            description="This asset has not reported a supported metric yet."
          />
        ))}
      {tab === 'Network' && (
        <div className="capability-workspace network-workspace">
          <div className="capability-heading">
            <div>
              <p className="eyebrow">NETWORK OVERVIEW</p>
              <h3>{networkCapability?.sourceLabel ?? 'Normalized network telemetry'}</h3>
            </div>
            <StatusBadge
              value={networkCapability?.state ?? 'NOT_REPORTED'}
              label={humanCapabilityState(networkCapability?.state)}
            />
          </div>
          {['AVAILABLE', 'STALE'].includes(networkCapability?.state) ? (
            <>
              <div className="network-summary-grid">
                <div>
                  <span>Current RX</span>
                  <strong>{humanBitsPerSecond(network.receive.current)}</strong>
                </div>
                <div>
                  <span>Current TX</span>
                  <strong>{humanBitsPerSecond(network.transmit.current)}</strong>
                </div>
                <div>
                  <span>Average RX</span>
                  <strong>{humanBitsPerSecond(network.receive.average)}</strong>
                </div>
                <div>
                  <span>Average TX</span>
                  <strong>{humanBitsPerSecond(network.transmit.average)}</strong>
                </div>
                <div>
                  <span>Peak RX</span>
                  <strong>{humanBitsPerSecond(network.receive.peak)}</strong>
                </div>
                <div>
                  <span>Peak TX</span>
                  <strong>{humanBitsPerSecond(network.transmit.peak)}</strong>
                </div>
                <div>
                  <span>Total received</span>
                  <strong>{humanBytes(network.totalReceived)}</strong>
                </div>
                <div>
                  <span>Total transmitted</span>
                  <strong>{humanBytes(network.totalTransmitted)}</strong>
                </div>
              </div>
              <NetworkTrafficChart
                history={data.history}
                range={data.historyRange}
                overlays={chartOverlays}
                overlaysEnabled={overlaysEnabled}
                onOverlaySelect={setSelectedOverlay}
              />
              {data.interfaces.length ? (
                <DataTable label="Reported network interfaces">
                  <thead>
                    <tr>
                      <th>Interface</th>
                      <th>MAC</th>
                      <th>Network / bridge</th>
                      <th>State</th>
                      <th>RX</th>
                      <th>TX</th>
                      <th>Speed</th>
                      <th>Utilization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.interfaces.map((item: any) => {
                      const rx = interfaceBitsPerSecond(Number(item.speedBps), item.inUtilization);
                      const tx = interfaceBitsPerSecond(Number(item.speedBps), item.outUtilization);
                      const capacityKnown = Number(item.speedBps) > 0;
                      return (
                        <tr key={item.ifIndex}>
                          <td>{item.name}</td>
                          <td>{item.mac || 'Not reported'}</td>
                          <td>{item.alias || item.description || 'Not reported'}</td>
                          <td>
                            <StatusBadge value={item.operStatus} />
                          </td>
                          <td>{humanBitsPerSecond(rx)}</td>
                          <td>{humanBitsPerSecond(tx)}</td>
                          <td>
                            {capacityKnown
                              ? humanBitsPerSecond(Number(item.speedBps))
                              : 'Not reported'}
                          </td>
                          <td>
                            {capacityKnown &&
                            item.inUtilization != null &&
                            item.outUtilization != null
                              ? `${Number(item.inUtilization).toFixed(1)}% RX · ${Number(item.outUtilization).toFixed(1)}% TX`
                              : 'Not calculated'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </DataTable>
              ) : (
                <div className="inline-callout">
                  <Network size={20} />
                  <div>
                    <strong>Aggregate throughput available</strong>
                    <p>
                      Per-interface telemetry is not reported by this source. Aggregate throughput
                      is not attributed to a vNIC.
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <EmptyState
              title={humanCapabilityState(networkCapability?.state)}
              description={
                networkCapability?.guidance ??
                'The canonical capability registry has no network evidence for this asset.'
              }
            />
          )}
        </div>
      )}
      {tab === 'Storage' && (
        <div className="behavior-workspace">
          {isVirtualMachine && guestFilesystems.length > 0 && (
            <div className="table-shell">
              <table>
                <thead>
                  <tr>
                    <th>Drive</th>
                    <th>Type</th>
                    <th>Used</th>
                    <th>Free</th>
                    <th>Capacity</th>
                    <th>Utilization</th>
                  </tr>
                </thead>
                <tbody>
                  {guestFilesystems.map((filesystem) => (
                    <tr key={filesystem.id}>
                      <td>{filesystem.mount || filesystem.id}</td>
                      <td>{filesystem.type || 'Unknown'}</td>
                      <td>
                        {filesystem.usedBytes == null
                          ? 'Usage unavailable'
                          : formatMetricValue(filesystem.usedBytes, 'bytes')}
                      </td>
                      <td>
                        {filesystem.freeBytes == null
                          ? '—'
                          : formatMetricValue(filesystem.freeBytes, 'bytes')}
                      </td>
                      <td>
                        {filesystem.capacityBytes == null
                          ? '—'
                          : formatMetricValue(filesystem.capacityBytes, 'bytes')}
                      </td>
                      <td>
                        {filesystem.utilizationPercent == null
                          ? 'Usage unavailable'
                          : `${filesystem.utilizationPercent.toFixed(2)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {isVirtualMachine && guestFilesystems.length > 0 && (
            <div className="inline-callout">
              <HardDrive size={20} />
              <div>
                <strong>Guest filesystem behavior is learning</strong>
                <p>
                  Current measurements are available. Expected bands and capacity prediction remain
                  unavailable until sufficient valid history is collected.
                </p>
              </div>
            </div>
          )}
          <div className="chart-grid">
            {groupHistory(data.history)
              .filter(
                (group: any) =>
                  group.metric.startsWith('disk.') ||
                  group.metric.startsWith('storage.') ||
                  group.metric.startsWith('virtual_disk.') ||
                  group.metric.startsWith('guest.filesystem.'),
              )
              .map((group: any) => (
                <TimeSeriesChart
                  key={group.key}
                  metric={group.metric}
                  unit={group.points[0]?.unit}
                  points={group.points}
                  range={data.historyRange}
                  capability={metricCapability(group.metric)?.state}
                  overlays={chartOverlays}
                  overlaysEnabled={overlaysEnabled}
                  onOverlaySelect={setSelectedOverlay}
                />
              ))}
            {!groupHistory(data.history).some(
              (group: any) =>
                group.metric.startsWith('disk.') ||
                group.metric.startsWith('storage.') ||
                group.metric.startsWith('virtual_disk.') ||
                group.metric.startsWith('guest.filesystem.'),
            ) && (
              <EmptyState
                title={
                  storageCapability?.state === 'AVAILABLE'
                    ? 'No samples in the selected range'
                    : humanCapabilityState(storageCapability?.state)
                }
                description={
                  isVirtualMachine
                    ? 'Disk I/O and virtual capacity are separate from guest filesystem utilization.'
                    : (storageCapability?.guidance ??
                      'This source has not reported storage telemetry for the selected period.')
                }
              />
            )}
          </div>
        </div>
      )}
      {tab === 'Hardware' && (
        <div className="behavior-workspace">
          <div className="inline-callout">
            <HeartPulse size={20} />
            <div>
              <strong>Hardware telemetry source</strong>
              <p>
                Hypervisor native sensors:{' '}
                {temperatureMetrics.length ? 'Available' : 'Not available'}
              </p>
              <p>Redfish: {asset.source === 'redfish' ? 'Configured' : 'Not configured'}</p>
            </div>
          </div>
          {temperatureMetrics.length ? (
            <div className="metric-grid">
              {temperatureMetrics.map((item: any) => (
                <div key={item.name}>
                  <span>{metricLabel(item.name)}</span>
                  <strong>{formatMetricValue(Number(item.value), item.unit)}</strong>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="Physical hardware sensors are not reported"
              description="Add a read-only Redfish hardware-management integration to monitor temperature, fans, power and chassis health."
              action={
                <button className="button primary" onClick={() => navigate('Integrations')}>
                  Add Hardware Management
                </button>
              }
            />
          )}
        </div>
      )}
      {tab === 'Virtual Machines' &&
        (data.children.length ? (
          <CompactAssetTable items={data.children} />
        ) : (
          <EmptyState
            title="No virtual machines reported"
            description="No child virtual machines are currently associated with this host."
          />
        ))}
      {tab === 'Interfaces' &&
        (data.interfaces.length ? (
          <div className="interface-workspace">
            <div className="interface-page-summary">
              <strong>
                {Number(data.interfaceOffset ?? 0) + 1}–
                {Math.min(
                  Number(data.interfaceTotal ?? data.interfaces.length),
                  Number(data.interfaceOffset ?? 0) + data.interfaces.length,
                )}{' '}
                of {data.interfaceTotal ?? data.interfaces.length}
              </strong>
              <span>25 interfaces per page</span>
            </div>
            <div className="interface-page-grid">
              {data.interfaces.map((item: any) => {
                const inbound = interfaceBitsPerSecond(Number(item.speedBps), item.inUtilization);
                const outbound = interfaceBitsPerSecond(Number(item.speedBps), item.outUtilization);
                return (
                  <button
                    type="button"
                    className={`interface-card${activeInterface?.ifIndex === item.ifIndex ? ' selected' : ''}`}
                    key={item.ifIndex}
                    onClick={() => setSelectedInterface(item.ifIndex)}
                    aria-pressed={activeInterface?.ifIndex === item.ifIndex}
                  >
                    <header>
                      <div>
                        <strong>{item.name}</strong>
                        <small>
                          {item.alias || item.description || `Interface ${item.ifIndex}`}
                        </small>
                      </div>
                      <StatusBadge
                        value={item.operStatus === 'up' ? 'healthy' : item.operStatus}
                        label={item.operStatus}
                      />
                    </header>
                    <dl>
                      <div>
                        <dt>Admin</dt>
                        <dd>{item.adminStatus}</dd>
                      </div>
                      <div>
                        <dt>Speed</dt>
                        <dd>{humanBitsPerSecond(Number(item.speedBps))}</dd>
                      </div>
                      <div>
                        <dt>Inbound</dt>
                        <dd>{humanBitsPerSecond(inbound)}</dd>
                      </div>
                      <div>
                        <dt>Outbound</dt>
                        <dd>{humanBitsPerSecond(outbound)}</dd>
                      </div>
                      <div>
                        <dt>Utilization</dt>
                        <dd>
                          {Number(item.inUtilization ?? 0).toFixed(1)}% in ·{' '}
                          {Number(item.outUtilization ?? 0).toFixed(1)}% out
                        </dd>
                      </div>
                      <div>
                        <dt>Errors / discards</dt>
                        <dd>
                          {Number(item.inErrors) + Number(item.outErrors)} /{' '}
                          {Number(item.inDiscards) + Number(item.outDiscards)}
                        </dd>
                      </div>
                    </dl>
                    <div className="interface-bars" aria-label={`${item.name} utilization`}>
                      <span style={{ width: `${Math.min(100, Number(item.inUtilization ?? 0))}%` }}>
                        Inbound
                      </span>
                      <span
                        style={{ width: `${Math.min(100, Number(item.outUtilization ?? 0))}%` }}
                      >
                        Outbound
                      </span>
                    </div>
                    <small>64-bit counter-derived rates · resets and rollovers rejected</small>
                  </button>
                );
              })}
            </div>
            {activeInterface && (
              <section
                className="selected-interface-chart"
                aria-label={`${activeInterface.name} current bandwidth`}
              >
                <header>
                  <div>
                    <strong>{activeInterface.name} bandwidth</strong>
                    <small>
                      Only the selected interface is charted; no per-interface query fan-out.
                    </small>
                  </div>
                  <StatusBadge
                    value={
                      activeInterface.operStatus === 'up' ? 'healthy' : activeInterface.operStatus
                    }
                  />
                </header>
                <div className="interface-bandwidth-chart">
                  <span
                    style={{
                      height: `${Math.max(2, Math.min(100, Number(activeInterface.inUtilization ?? 0)))}%`,
                    }}
                  >
                    In {Number(activeInterface.inUtilization ?? 0).toFixed(1)}%
                  </span>
                  <span
                    style={{
                      height: `${Math.max(2, Math.min(100, Number(activeInterface.outUtilization ?? 0)))}%`,
                    }}
                  >
                    Out {Number(activeInterface.outUtilization ?? 0).toFixed(1)}%
                  </span>
                </div>
              </section>
            )}
            <div className="pagination-actions">
              <button
                className="button secondary"
                disabled={!Number(data.interfaceOffset)}
                onClick={() =>
                  onInterfacePage(
                    Math.max(0, Number(data.interfaceOffset) - Number(data.interfaceLimit ?? 25)),
                  )
                }
              >
                Previous interfaces
              </button>
              <button
                className="button secondary"
                disabled={
                  Number(data.interfaceOffset ?? 0) + data.interfaces.length >=
                  Number(data.interfaceTotal ?? data.interfaces.length)
                }
                onClick={() =>
                  onInterfacePage(
                    Number(data.interfaceOffset ?? 0) + Number(data.interfaceLimit ?? 25),
                  )
                }
              >
                Next interfaces
              </button>
            </div>
          </div>
        ) : (
          <EmptyState
            title="Interfaces not applicable"
            description="This asset has not reported SNMP interface inventory."
          />
        ))}
      {tab === 'Logs' &&
        (scopedLogs.length ? (
          <DataTable label="Asset log evidence">
            <thead>
              <tr>
                <th>Time</th>
                <th>Source</th>
                <th>Severity</th>
                <th>Evidence</th>
              </tr>
            </thead>
            <tbody>
              {scopedLogs.map((item: any) => (
                <tr key={item.id}>
                  <td>{relativeTime(item.occurredAt)}</td>
                  <td>{item.source}</td>
                  <td>
                    <StatusBadge value={item.severity} />
                  </td>
                  <td>{item.summary}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : (
          <EmptyState
            title={
              data.capabilities?.logs?.state === 'NOT_CONFIGURED'
                ? `No log source is configured for ${name}.`
                : 'No logs for this period'
            }
            description={
              data.capabilities?.logs?.guidance ??
              'A configured source has not reported log evidence for this asset in the selected period.'
            }
            action={
              data.capabilities?.logs?.state === 'NOT_CONFIGURED' ? (
                <button className="button primary" onClick={() => navigate('Integrations')}>
                  Configure Log Source
                </button>
              ) : undefined
            }
          />
        ))}
      {tab === 'Events' &&
        (scopedEvents.length ? (
          <DataTable label="Asset events">
            <thead>
              <tr>
                <th>Time</th>
                <th>Category</th>
                <th>Severity</th>
                <th>Observed event</th>
              </tr>
            </thead>
            <tbody>
              {scopedEvents.map((item: any) => (
                <tr key={item.id}>
                  <td>{relativeTime(item.occurredAt)}</td>
                  <td>{item.category}</td>
                  <td>
                    <StatusBadge value={item.severity} />
                  </td>
                  <td>{item.summary}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : (
          <EmptyState
            title="No events for this asset in the selected period"
            description="No lifecycle, configuration, incident, or collector event is associated with this asset in the selected 30 days."
          />
        ))}
      {tab === 'Topology' && (
        <div className="inline-callout">
          <GitBranch size={20} />
          <div>
            <strong>Operational relationship context</strong>
            <p>
              {asset.parent_name ? `Parent: ${asset.parent_name}. ` : ''}
              {data.children.length} discovered child relationships and {data.services.length}{' '}
              service relationships are available.
            </p>
          </div>
          <TopologyAction onClick={() => navigate('Topology', { topologyAssetId: asset.id })} />
        </div>
      )}
      {tab === 'Map' && (
        <div className="inline-callout">
          <MapPinned size={20} />
          <div>
            <strong>Physical and geographic context</strong>
            <p>Open the spatial workspace to locate this asset using confirmed placement.</p>
          </div>
          <button
            className="button secondary"
            onClick={() => navigate('Maps', { query: { asset: asset.id } })}
          >
            Show on map
          </button>
        </div>
      )}
      {tab === 'Relationships' &&
        (data.children.length || asset.parent_name ? (
          <div className="relationship-map relationship-map-actionable">
            <div className="relationship-node parent">
              <span>Parent</span>
              <strong>{asset.parent_name || name}</strong>
            </div>
            {data.children.map((child: any) => (
              <div className="relationship-node" key={child.id}>
                <span>{child.assetType}</span>
                <strong>
                  {customerFacingEntityName(child.displayName, child.source === 'demo')}
                </strong>
                <StatusBadge value={child.health} />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No related assets"
            description="No parent or child relationship is currently reported."
          />
        ))}
      {tab === 'Services' &&
        (data.services.length ? (
          <DataTable label="Business service relationships">
            <thead>
              <tr>
                <th>Service</th>
                <th>Criticality</th>
                <th>Health</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.services.map((service: any) => (
                <tr key={service.id}>
                  <td>{customerFacingEntityName(service.name)}</td>
                  <td>{service.criticality}</td>
                  <td>
                    <StatusBadge value={service.healthState} />
                  </td>
                  <td>
                    <button
                      className="table-link"
                      onClick={() => navigate('Services', { detailId: service.id })}
                    >
                      Open service
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : (
          <EmptyState
            title="No business service relationship"
            description="Relate this asset to the business service it supports to add customer-impact context."
            action={
              <button className="button primary" onClick={() => navigate('Services')}>
                Manage services
              </button>
            }
          />
        ))}
      {tab === 'History' &&
        (scopedEvents.length || scopedIncidents.length || scopedBehaviorFindings.length ? (
          <DataTable label="Asset timeline">
            <thead>
              <tr>
                <th>Time</th>
                <th>Type</th>
                <th>State</th>
                <th>Event</th>
              </tr>
            </thead>
            <tbody>
              {[
                ...scopedEvents.map((item: any) => ({
                  id: `event-${item.id}`,
                  at: item.occurredAt,
                  type: item.category,
                  state: item.severity,
                  text: item.summary,
                })),
                ...scopedIncidents.flatMap((item: any) => [
                  {
                    id: `incident-${item.id}`,
                    at: item.updatedAt,
                    type: 'Incident',
                    state: item.status,
                    text: customerFacingEntityName(item.title),
                  },
                ]),
                ...scopedBehaviorFindings.map((item: any) => ({
                  id: `finding-${item.id}`,
                  at: item.lastDetectedAt,
                  type: 'Behavior',
                  state: item.state,
                  text: item.summary,
                })),
              ]
                .sort((a: any, b: any) => new Date(b.at).getTime() - new Date(a.at).getTime())
                .slice(0, 100)
                .map((item: any) => (
                  <tr key={item.id}>
                    <td>{formatDate(item.at)}</td>
                    <td>{item.type}</td>
                    <td>
                      <StatusBadge value={item.state} />
                    </td>
                    <td>{item.text}</td>
                  </tr>
                ))}
            </tbody>
          </DataTable>
        ) : (
          <EmptyState
            title="No asset timeline events"
            description="Discovery, lifecycle, incident, configuration, maintenance, and behavior changes will appear here when observed. Metrics remain in the Metrics tab."
          />
        ))}
      {tab === 'Incidents' && (
        <>
          <CompactIncidentTable items={scopedIncidents} />
          {scopedIncidents.length > 0 && (
            <button
              className="button secondary investigation-entry"
              onClick={() => setTab('Investigate')}
            >
              Investigate current evidence
            </button>
          )}
        </>
      )}
      {tab === 'Behavior' && (
        <div className="behavior-workspace">
          <div className="behavior-summary-grid">
            <div>
              <span>Overall state</span>
              <strong>
                {humanCapabilityState(
                  activeBehaviorFindings.length
                    ? 'ANOMALOUS'
                    : data.behavior.baselines.length
                      ? 'HEALTHY_BASELINE'
                      : 'LEARNING',
                )}
              </strong>
            </div>
            <div>
              <span>Active findings</span>
              <strong>{activeBehaviorFindings.length}</strong>
            </div>
            <div>
              <span>Last anomaly</span>
              <strong>
                {lastBehaviorAnomaly
                  ? relativeTime(lastBehaviorAnomaly.lastDetectedAt)
                  : 'None observed'}
              </strong>
            </div>
            <div>
              <span>Baseline maturity</span>
              <strong>{maturity.label}</strong>
              <small>
                {maturity.historyDays
                  ? `${maturity.historyDays} days of history`
                  : 'History is accumulating'}
              </small>
            </div>
          </div>
          {behaviorRows.length ? (
            <DataTable label="Behavior by metric">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Current</th>
                  <th>Expected</th>
                  <th>Confidence</th>
                  <th>Deviation</th>
                  <th>Trend</th>
                  <th>Rate of change</th>
                  <th>Status</th>
                  <th>Last anomaly</th>
                </tr>
              </thead>
              <tbody>
                {behaviorRows.map((item) => (
                  <tr
                    className={item.metric === selectedBehavior?.metric ? 'selected-row' : ''}
                    key={item.metric}
                    onClick={() => setSelectedBehaviorMetric(item.metric)}
                  >
                    <td>
                      <button
                        className="table-link"
                        onClick={() => setSelectedBehaviorMetric(item.metric)}
                      >
                        {metricLabel(item.metric)}
                      </button>
                    </td>
                    <td>{formatMetricValue(item.currentValue, item.unit)}</td>
                    <td>
                      {item.lowerBound == null || item.upperBound == null
                        ? 'Learning'
                        : `${formatMetricValue(item.lowerBound, item.unit)} – ${formatMetricValue(item.upperBound, item.unit)}`}
                    </td>
                    <td>
                      <StatusBadge value={item.confidence} />
                    </td>
                    <td>{item.deviation}</td>
                    <td>{item.trend}</td>
                    <td>
                      {item.rateOfChange
                        ? `${formatMetricValue(item.rateOfChange.perHour, item.unit)}/h over ${item.rateOfChange.durationMinutes}m`
                        : 'Insufficient samples'}
                    </td>
                    <td>
                      <StatusBadge value={item.status} />
                    </td>
                    <td>{item.lastAnomaly ? relativeTime(item.lastAnomaly) : 'None observed'}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <EmptyState
              title="No behavior-capable telemetry"
              description="Behavior becomes available when this asset reports supported metrics."
            />
          )}
          {selectedBehavior && (
            <section
              className="behavior-detail"
              aria-label={`${metricLabel(selectedBehavior.metric)} behavior detail`}
            >
              <div className="capability-heading">
                <div>
                  <span className="eyebrow">Selected behavior</span>
                  <h3>{metricLabel(selectedBehavior.metric)}</h3>
                  <p>
                    {selectedBehavior.lowerBound == null
                      ? 'A reliable expected band is still being learned.'
                      : `Actual values are compared with the learned ${formatMetricValue(selectedBehavior.lowerBound, selectedBehavior.unit)}–${formatMetricValue(selectedBehavior.upperBound, selectedBehavior.unit)} range.`}
                  </p>
                </div>
                <button
                  className="button secondary"
                  onClick={() =>
                    onOpsAI(
                      `Explain the observed behavior for ${name}, metric ${metricLabel(selectedBehavior.metric)}. Use the current value, expected range, trend, rate of change, findings, and correlated evidence. Do not claim causality.`,
                    )
                  }
                >
                  Ask OpsAI about this behavior
                </button>
              </div>
              <TimeSeriesChart
                metric={selectedBehavior.metric}
                unit={selectedBehavior.unit}
                points={selectedBehavior.points}
                lower={selectedBehavior.lowerBound ?? undefined}
                upper={selectedBehavior.upperBound ?? undefined}
                range={data.historyRange}
                capability={metricCapability(selectedBehavior.metric)?.state}
                overlays={chartOverlays.filter(
                  (overlay) =>
                    overlay.kind !== 'anomaly' ||
                    overlay.label
                      .toLowerCase()
                      .includes(metricLabel(selectedBehavior.metric).split(' ')[0]!.toLowerCase()),
                )}
                overlaysEnabled={overlaysEnabled}
                onOverlaySelect={setSelectedOverlay}
              />
              <div className="behavior-context-grid">
                <div>
                  <span>Current</span>
                  <strong>
                    {formatMetricValue(selectedBehavior.currentValue, selectedBehavior.unit)}
                  </strong>
                </div>
                <div>
                  <span>Expected</span>
                  <strong>
                    {selectedBehavior.lowerBound == null
                      ? 'Learning'
                      : `${formatMetricValue(selectedBehavior.lowerBound, selectedBehavior.unit)}–${formatMetricValue(selectedBehavior.upperBound, selectedBehavior.unit)}`}
                  </strong>
                </div>
                <div>
                  <span>Samples</span>
                  <strong>{selectedBehavior.sampleCount}</strong>
                </div>
                <div>
                  <span>Trend</span>
                  <strong>{selectedBehavior.trend}</strong>
                </div>
              </div>
              {selectedSeasonalBaseline ? (
                <div className="inline-callout neutral">
                  <TrendingUp size={20} />
                  <div>
                    <strong>Comparable seasonal context</strong>
                    <p>
                      {String(selectedSeasonalBaseline.bucket)
                        .replace('dow:', 'Day ')
                        .replace(':hour:', ', hour ')}{' '}
                      · expected{' '}
                      {formatMetricValue(
                        Number(selectedSeasonalBaseline.lowerBound),
                        selectedSeasonalBaseline.unit,
                      )}
                      –
                      {formatMetricValue(
                        Number(selectedSeasonalBaseline.upperBound),
                        selectedSeasonalBaseline.unit,
                      )}{' '}
                      from {selectedSeasonalBaseline.sampleCount} comparable samples.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="supporting-copy">
                  Seasonal comparisons appear after at least 14 days of sufficiently confident
                  comparable history.
                </p>
              )}
            </section>
          )}
          <section className="behavior-findings-section">
            <header className="section-action-heading">
              <h3>Active findings</h3>
              {activeBehaviorFindings.length > 0 && (
                <button className="button secondary" onClick={() => setTab('Investigate')}>
                  Investigate
                </button>
              )}
            </header>
            {activeBehaviorFindings.length ? (
              <div className="behavior-finding-list">
                {activeBehaviorFindings.slice(0, 20).map((item: any) => {
                  const row = behaviorRows.find(
                    (candidate) => candidate.metric === canonicalMetricId(String(item.metric)),
                  );
                  const durationSeconds =
                    item.firstDetectedAt && item.lastDetectedAt
                      ? Math.max(
                          0,
                          (Date.parse(item.lastDetectedAt) - Date.parse(item.firstDetectedAt)) /
                            1000,
                        )
                      : null;
                  const expected =
                    item.expectedLower == null || item.expectedUpper == null
                      ? 'Expected range not reported'
                      : `expected ${formatMetricValue(Number(item.expectedLower), row?.unit)}–${formatMetricValue(Number(item.expectedUpper), row?.unit)}`;
                  return (
                    <div key={item.id}>
                      <HealthIndicator status={item.severity} />
                      <span>
                        <FindingSummary finding={item} entityName={name} />
                        <small>
                          {metricLabel(item.metric)} · first observed{' '}
                          {formatDate(item.firstDetectedAt)} · duration{' '}
                          {durationSeconds == null
                            ? 'not reported'
                            : humanDuration(durationSeconds)}{' '}
                          · {expected} · confidence {item.confidence ?? 'not reported'} · evidence{' '}
                          {humanCapabilityState(item.kind ?? 'expected-range deviation')}
                          {item.relatedIncidentId
                            ? ' · related incident available'
                            : ''}
                        </small>
                      </span>
                      <StatusBadge value={item.state} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                title="No active behavior findings"
                description="Current supported metrics are within learned expectations, or their baselines are still learning."
              />
            )}
          </section>
          <section className="behavior-findings-section">
            <h3>Recovered findings</h3>
            {recoveredBehaviorFindings.length ? (
              <div className="behavior-finding-list">
                {recoveredBehaviorFindings.slice(0, 20).map((item: any) => {
                  const recoveredAt = item.recoveredAt ?? item.lastDetectedAt;
                  const durationSeconds =
                    item.firstDetectedAt && recoveredAt
                      ? Math.max(
                          0,
                          (Date.parse(recoveredAt) - Date.parse(item.firstDetectedAt)) / 1000,
                        )
                      : null;
                  return (
                    <div key={item.id}>
                      <HealthIndicator status="healthy" />
                      <span>
                        <FindingSummary finding={item} entityName={name} />
                        <small>
                          {metricLabel(item.metric)} · first observed{' '}
                          {formatDate(item.firstDetectedAt)} · recovered {formatDate(recoveredAt)} ·
                          duration{' '}
                          {durationSeconds == null
                            ? 'not reported'
                            : humanDuration(durationSeconds)}{' '}
                          · evidence {humanCapabilityState(item.kind ?? 'expected-range deviation')}
                          {item.relatedIncidentId
                            ? ' · related incident available'
                            : ''}
                        </small>
                      </span>
                      <StatusBadge value="recovered" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="supporting-copy">
                No recovered behavior findings are recorded for this asset.
              </p>
            )}
          </section>
          <section className="behavior-correlation">
            <h3>Correlated operational evidence</h3>
            <p>
              Events, logs, and incidents near a finding are supporting context only; proximity does
              not establish causality.
            </p>
            <div className="behavior-context-grid">
              <div>
                <span>Events</span>
                <strong>{scopedEvents.length}</strong>
              </div>
              <div>
                <span>Logs</span>
                <strong>{scopedLogs.length}</strong>
              </div>
              <div>
                <span>Incidents</span>
                <strong>{scopedIncidents.length}</strong>
              </div>
              <div>
                <span>Recovered findings</span>
                <strong>{recoveredBehaviorFindings.length}</strong>
              </div>
            </div>
          </section>
          <AIOpsValuePanel
            data={{
              ...data,
              events: scopedEvents,
              incidents: scopedIncidents,
              behavior: { ...data.behavior, findings: scopedBehaviorFindings },
            }}
            role={role}
          />
        </div>
      )}
      {tab === 'Capacity' && (
        <PredictiveRiskPanel forecasts={data.forecasts} history={data.history} />
      )}
      {tab === 'Capacity' &&
        (data.forecasts.length ? (
          <DataTable label="Asset capacity forecasts">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Current</th>
                <th>Trend</th>
                <th>Threshold</th>
                <th>Quality</th>
              </tr>
            </thead>
            <tbody>
              {data.forecasts.map((item: any) => (
                <tr key={item.metric}>
                  <td>{metricLabel(item.metric)}</td>
                  <td>
                    {formatMetricValue(Number(item.currentValue), metricUnit(String(item.metric)))}
                  </td>
                  <td>
                    <TrendIndicator value={item.growthPerDay} />
                  </td>
                  <td>
                    {item.estimatedThresholdAt
                      ? formatDate(item.estimatedThresholdAt)
                      : 'Insufficient data'}
                  </td>
                  <td>
                    <StatusBadge value={item.quality} />
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : (
          <EmptyState
            title="Insufficient capacity data"
            description="A reliable forecast is not yet available for this asset."
          />
        ))}
      {tab === 'Investigate' && (
        <InvestigationWorkspace assetId={asset.id} window={evidenceWindow} onOpsAI={onOpsAI} />
      )}
      {tab === 'OpsAI' && (
        <div className="asset-insight-stack">
          <div className="insight-facts">
            <h3>Observed facts</h3>
            <p>
              {asset.health} health ({data.healthProvenance?.source ?? 'collector evidence'}) ·
              Network {humanCapabilityState(networkCapability?.state)} · Storage{' '}
              {humanCapabilityState(storageCapability?.state)} ·{' '}
              {scopedIncidents.filter((item: any) => item.status !== 'recovered').length} active
              incidents · {scopedBehaviorFindings.length} behavioral findings · {scopedLogs.length}{' '}
              related log events.
            </p>
          </div>
          <div className="insight-explanation">
            <h3>Likely explanation</h3>
            <p>
              Generated only after an operator asks OpsAI. Correlation is never labeled as proven
              root cause.
            </p>
          </div>
          <div className="insight-checks">
            <h3>Recommended next checks</h3>
            <p>Evidence-backed, read-only checks only. No remediation or arbitrary execution.</p>
          </div>
          <div className="inline-callout">
            <Bot size={20} />
            <div>
              <strong>Grounded asset investigation</strong>
              <p>
                OpsAI combines metrics, behavior, logs, events, incidents, topology, services, and
                capacity. It cannot change this asset.
              </p>
            </div>
            <button
              className="button primary"
              onClick={() =>
                onOpsAI(`Summarize the operational state and recent issues for ${name}.`)
              }
            >
              Ask OpsAI
            </button>
          </div>
        </div>
      )}
      {tab === 'Configuration' && (
        <div className="behavior-workspace">
          <div className="detail-summary">
            <div>
              <span>Collector / source</span>
              <strong>{customerFacingEntityName(asset.source, asset.source === 'demo')}</strong>
            </div>
            <div>
              <span>Site Edge model</span>
              <strong>Outbound-only configuration sync</strong>
            </div>
            <div>
              <span>Infrastructure credentials</span>
              <strong>Edge-local encrypted store</strong>
            </div>
            <div>
              <span>Cloud plaintext</span>
              <strong>Never accepted</strong>
            </div>
          </div>
          <DataTable label="Telemetry capabilities">
            <thead>
              <tr>
                <th>Capability</th>
                <th>Category</th>
                <th>State</th>
                <th>Last sample</th>
              </tr>
            </thead>
            <tbody>
              {(data.capabilities?.metrics ?? []).map((item: any) => (
                <tr key={item.id}>
                  <td>{metricLabel(item.id)}</td>
                  <td>{item.category ?? 'Telemetry'}</td>
                  <td>
                    <StatusBadge value={item.state} />
                  </td>
                  <td>
                    {item.lastObservedAt
                      ? formatDate(item.lastObservedAt)
                      : (item.guidance ?? 'Not reported')}
                  </td>
                </tr>
              ))}
              {data.capabilities?.guest && (
                <>
                  <tr>
                    <td>QEMU Guest Agent</td>
                    <td>Guest telemetry</td>
                    <td>
                      <StatusBadge
                        value={data.capabilities.guest.agent.state}
                        label={
                          data.capabilities.guest.agent.reportedState === 'RESPONDING'
                            ? 'Healthy'
                            : humanCapabilityState(data.capabilities.guest.agent.state)
                        }
                      />
                    </td>
                    <td>
                      {String(data.capabilities.guest.agent.reportedState).replaceAll('_', ' ')}
                    </td>
                  </tr>
                  <tr>
                    <td>Guest filesystems</td>
                    <td>Storage</td>
                    <td>
                      <StatusBadge
                        value={data.capabilities.guest.filesystem.state}
                        label={
                          data.capabilities.guest.filesystem.state === 'SUPPORTED_VIA_GUEST_AGENT'
                            ? 'Monitored'
                            : humanCapabilityState(data.capabilities.guest.filesystem.state)
                        }
                      />
                    </td>
                    <td>{Number(data.capabilities.guest.filesystem.count)} monitored</td>
                  </tr>
                  <tr>
                    <td>Guest OS</td>
                    <td>Identity</td>
                    <td>
                      <StatusBadge
                        value={data.capabilities.guest.os.state}
                        label={humanCapabilityState(data.capabilities.guest.os.state)}
                      />
                    </td>
                    <td>{String(metadata.guestOSName ?? 'Not reported')}</td>
                  </tr>
                  <tr>
                    <td>Guest network identity</td>
                    <td>Network</td>
                    <td>
                      <StatusBadge
                        value={data.capabilities.guest.network.state}
                        label={humanCapabilityState(data.capabilities.guest.network.state)}
                      />
                    </td>
                    <td>{String(metadata.guestPrimaryIP ?? 'Not reported')}</td>
                  </tr>
                </>
              )}
              {data.capabilities?.logs && (
                <tr>
                  <td>Logs</td>
                  <td>Application</td>
                  <td>
                    <StatusBadge value={data.capabilities.logs.state} />
                  </td>
                  <td>{data.capabilities.logs.guidance ?? 'Configured'}</td>
                </tr>
              )}
            </tbody>
          </DataTable>
          {String(role).startsWith('aot_') && (
            <details className="diagnostics-panel">
              <summary>Diagnostics</summary>
              <p>Raw source metadata is restricted to AOT operational roles.</p>
              <pre>{JSON.stringify(metadata, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
      {data.interfaces.length > 0 && tab === 'Overview' && (
        <DataTable label="Network interfaces">
          <thead>
            <tr>
              <th>Interface</th>
              <th>State</th>
              <th>Inbound</th>
              <th>Outbound</th>
              <th>Errors</th>
            </tr>
          </thead>
          <tbody>
            {data.interfaces.map((item: any) => (
              <tr key={item.ifIndex}>
                <td>{item.name}</td>
                <td>
                  <StatusBadge
                    value={item.operStatus === 'up' ? 'healthy' : item.operStatus}
                    label={item.operStatus}
                  />
                </td>
                <td>{item.inUtilization?.toFixed?.(1) ?? '—'}%</td>
                <td>{item.outUtilization?.toFixed?.(1) ?? '—'}%</td>
                <td>{Number(item.inErrors) + Number(item.outErrors)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </SectionCard>
  );
}

function ServicesView({
  initialServiceId,
  navigate,
  refreshVersion = 0,
}: {
  initialServiceId?: string;
  navigate: Navigate;
  refreshVersion?: number;
}) {
  const [items, setItems] = useState<any[] | null>(null);
  const [selected, setSelected] = useState<any>(null);
  const selectedServiceIdRef = useRef<string | undefined>(undefined);
  const [tab, setTab] = useState('Health');
  const [showCreate, setShowCreate] = useState(false);
  const [assets, setAssets] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [error, setError] = useState('');
  const initialQuery = new URLSearchParams(window.location.search);
  const [filters, setFilters] = useState({
    search: '',
    health: initialQuery.get('health') ?? '',
    criticality: initialQuery.get('criticality') ?? '',
    site: initialQuery.get('site') ?? '',
  });
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    siteId: '',
    criticality: 'medium',
    ownerTeam: '',
    assetIds: [] as string[],
  });
  const load = async () => {
    try {
      const [services, assetResult, siteResult] = await Promise.all([
        api<any>('/v1/services'),
        api<any>(`/v1/assets?includeDemo=${includeDemo()}&limit=100`),
        api<any>('/v1/sites'),
      ]);
      setItems(services.items);
      setAssets(assetResult.items);
      setSites(siteResult.items);
      setError('');
      if (initialServiceId)
        await openService(
          initialServiceId,
          false,
          isSameDetailSelection(selectedServiceIdRef.current, initialServiceId),
        );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Service intelligence is unavailable.');
      setItems((current) => current ?? []);
    }
  };
  useEffect(() => {
    void load();
  }, [refreshVersion]);
  useEffect(() => {
    if (selected || initialServiceId) return;
    const params = new URLSearchParams();
    if (filters.health) params.set('health', filters.health);
    if (filters.criticality) params.set('criticality', filters.criticality);
    if (filters.site) params.set('site', filters.site);
    const query = params.toString();
    window.history.replaceState(null, '', `/services${query ? `?${query}` : ''}`);
  }, [filters.health, filters.criticality, filters.site, selected, initialServiceId]);
  async function openService(id: string, updateRoute = true, preserveUI = false) {
    const [detail, health, incidents, slo, topology, history] = await Promise.all([
      api<any>(`/v1/services/${id}`),
      api<any>(`/v1/services/${id}/health`),
      api<any>(`/v1/services/${id}/incidents`),
      api<any>(`/v1/services/${id}/slo`).catch(() => ({ items: [] })),
      api<any>(`/v1/services/${id}/topology`).catch(() => ({ edges: [] })),
      api<any>(`/v1/services/${id}/history`).catch(() => ({ items: [] })),
    ]);
    selectedServiceIdRef.current = id;
    setSelected({
      ...detail,
      health,
      incidents: incidents.items,
      slo: slo.items,
      topology: topology.edges,
      history: history.items,
    });
    if (!preserveUI) setTab('Health');
    if (updateRoute) navigate('Services', { detailId: id });
  }
  async function createService(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    try {
      const result = await api<{ id: string }>('/v1/services', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name,
          slug: form.slug,
          description: form.description,
          siteId: form.siteId || null,
          criticality: form.criticality,
          ownerTeam: form.ownerTeam || null,
          enabled: true,
          components: form.assetIds.map((assetId) => ({
            assetId,
            importance: 'required',
            minimumHealthy: 1,
          })),
          dependencies: [],
        }),
      });
      setShowCreate(false);
      setForm({
        name: '',
        slug: '',
        description: '',
        siteId: '',
        criticality: 'medium',
        ownerTeam: '',
        assetIds: [],
      });
      await load();
      await openService(result.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Service could not be created.');
    }
  }
  async function recalculate() {
    if (!selected) return;
    await api(`/v1/services/${selected.service.id}/recalculate`, { method: 'POST' });
    await openService(selected.service.id, false);
  }
  const relationshipLabel = (key: string) => {
    const separator = key.indexOf(':');
    const kind = separator >= 0 ? key.slice(0, separator) : '';
    const id = separator >= 0 ? key.slice(separator + 1) : key;
    if (kind === 'asset')
      return assets.find((asset) => asset.id === id)?.displayName ?? 'Monitored asset';
    if (kind === 'service')
      return (items ?? []).find((service) => service.id === id)?.name ?? 'Business service';
    return 'Operational dependency';
  };
  const filteredServices = (items ?? []).filter(
    (service) =>
      (!filters.search ||
        `${service.name} ${service.description} ${service.ownerTeam ?? ''}`
          .toLowerCase()
          .includes(filters.search.toLowerCase())) &&
      matchesServiceHealth(service.healthState, filters.health) &&
      (!filters.criticality || service.criticality === filters.criticality) &&
      (!filters.site || service.siteId === filters.site),
  );
  if (error && !items) return <ErrorState message={error} onRetry={load} />;
  return (
    <>
      <PageHeader
        eyebrow="SERVICE INTELLIGENCE"
        title="Business services"
        description="Model customer-facing services, trace infrastructure impact, and measure operational objectives."
        actions={
          !selected ? (
            <button className="button" onClick={() => setShowCreate((value) => !value)}>
              {showCreate ? 'Cancel' : 'Add service'}
            </button>
          ) : undefined
        }
      />
      {error && (
        <div className="error-state" role="alert">
          <CircleAlert size={20} />
          {error}
        </div>
      )}
      {showCreate && !selected && (
        <SectionCard
          title="Service onboarding"
          description="Start with the required components, then refine dependencies and redundancy as the service evolves."
        >
          <form className="service-form" onSubmit={createService}>
            <label>
              <span>Service name</span>
              <input
                required
                minLength={2}
                value={form.name}
                onChange={(event) =>
                  setForm({
                    ...form,
                    name: event.target.value,
                    slug:
                      form.slug ||
                      event.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-|-$/g, ''),
                  })
                }
              />
            </label>
            <label>
              <span>Slug</span>
              <input
                required
                pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                value={form.slug}
                onChange={(event) => setForm({ ...form, slug: event.target.value })}
              />
            </label>
            <label>
              <span>Criticality</span>
              <select
                value={form.criticality}
                onChange={(event) => setForm({ ...form, criticality: event.target.value })}
              >
                <option>low</option>
                <option>medium</option>
                <option>high</option>
                <option>critical</option>
              </select>
            </label>
            <label>
              <span>Site scope</span>
              <select
                value={form.siteId}
                onChange={(event) => setForm({ ...form, siteId: event.target.value, assetIds: [] })}
              >
                <option value="">Tenant-wide</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {customerFacingSiteName(site)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Owner team</span>
              <input
                value={form.ownerTeam}
                onChange={(event) => setForm({ ...form, ownerTeam: event.target.value })}
              />
            </label>
            <label className="span-2">
              <span>Description</span>
              <textarea
                maxLength={1000}
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </label>
            <fieldset className="span-2">
              <legend>Required components</legend>
              <div className="component-picker">
                {assets
                  .filter((asset) => !form.siteId || asset.siteId === form.siteId)
                  .slice(0, 250)
                  .map((asset) => (
                    <label key={asset.id}>
                      <input
                        type="checkbox"
                        checked={form.assetIds.includes(asset.id)}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            assetIds: event.target.checked
                              ? [...form.assetIds, asset.id]
                              : form.assetIds.filter((id) => id !== asset.id),
                          })
                        }
                      />
                      <span>
                        {customerFacingEntityName(
                          asset.displayName ?? asset.name,
                          asset.source === 'demo',
                        )}
                        <small>
                          {asset.assetType} · {asset.health}
                        </small>
                      </span>
                    </label>
                  ))}
              </div>
            </fieldset>
            <div className="span-2 form-actions">
              <button className="button" type="submit">
                Create business service
              </button>
            </div>
          </form>
        </SectionCard>
      )}
      {!selected && !showCreate && (
        <>
          <FilterBar
            search={filters.search}
            onSearch={(search) => setFilters({ ...filters, search })}
            placeholder="Search business services"
          >
            <select
              aria-label="Service health"
              value={filters.health}
              onChange={(event) => setFilters({ ...filters, health: event.target.value })}
            >
              <option value="">All health</option>
              <option value="attention">Needs attention</option>
              <option>healthy</option>
              <option>degraded</option>
              <option>critical</option>
              <option>unknown</option>
              <option>maintenance</option>
              <option>disabled</option>
            </select>
            <select
              aria-label="Service criticality"
              value={filters.criticality}
              onChange={(event) => setFilters({ ...filters, criticality: event.target.value })}
            >
              <option value="">All criticality</option>
              <option>low</option>
              <option>medium</option>
              <option>high</option>
              <option>critical</option>
            </select>
            <select
              aria-label="Service site"
              value={filters.site}
              onChange={(event) => setFilters({ ...filters, site: event.target.value })}
            >
              <option value="">All sites</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {customerFacingSiteName(site)}
                </option>
              ))}
            </select>
          </FilterBar>
          <SectionCard>
            {!items ? (
              <LoadingState />
            ) : filteredServices.length ? (
              <DataTable label="Business services">
                <thead>
                  <tr>
                    <th>Health</th>
                    <th>Service</th>
                    <th>Criticality</th>
                    <th>Owner</th>
                    <th>Components</th>
                    <th>Incidents</th>
                    <th>Availability / SLO</th>
                    <th>Last change</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredServices.map((service) => (
                    <tr key={service.id}>
                      <td>
                        <StatusBadge value={service.healthState} />
                      </td>
                      <td>
                        <span className="asset-identity">
                          <AssetVisual
                            asset={{ assetType: 'service', model: service.name }}
                            size={28}
                            status={service.healthState}
                          />
                          <span>
                            <strong>{customerFacingEntityName(service.name)}</strong>
                            <small>{service.description || service.slug}</small>
                          </span>
                        </span>
                      </td>
                      <td>{service.criticality}</td>
                      <td>{service.ownerTeam ?? 'Unassigned'}</td>
                      <td>{service.componentCount}</td>
                      <td>{service.openIncidentCount}</td>
                      <td>
                        {service.availabilityPercent == null
                          ? '—'
                          : `${Number(service.availabilityPercent).toFixed(3)}%`}
                        <small>{service.sloStatus ?? 'No SLO result'}</small>
                      </td>
                      <td>
                        {service.healthCalculatedAt
                          ? relativeTime(service.healthCalculatedAt)
                          : 'Awaiting evaluation'}
                      </td>
                      <td>
                        <a
                          className="row-action"
                          href={portalPath('Services', service.id)}
                          onClick={(event) => {
                            if (
                              event.button !== 0 ||
                              event.metaKey ||
                              event.ctrlKey ||
                              event.shiftKey ||
                              event.altKey
                            )
                              return;
                            event.preventDefault();
                            void openService(service.id);
                          }}
                        >
                          Open <ChevronRight size={14} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            ) : (
              <EmptyState
                title={items.length ? 'No matching services' : 'No business services'}
                description={
                  items.length
                    ? 'No services match the current filters.'
                    : 'Infrastructure monitoring is active. Model a service when you are ready to add customer-impact intelligence.'
                }
                action={
                  <button className="button" onClick={() => setShowCreate(true)}>
                    Add first service
                  </button>
                }
              />
            )}
          </SectionCard>
        </>
      )}
      {selected && (
        <>
          <SectionCard
            className="detail-panel"
            title={customerFacingEntityName(selected.service.name)}
            description={selected.service.description || 'Business service model'}
            action={
              <div className="detail-actions">
                <StatusBadge value={selected.health.state} />
                <TopologyAction
                  onClick={() => navigate('Topology', { topologyServiceId: selected.service.id })}
                />
                <button className="button secondary" onClick={recalculate}>
                  Recalculate
                </button>
                <button
                  className="button secondary"
                  onClick={() => {
                    selectedServiceIdRef.current = undefined;
                    setSelected(null);
                    navigate('Services', { replace: true });
                  }}
                >
                  Close
                </button>
              </div>
            }
          >
            <div className="service-facts">
              <div>
                <span>Criticality</span>
                <strong>{selected.service.criticality}</strong>
              </div>
              <div>
                <span>Owner</span>
                <strong>
                  {selected.service.ownerTeam ?? selected.service.ownerName ?? 'Unassigned'}
                </strong>
              </div>
              <div>
                <span>Components</span>
                <strong>{selected.components.length}</strong>
              </div>
              <div>
                <span>Open incidents</span>
                <strong>
                  {selected.incidents.filter((item: any) => item.status === 'open').length}
                </strong>
              </div>
            </div>
            <Tabs
              label="Service detail"
              items={[
                'Health',
                'Components',
                'Dependencies',
                'Incidents',
                'SLO',
                'History',
                'OpsAI',
              ]}
              active={tab}
              onChange={setTab}
            />
            {tab === 'Health' && (
              <div className="service-detail-grid">
                <section>
                  <h3>Current service state</h3>
                  <HealthIndicator status={selected.health.state} />
                  <ul>
                    {selected.health.reasons.map((reason: string) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                  <small>
                    Algorithm v{selected.health.algorithmVersion} ·{' '}
                    {formatDate(selected.health.calculatedAt)}
                  </small>
                </section>
                <section>
                  <h3>Contributing assets</h3>
                  {selected.health.contributingAssetIds.length ? (
                    selected.health.contributingAssetIds.map((id: string) => (
                      <button
                        className="text-button"
                        key={id}
                        onClick={() => navigate('Assets', { detailId: id })}
                      >
                        {selected.components.find((item: any) => item.assetId === id)?.assetName ??
                          id}
                      </button>
                    ))
                  ) : (
                    <p>No impaired contributors.</p>
                  )}
                </section>
              </div>
            )}
            {tab === 'Components' && (
              <DataTable label="Service components">
                <thead>
                  <tr>
                    <th>Health</th>
                    <th>Asset</th>
                    <th>Type</th>
                    <th>Importance</th>
                    <th>Redundancy</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.components.map((item: any) => (
                    <tr key={item.id}>
                      <td>
                        <StatusBadge value={item.state} />
                      </td>
                      <td>
                        <span className="asset-identity">
                          <AssetVisual
                            asset={{ assetType: item.assetType, model: item.assetName }}
                            size={26}
                            status={item.state}
                          />
                          <button
                            className="text-button"
                            onClick={() => navigate('Assets', { detailId: item.assetId })}
                          >
                            {item.assetName}
                          </button>
                        </span>
                      </td>
                      <td>{item.assetType}</td>
                      <td>{item.importance}</td>
                      <td>{item.redundancyGroup ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
            {tab === 'Dependencies' &&
              (selected.topology.length ? (
                <div className="dependency-list">
                  {selected.topology.map((edge: any) => (
                    <div key={edge.id}>
                      <strong>{relationshipLabel(edge.source)}</strong>
                      <span>
                        <StatusBadge value={edge.relationshipType} />
                        {Number(edge.sharedByServices) > 1 && (
                          <small>Shared dependency · {edge.sharedByServices} services</small>
                        )}
                      </span>
                      <strong>{relationshipLabel(edge.target)}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No typed dependencies"
                  description="This service currently derives health directly from its assigned components."
                />
              ))}
            {tab === 'Incidents' && <CompactIncidentTable items={selected.incidents} />}
            {tab === 'SLO' &&
              (selected.slo.length ? (
                <div className="slo-grid">
                  {selected.slo.map((slo: any) => (
                    <article key={slo.id}>
                      <StatusBadge value={slo.status} />
                      <strong>
                        {slo.achievementPercent == null
                          ? 'Insufficient data'
                          : `${slo.achievementPercent.toFixed(3)}%`}
                      </strong>
                      <span>
                        {slo.targetPercent}% target · {slo.evaluationPeriod.replaceAll('_', ' ')}
                      </span>
                      <small>
                        {slo.remainingBudgetPercent.toFixed(1)}% error budget remaining ·{' '}
                        {slo.coveragePercent.toFixed(1)}% coverage
                      </small>
                      <small>
                        Burn{' '}
                        {slo.burnRate == null
                          ? 'insufficient data'
                          : `${Number(slo.burnRate).toFixed(2)}×`}{' '}
                        · {String(slo.burnState).replaceAll('_', ' ')}
                      </small>
                    </article>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No SLO configured"
                  description="SLO configuration is available on Business and Enterprise plans. Missing coverage is never reported as uptime."
                />
              ))}
            {tab === 'History' &&
              (selected.history.length ? (
                <DataTable label="Service health history">
                  <thead>
                    <tr>
                      <th>State</th>
                      <th>Started</th>
                      <th>Ended</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.history.map((item: any) => (
                      <tr key={item.id}>
                        <td>
                          <StatusBadge value={item.state} />
                        </td>
                        <td>{formatDate(item.startedAt)}</td>
                        <td>{item.endedAt ? formatDate(item.endedAt) : 'Current'}</td>
                        <td>{item.reasons?.join(' · ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              ) : (
                <EmptyState
                  title="No service transitions"
                  description="History records meaningful service state changes."
                />
              ))}
            {tab === 'OpsAI' && (
              <div className="service-opsai">
                <p>
                  Ask OpsAI to explain this service using its current health, dependencies,
                  incidents, SLO state, capacity, behavioral evidence, and approved knowledge.
                </p>
                <button
                  className="button"
                  onClick={() =>
                    navigate('OpsAI', {
                      question: `Why is ${selected.service.name} in ${selected.health.state} state, what is the business impact, and which safe checks should we perform?`,
                      context: 'service',
                    })
                  }
                >
                  Ask OpsAI about this service
                </button>
              </div>
            )}
          </SectionCard>
        </>
      )}
    </>
  );
}

function IncidentsView({
  onOpsAI,
  initialIncidentId,
  navigate,
  refreshVersion = 0,
}: {
  onOpsAI: (question: string, incidentId?: string) => void;
  initialIncidentId?: string;
  navigate: Navigate;
  refreshVersion?: number;
}) {
  const [items, setItems] = useState<any[] | null>(null);
  const [sites, setSites] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const initialQuery = new URLSearchParams(window.location.search);
  const [filters, setFilters] = useState({
    search: '',
    status: initialQuery.get('status') ?? '',
    severity: initialQuery.get('severity') ?? '',
    site: initialQuery.get('site') ?? '',
    asset: initialQuery.get('asset') ?? '',
    service: initialQuery.get('service') ?? '',
    range: initialQuery.get('range') ?? '',
  });
  const [error, setError] = useState('');
  const load = async () => {
    try {
      const [result, siteResult] = await Promise.all([
        api<any>('/v1/incidents'),
        api<any>('/v1/sites'),
      ]);
      setItems(result.items);
      setSites(siteResult.items);
      setError('');
      if (initialIncidentId) await openIncident(initialIncidentId, false, result.items);
    } catch (reason) {
      setItems((current) => current ?? []);
      setError(reason instanceof Error ? reason.message : 'Incident data unavailable.');
    }
  };
  useEffect(() => {
    void load();
  }, [refreshVersion]);
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.severity) params.set('severity', filters.severity);
    if (filters.site) params.set('site', filters.site);
    if (filters.asset) params.set('asset', filters.asset);
    if (filters.service) params.set('service', filters.service);
    if (filters.range) params.set('range', filters.range);
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, [
    filters.status,
    filters.severity,
    filters.site,
    filters.asset,
    filters.service,
    filters.range,
  ]);
  const filtered = (items ?? []).filter(
    (item: any) =>
      (!filters.search || item.title.toLowerCase().includes(filters.search.toLowerCase())) &&
      matchesIncidentFilter(item, filters) &&
      (!filters.range ||
        new Date(item.openedAt).getTime() >= Date.now() - Number(filters.range) * 86_400_000),
  );
  async function openIncident(id: string, updateRoute = true, sourceItems = items ?? []) {
    const detail = await api<any>(`/v1/incidents/${id}`);
    const primary =
      detail.relatedAssets.find((asset: any) => asset.relationship === 'primary') ??
      detail.relatedAssets[0];
    const [history, forecasts, evidence, changes, impact] = await Promise.all([
      primary
        ? api<any>(`/v1/assets/${primary.id}/history?range=24h&resolution=5m`).catch(() => ({
            items: [],
          }))
        : Promise.resolve({ items: [] }),
      primary
        ? api<any>('/v1/capacity/forecasts').catch(() => ({ items: [] }))
        : Promise.resolve({ items: [] }),
      api<any>(`/v1/incidents/${id}/evidence`).catch(() => ({ items: [] })),
      api<any>(`/v1/incidents/${id}/changes`).catch(() => ({ items: [] })),
      api<any>(`/v1/incidents/${id}/impact`).catch(() => ({ services: [], affectedAssetIds: [] })),
    ]);
    setSelected({
      ...detail,
      primary,
      history: history.items,
      forecasts: forecasts.items.filter((item: any) => item.assetId === primary?.id),
      behavioralEvidence: evidence.items,
      recentChanges: changes.items,
      businessImpact: impact.services,
      similar: similarIncidents(sourceItems, detail.incident),
    });
    if (updateRoute) navigate('Incidents', { detailId: id });
  }
  async function acknowledge() {
    const note = window.prompt('Acknowledgement note (does not resolve the incident):');
    if (!note || !selected) return;
    await api(`/v1/incidents/${selected.incident.id}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
    const incidentId = selected.incident.id;
    await load();
    await openIncident(incidentId, false, items ?? []);
  }
  function impactPathLabel(node: string, currentService: any) {
    const [kind, id] = node.split(':', 2);
    if (kind === 'asset') {
      return (
        selected.relatedAssets.find((asset: any) => asset.id === id)?.displayName ??
        'Related infrastructure'
      );
    }
    if (kind === 'service') {
      return (
        selected.businessImpact.find((service: any) => service.serviceId === id)?.name ??
        currentService.name ??
        'Business service'
      );
    }
    return 'Related dependency';
  }
  return (
    <>
      <PageHeader
        eyebrow="INCIDENT OPERATIONS"
        title="Incidents"
        description="Incident status, acknowledgement, related evidence, and recovery timelines."
      />
      {!selected && (
        <>
          <FilterBar
            search={filters.search}
            onSearch={(search) => setFilters({ ...filters, search })}
            placeholder="Search incident summary"
          >
            <select
              aria-label="Incident status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All states</option>
              <option>open</option>
              <option>acknowledged</option>
              <option>recovered</option>
            </select>
            <select
              aria-label="Incident severity"
              value={filters.severity}
              onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
            >
              <option value="">All severity</option>
              <option>critical</option>
              <option>warning</option>
            </select>
            <select
              aria-label="Incident site"
              value={filters.site}
              onChange={(e) => setFilters({ ...filters, site: e.target.value })}
            >
              <option value="">All sites</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {customerFacingSiteName(site)}
                </option>
              ))}
            </select>
            <select
              aria-label="Incident time range"
              value={filters.range}
              onChange={(e) => setFilters({ ...filters, range: e.target.value })}
            >
              <option value="">Any time</option>
              <option value="1">Last 24 hours</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
            </select>
            <SavedViewMenu
              storageKey="aot_my_views_incidents"
              filters={filters}
              onApply={(saved) => setFilters((current) => ({ ...current, ...saved }))}
            />
            {filters.asset && (
              <button
                className="filter-scope-chip"
                onClick={() => setFilters({ ...filters, asset: '' })}
                aria-label="Clear primary asset filter"
              >
                Asset:{' '}
                {(items ?? []).find((item) => item.primaryAssetId === filters.asset)
                  ?.primaryAssetName ?? 'Selected asset'}{' '}
                ×
              </button>
            )}
            {filters.service && (
              <button
                className="filter-scope-chip"
                onClick={() => setFilters({ ...filters, service: '' })}
                aria-label="Clear affected service filter"
              >
                Service:{' '}
                {(items ?? []).find((item) =>
                  String(item.affectedServiceIds ?? '')
                    .split(',')
                    .includes(filters.service),
                )?.affectedServices ?? 'Selected service'}{' '}
                ×
              </button>
            )}
          </FilterBar>
          <SectionCard>
            {error ? (
              <ErrorState message={error} onRetry={load} />
            ) : !items ? (
              <LoadingState />
            ) : filtered.length ? (
              <DataTable label="Incidents">
                <thead>
                  <tr>
                    <th>Severity</th>
                    <th>Summary</th>
                    <th>Affected service</th>
                    <th>Primary asset</th>
                    <th>Site</th>
                    <th>State</th>
                    <th>Acknowledgement</th>
                    <th>Detected</th>
                    <th>Duration</th>
                    <th>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item: any) => (
                    <tr key={item.id}>
                      <td>
                        <StatusBadge value={item.severity} />
                      </td>
                      <td>
                        <a
                          className="entity-link"
                          href={portalPath('Incidents', item.id)}
                          onClick={(event) => {
                            if (
                              event.button !== 0 ||
                              event.metaKey ||
                              event.ctrlKey ||
                              event.shiftKey ||
                              event.altKey
                            )
                              return;
                            event.preventDefault();
                            void openIncident(item.id);
                          }}
                        >
                          {customerFacingEntityName(item.title)}
                        </a>
                        <small>{item.type}</small>
                      </td>
                      <td>
                        {item.affectedServices ? item.affectedServices : '—'}
                        {item.businessImpactRank > 0 && (
                          <small>{item.businessImpactRank > 1 ? 'Impacted' : 'At risk'}</small>
                        )}
                      </td>
                      <td>
                        <span className="asset-identity">
                          <AssetVisual
                            asset={{ assetType: 'unknown', model: item.primaryAssetName }}
                            size={24}
                            status={item.severity}
                          />
                          <span>
                            {item.primaryAssetName ?? 'Not reported'}
                            {item.primaryAssetId && (
                              <button
                                className="text-button"
                                onClick={() =>
                                  navigate('Maps', { query: { asset: item.primaryAssetId } })
                                }
                              >
                                Show on map
                              </button>
                            )}
                          </span>
                        </span>
                      </td>
                      <td>
                        {customerFacingSiteName(
                          sites.find((site) => site.id === item.siteId) ?? { name: '—' },
                        )}
                      </td>
                      <td>
                        <StatusBadge value={item.status} />
                      </td>
                      <td>
                        {item.acknowledgedAt ? (
                          <>
                            <strong>Acknowledged</strong>
                            <small>
                              {item.acknowledgedByName ?? 'Authorized operator'} ·{' '}
                              {relativeTime(item.acknowledgedAt)}
                            </small>
                          </>
                        ) : item.status === 'recovered' ? (
                          'Recovered'
                        ) : (
                          'Unacknowledged'
                        )}
                      </td>
                      <td>{formatDate(item.openedAt)}</td>
                      <td>{duration(item.openedAt, item.recoveredAt ?? item.updatedAt)}</td>
                      <td>
                        <div className="row-actions">
                          <a
                            className="row-action"
                            href={portalPath('Incidents', item.id)}
                            onClick={(event) => {
                              if (
                                event.button !== 0 ||
                                event.metaKey ||
                                event.ctrlKey ||
                                event.shiftKey ||
                                event.altKey
                              )
                                return;
                              event.preventDefault();
                              void openIncident(item.id);
                            }}
                          >
                            Investigate <ChevronRight size={14} />
                          </a>
                          <ContextActionMenu label={`Actions for ${item.title}`}>
                            <button onClick={() => openIncident(item.id)}>Open workspace</button>
                            <TopologyAction
                              onClick={() =>
                                navigate('Topology', { topologyAssetId: item.primaryAssetId })
                              }
                            />
                            <button
                              onClick={() =>
                                navigate('Maps', { query: { asset: item.primaryAssetId } })
                              }
                            >
                              View on map
                            </button>
                            <button
                              onClick={() => onOpsAI(`Explain incident: ${item.title}`, item.id)}
                            >
                              Ask OpsAI
                            </button>
                            <MaintenanceAction onClick={() => navigate('Settings')} />
                          </ContextActionMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            ) : (
              <EmptyState
                title="No matching incidents"
                description="No incidents match the selected state and severity filters."
              />
            )}
          </SectionCard>
        </>
      )}
      {selected && (
        <SectionCard
          className="detail-panel incident-workspace-card"
          title={customerFacingEntityName(selected.incident.title)}
          description="NOC investigation workspace"
          action={
            <div className="detail-actions">
              <StatusBadge value={selected.incident.severity} />
              <StatusBadge value={selected.incident.status} />
              <ContextActionMenu label="Incident actions">
                {selected.primary && (
                  <button onClick={() => navigate('Assets', { detailId: selected.primary.id })}>
                    Open asset
                  </button>
                )}
                <TopologyAction
                  onClick={() => navigate('Topology', { topologyAssetId: selected.primary?.id })}
                />
                <button
                  onClick={() => navigate('Maps', { query: { asset: selected.primary?.id } })}
                >
                  View on map
                </button>
                <button
                  onClick={() =>
                    onOpsAI(`Explain incident: ${selected.incident.title}`, selected.incident.id)
                  }
                >
                  Ask OpsAI
                </button>
                <MaintenanceAction onClick={() => navigate('Settings')} />
              </ContextActionMenu>
              <button
                className="button secondary"
                onClick={() => {
                  setSelected(null);
                  navigate('Incidents', { replace: true });
                }}
              >
                Close
              </button>
            </div>
          }
        >
          <IncidentFactStrip
            incident={selected.incident}
            primary={selected.primary}
            siteName={customerFacingSiteName(
              sites.find((site) => site.id === selected.incident.site_id) ?? { name: '—' },
            )}
            durationLabel={duration(
              selected.incident.opened_at,
              selected.incident.recovered_at ?? selected.incident.updated_at,
            )}
          />
          <section className="business-impact-panel">
            <p className="eyebrow">BUSINESS IMPACT</p>
            <h3>Impacted services</h3>
            {selected.businessImpact.length ? (
              <div className="impact-service-list">
                {selected.businessImpact.map((service: any) => (
                  <button
                    key={service.serviceId}
                    onClick={() => navigate('Services', { detailId: service.serviceId })}
                  >
                    <StatusBadge value={selected.incident.severity} />
                    <span>
                      <strong>{service.name ?? service.serviceId}</strong>
                      <small>
                        {service.direct
                          ? 'Direct component impact'
                          : 'Transitive dependency impact'}{' '}
                        · {service.criticality ?? 'unclassified'} criticality
                      </small>
                      <small className="impact-path">
                        {service.path
                          ?.map((node: string) => impactPathLabel(node, service))
                          .join(' → ')}
                      </small>
                    </span>
                    <ChevronRight size={15} />
                  </button>
                ))}
              </div>
            ) : (
              <p>No business service is linked to the affected assets.</p>
            )}
          </section>
          <div className="incident-layout incident-workspace-layout">
            <div className="investigation-main">
              <section className="workspace-section">
                <p className="eyebrow">OBSERVED EVIDENCE</p>
                <h3>Incident timeline</h3>
                <ol className="timeline">
                  {selected.events.map((event: any) => (
                    <li key={event.id} className={`tone-${statusTone(event.eventType)}`}>
                      <i />
                      <div>
                        <strong>{event.eventType}</strong>
                        <p>{event.detail}</p>
                        <time>{formatDate(event.occurredAt)}</time>
                      </div>
                    </li>
                  ))}
                </ol>
                {selected.incident.status === 'open' && (
                  <button className="button secondary" onClick={acknowledge}>
                    Acknowledge incident
                  </button>
                )}
              </section>
              <section className="workspace-section deterministic-analysis">
                <p className="eyebrow">CORRELATED ANALYSIS</p>
                <h3>Correlation and maintenance</h3>
                <dl>
                  <div>
                    <dt>Correlation</dt>
                    <dd>
                      {selected.incident.correlation_key
                        ? 'Grouped by related operational signals'
                        : 'No related incident group reported'}
                    </dd>
                  </div>
                  <div>
                    <dt>Maintenance influence</dt>
                    <dd>
                      {selected.events.some((event: any) =>
                        String(event.eventType).includes('maintenance'),
                      )
                        ? 'Maintenance event present in the observed timeline'
                        : 'No maintenance event reported in this timeline'}
                    </dd>
                  </div>
                </dl>
              </section>
              <section className="workspace-section">
                <p className="eyebrow">BEHAVIORAL EVIDENCE</p>
                <h3>Expected behavior and deviations</h3>
                {selected.behavioralEvidence.length ? (
                  <div className="evidence-ranked-list">
                    {selected.behavioralEvidence.map((item: any) => (
                      <div key={`${item.evidenceType}-${item.evidenceId}`}>
                        <span className="evidence-rank">{Number(item.rankScore).toFixed(0)}</span>
                        <span>
                          <strong>{item.summary ?? 'Operational evidence'}</strong>
                          <small>
                            {item.evidenceType} · {formatDate(item.occurredAt)} · Related by time, asset and severity
                          </small>
                        </span>
                        <StatusBadge value={item.severity ?? 'info'} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title="No ranked behavioral evidence"
                    description="No anomaly or event evidence is linked to this incident."
                  />
                )}
              </section>
              <section className="workspace-section">
                <p className="eyebrow">RECENT CHANGES</p>
                <h3>Change and task context</h3>
                {selected.recentChanges.length ? (
                  <ol className="timeline">
                    {selected.recentChanges.map((item: any) => (
                      <li key={item.id} className={`tone-${statusTone(item.severity)}`}>
                        <i />
                        <div>
                          <strong>{item.category}</strong>
                          <p>{item.summary}</p>
                          <time>
                            {formatDate(item.occurredAt)} · {item.source}
                          </time>
                        </div>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <EmptyState
                    title="No nearby changes"
                    description="No configuration, task, or authentication event was reported near this incident."
                  />
                )}
              </section>
              <section className="workspace-section">
                <h3>Metrics and history</h3>
                {selected.history.length ? (
                  <div className="chart-grid">
                    {groupHistory(selected.history)
                      .slice(0, 2)
                      .map((group: any) => (
                        <HistoryChart key={group.key} metric={group.label} points={group.points} />
                      ))}
                  </div>
                ) : (
                  <EmptyState
                    title="No metric history"
                    description="No five-minute history was returned for the primary affected asset."
                  />
                )}
              </section>
              <section className="workspace-section similar-incidents">
                <h3>Previous similar incidents</h3>
                <p>Matched by incident type or related operational signals.</p>
                {selected.similar.length ? (
                  <div className="record-list">
                    {selected.similar.map((item: any) => (
                      <button key={item.id} onClick={() => openIncident(item.id)}>
                        <span>
                          <strong>{customerFacingEntityName(item.title)}</strong>
                          <small>{formatDate(item.openedAt)}</small>
                        </span>
                        <StatusBadge value={item.status} />
                      </button>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title="No similar incidents"
                    description="No prior incident shares the current type or related operational signals."
                  />
                )}
              </section>
            </div>
            <aside className="evidence-panel investigation-context">
              <p className="eyebrow">CONTEXT</p>
              <h3>Affected infrastructure</h3>
              {selected.relatedAssets.map((asset: any) => (
                <div className="evidence-row" key={asset.id}>
                  <span>
                    <strong>
                      {customerFacingEntityName(asset.displayName, asset.source === 'demo')}
                    </strong>
                    <small>
                      {asset.assetType} · {asset.relationship}
                    </small>
                  </span>
                  <StatusBadge value={asset.health} />
                </div>
              ))}
              <dl>
                <div>
                  <dt>Detected</dt>
                  <dd>{formatDate(selected.incident.opened_at)}</dd>
                </div>
                <div>
                  <dt>Last update</dt>
                  <dd>{formatDate(selected.incident.updated_at)}</dd>
                </div>
                <div>
                  <dt>Correlation</dt>
                  <dd>
                    {selected.incident.correlation_key
                      ? 'Correlated by platform signals'
                      : 'Single source'}
                  </dd>
                </div>
                <div>
                  <dt>Maintenance influence</dt>
                  <dd>Reflected in timeline when applicable</dd>
                </div>
              </dl>
              <div className="relationship-summary">
                <h3>Relationships</h3>
                <p>
                  {selected.relatedAssets.length > 1
                    ? `${selected.relatedAssets.length} assets are related by incident evidence.`
                    : 'A single primary asset is reported.'}
                </p>
                {selected.primary && (
                  <button
                    className="text-button"
                    onClick={() => navigate('Topology', { topologyAssetId: selected.primary.id })}
                  >
                    Open relationship topology <ChevronRight size={14} />
                  </button>
                )}
              </div>
              <div className="capacity-relevance">
                <h3>Capacity relevance</h3>
                {selected.forecasts.length ? (
                  selected.forecasts.slice(0, 3).map((item: any) => (
                    <div key={item.metric}>
                      <span>{metricLabel(item.metric)}</span>
                      <strong>{Number(item.currentValue).toFixed(1)}%</strong>
                      <StatusBadge value={item.state} />
                    </div>
                  ))
                ) : (
                  <p>No capacity forecast is linked to the primary asset.</p>
                )}
              </div>
              <div className="ai-distinction">
                <Bot size={18} />
                <span>
                  <strong>AI-generated explanation</strong>
                  <small>Requested on demand and separated from evidence and analysis.</small>
                </span>
              </div>
              <button
                className="button primary"
                onClick={() =>
                  onOpsAI(`Explain incident: ${selected.incident.title}`, selected.incident.id)
                }
              >
                Explain with OpsAI
              </button>
              <div className="recommended-checks-placeholder">
                <h3>OpsAI recommended checks</h3>
                <p>Generated only after the operator explicitly asks OpsAI.</p>
              </div>
            </aside>
          </div>
        </SectionCard>
      )}
    </>
  );
}

function CapacityView({
  navigate,
  refreshVersion = 0,
}: {
  navigate: Navigate;
  refreshVersion?: number;
}) {
  const [data, setData] = useState<any>(null);
  const initialQuery = new URLSearchParams(window.location.search);
  const [filters, setFilters] = useState({
    search: '',
    asset: initialQuery.get('asset') ?? '',
    site: '',
    platform: initialQuery.get('platform') ?? '',
    severity: initialQuery.get('severity') ?? '',
    horizon: initialQuery.get('horizon') ?? '',
  });
  const [attentionOnly, setAttentionOnly] = useState(initialQuery.get('attention') === 'true');
  const [error, setError] = useState('');
  const load = () =>
    Promise.all([
      api<any>('/v1/capacity/forecasts'),
      api<any>('/v1/capacity/findings'),
      api<any>(`/v1/assets?includeDemo=${includeDemo()}&limit=100`),
      api<any>('/v1/sites'),
    ])
      .then(([forecasts, findings, assets, sites]) => {
        setData({
          forecasts: forecasts.items,
          findings: findings.items,
          assets: assets.items,
          sites: sites.items,
          loadedAt: new Date().toISOString(),
        });
        setError('');
      })
      .catch((reason) => {
        setData(
          (current: any) => current ?? { forecasts: [], findings: [], assets: [], sites: [] },
        );
        setError(reason instanceof Error ? reason.message : 'Capacity data unavailable.');
      });
  useEffect(() => {
    void load();
  }, [refreshVersion]);
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.platform) params.set('platform', filters.platform);
    if (filters.asset) params.set('asset', filters.asset);
    if (filters.severity) params.set('severity', filters.severity);
    if (filters.horizon) params.set('horizon', filters.horizon);
    if (attentionOnly) params.set('attention', 'true');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, [filters.asset, filters.platform, filters.severity, filters.horizon, attentionOnly]);
  const assetSite = (assetId: string) =>
    data?.assets?.find((asset: any) => asset.id === assetId)?.siteId;
  const findings = (data?.findings ?? []).filter(
    (item: any) =>
      (!filters.search || item.assetName.toLowerCase().includes(filters.search.toLowerCase())) &&
      (!filters.asset || item.assetId === filters.asset) &&
      (!filters.site || assetSite(item.assetId) === filters.site) &&
      (!filters.platform || assetPlatformSource(item) === filters.platform) &&
      (!filters.severity || item.severity === filters.severity),
  );
  const severityAssetIds = new Set(
    (data?.findings ?? [])
      .filter((item: any) => !filters.severity || item.severity === filters.severity)
      .map((item: any) => item.assetId),
  );
  const forecasts = (data?.forecasts ?? []).filter(
    (item: any) =>
      (!filters.search || item.assetName.toLowerCase().includes(filters.search.toLowerCase())) &&
      (!filters.asset || item.assetId === filters.asset) &&
      (!filters.site || assetSite(item.assetId) === filters.site) &&
      (!filters.platform || assetPlatformSource(item) === filters.platform) &&
      (!filters.severity || severityAssetIds.has(item.assetId)) &&
      (!filters.horizon ||
        (item.estimatedThresholdAt &&
          (new Date(item.estimatedThresholdAt).getTime() - Date.now()) / 86_400_000 <=
            Number(filters.horizon))),
  );
  return (
    <>
      <PageHeader
        eyebrow="CAPACITY INTELLIGENCE"
        title="Capacity"
        description="Current utilization and evidence-based forecasts with clear confidence and data-availability states."
        actions={
          <div className="page-action-stack">
            <button
              className="button secondary"
              onClick={() =>
                navigate('OpsAI', {
                  question:
                    'Which capacity risks require attention and what evidence supports them?',
                  context: 'capacity',
                })
              }
            >
              Ask OpsAI about capacity
            </button>
            <FreshnessIndicator value={data?.loadedAt} onRefresh={load} />
          </div>
        }
      />
      <section className="scorecard compact">
        <MetricCard
          label="Active findings"
          value={data?.findings.length ?? '—'}
          status={
            data?.findings.some((item: any) => item.severity === 'critical')
              ? 'critical'
              : data?.findings.length
                ? 'warning'
                : 'healthy'
          }
        />
        <MetricCard label="Forecast series" value={data?.forecasts.length ?? '—'} />
        <MetricCard
          label="Threshold crossings"
          value={data?.forecasts.filter((item: any) => item.estimatedThresholdAt).length ?? '—'}
        />
        <MetricCard
          label="Insufficient data"
          value={data?.forecasts.filter((item: any) => !item.estimatedThresholdAt).length ?? '—'}
        />
      </section>
      <FilterBar
        search={filters.search}
        onSearch={(search) => setFilters({ ...filters, search })}
        placeholder="Search storage or asset"
      >
        <select
          aria-label="Capacity site"
          value={filters.site}
          onChange={(e) => setFilters({ ...filters, site: e.target.value })}
        >
          <option value="">All sites</option>
          {(data?.sites ?? []).map((site: any) => (
            <option key={site.id} value={site.id}>
              {customerFacingSiteName(site)}
            </option>
          ))}
        </select>
        <select
          aria-label="Platform"
          value={filters.platform}
          onChange={(e) => setFilters({ ...filters, platform: e.target.value })}
        >
          <option value="">All platforms</option>
          <option>proxmox</option>
          <option>vmware</option>
          <option>hyperv</option>
        </select>
        <select
          aria-label="Capacity severity"
          value={filters.severity}
          onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
        >
          <option value="">All severity</option>
          <option>critical</option>
          <option>warning</option>
          <option>informational</option>
        </select>
        <select
          aria-label="Forecast horizon"
          value={filters.horizon}
          onChange={(e) => setFilters({ ...filters, horizon: e.target.value })}
        >
          <option value="">Any horizon</option>
          <option value="30">Within 30 days</option>
          <option value="90">Within 90 days</option>
          <option value="180">Within 180 days</option>
        </select>
        <SavedViewMenu
          storageKey="aot_my_views_capacity"
          filters={filters}
          onApply={(saved) => setFilters((current) => ({ ...current, ...saved }))}
        />
        {attentionOnly && (
          <button
            className="filter-scope-chip"
            onClick={() => setAttentionOnly(false)}
            aria-label="Clear open capacity findings scope"
          >
            Open findings ×
          </button>
        )}
      </FilterBar>
      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : !data ? (
        <LoadingState />
      ) : (
        <div className="capacity-layout">
          <SectionCard
            title="Current risks"
            description="Open capacity findings based on observed utilization and configured thresholds."
          >
            {findings.length ? (
              <div className="attention-list">
                {findings.map((item: any) => (
                  <a
                    className="attention-static"
                    key={item.id}
                    href={portalPath('Assets', item.assetId, undefined, { tab: 'Capacity' })}
                    onClick={(event) => {
                      if (
                        event.button !== 0 ||
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey
                      )
                        return;
                      event.preventDefault();
                      navigate('Assets', { detailId: item.assetId, query: { tab: 'Capacity' } });
                    }}
                  >
                    <i className={`attention-marker tone-${statusTone(item.severity)}`} />
                    <span>
                      <strong>{customerFacingEntityName(item.title)}</strong>
                      <small>
                        {customerFacingEntityName(item.assetName)} · {metricLabel(item.metric)}
                      </small>
                    </span>
                    <StatusBadge value={item.severity} />
                  </a>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No capacity risks detected"
                description="No open capacity findings are reported."
              />
            )}
          </SectionCard>
          <SectionCard
            title="Forecast library"
            description="Forecasts include projected thresholds and confidence when sufficient history is available."
          >
            {forecasts.length ? (
              <DataTable label="Capacity forecasts">
                <thead>
                  <tr>
                    <th>Asset / storage</th>
                    <th>Platform</th>
                    <th>Current</th>
                    <th>Trend</th>
                    <th>Threshold</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {forecasts.map((item: any) => (
                    <tr key={`${item.assetId}-${item.metric}`}>
                      <td>
                        <a
                          className="entity-link"
                          href={portalPath('Assets', item.assetId, undefined, { tab: 'Capacity' })}
                          onClick={(event) => {
                            if (
                              event.button !== 0 ||
                              event.metaKey ||
                              event.ctrlKey ||
                              event.shiftKey ||
                              event.altKey
                            )
                              return;
                            event.preventDefault();
                            navigate('Assets', {
                              detailId: item.assetId,
                              query: { tab: 'Capacity' },
                            });
                          }}
                        >
                          {item.assetName}
                        </a>
                        <small>{metricLabel(item.metric)}</small>
                      </td>
                      <td>
                        <AssetPlatform item={item} />
                      </td>
                      <td>
                        {formatMetricValue(
                          Number(item.currentValue),
                          metricUnit(String(item.metric)),
                        )}
                      </td>
                      <td>
                        <TrendIndicator value={item.growthPerDay} />
                      </td>
                      <td>
                        {item.estimatedThresholdAt
                          ? formatDate(item.estimatedThresholdAt, false)
                          : 'INSUFFICIENT DATA'}
                      </td>
                      <td>
                        <StatusBadge
                          value={item.state === 'RISING' ? 'warning' : item.state}
                          label={item.state}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            ) : (
              <EmptyState
                title="Insufficient data"
                description="No forecast matches the selected filters, or more daily history is required."
              />
            )}
          </SectionCard>
        </div>
      )}
    </>
  );
}


function OpsAIView({
  initialQuestion,
  incidentId,
  context,
}: {
  initialQuestion: string;
  incidentId?: string;
  context: 'dashboard' | 'incident' | 'asset' | 'service' | 'capacity';
}) {
  const [question, setQuestion] = useState(initialQuestion || 'What needs attention right now?');
  const [answer, setAnswer] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (initialQuestion) setQuestion(initialQuestion);
  }, [initialQuestion]);
  async function ask(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      setAnswer(
        await api('/v1/copilot/query', {
          method: 'POST',
          body: JSON.stringify({ question, ...(incidentId ? { incidentId } : {}) }),
        }),
      );
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'OpsAI is unavailable.');
    } finally {
      setBusy(false);
    }
  }
  const suggestions = [
    'What needs attention right now?',
    'Which storage is approaching capacity?',
    'What happened in the last 24 hours?',
    'Which systems have recurring issues?',
  ];
  return (
    <>
      <PageHeader
        eyebrow="GROUNDED OPERATIONS INTELLIGENCE"
        title="OpsAI"
        description="Read-only explanations based on current workspace evidence and approved knowledge."
      />
      <div className="opsai-context-bar">
        <Bot size={16} aria-hidden="true" />
        <span>Analyzing</span>
        <strong>
          {context === 'incident'
            ? 'Current incident and related evidence'
            : context === 'asset'
              ? 'Selected asset operational context'
              : context === 'service'
                ? 'Selected business service and dependencies'
                : context === 'capacity'
                  ? 'Capacity forecast and supporting history'
                  : 'Tenant operations overview'}
        </strong>
        <StatusBadge value="informational" label="Read only" />
      </div>
      <div className="copilot-layout">
        <SectionCard
          className="ask-card"
          title="Ask OpsAI about your infrastructure"
          description="Answers use current operational context and approved knowledge."
        >
          <form onSubmit={ask}>
            <label htmlFor="opsai-question" className="sr-only">
              Operational question
            </label>
            <textarea
              id="opsai-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={5}
            />
            <button className="button primary" disabled={busy || question.trim().length < 3}>
              {busy ? 'Reviewing evidence…' : 'Ask OpsAI'}
            </button>
          </form>
          <div className="suggested-questions">
            {suggestions.map((item) => (
              <button key={item} onClick={() => setQuestion(item)}>
                {item}
              </button>
            ))}
          </div>
          <p className="safety-note">
            <ShieldCheck size={15} /> OpsAI cannot remediate systems or change configuration.
          </p>
        </SectionCard>
        <SectionCard className="answer-card">
          {error && <ErrorState message={error} />}
          {answer ? (
            <>
              <div className="answer-meta">
                <StatusBadge value={answer.severity} />
                <span>{Math.round(answer.confidence * 100)}% model-estimated confidence</span>
                <StatusBadge
                  value={answer.grounded ? 'healthy' : 'warning'}
                  label={answer.grounded ? 'Grounded' : 'Limited evidence'}
                />
              </div>
              <h2>{answer.summary}</h2>
              {answer.rca && (
                <div className="rca-panel">
                  <div className="answer-meta">
                    <strong>Evidence-backed RCA</strong>
                    <span>
                      {Math.round(answer.rca.confidence * 100)}% model-estimated causal confidence
                    </span>
                  </div>
                  <p>{answer.rca.conclusion}</p>
                  <dl className="rca-facts">
                    <div>
                      <dt>Impact</dt>
                      <dd>{answer.rca.impact}</dd>
                    </div>
                    <div>
                      <dt>Likely root cause</dt>
                      <dd>{answer.rca.likely_root_cause}</dd>
                    </div>
                  </dl>
                  {answer.rca.causal_chain.length > 0 && (
                    <ol>
                      {answer.rca.causal_chain.map((item: string) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ol>
                  )}
                  <div className="answer-sections">
                    <div>
                      <h3>Supporting evidence</h3>
                      <ul>
                        {answer.rca.supporting_evidence.map((item: any) => (
                          <li key={`${item.type}-${item.reference}`}>
                            <strong>{item.type}</strong> · {item.summary}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3>Missing or contradictory evidence</h3>
                      <ul>
                        {[...answer.rca.contradictory_evidence, ...answer.rca.missing_evidence].map(
                          (item: string) => (
                            <li key={item}>{item}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  </div>
                  <h3>Safe next checks</h3>
                  <ol>
                    {answer.rca.safe_next_checks.map((item: string) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ol>
                </div>
              )}
              <div className="answer-sections">
                <div>
                  <h3>Operational evidence supplied</h3>
                  <ul>
                    {answer.evidence.map((item: string) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3>Recommended checks</h3>
                  <ol>
                    {answer.recommended_actions.map((item: string) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ol>
                </div>
              </div>
              <h3>Knowledge sources</h3>
              <div className="chip-row">
                {answer.knowledge_sources.map((item: any) => (
                  <span key={item.id}>{item.title}</span>
                ))}
              </div>
            </>
          ) : (
            <EmptyState
              title="Ready to investigate"
              description="Ask a question to assemble current monitoring, incident, history, relationship, and knowledge evidence."
            />
          )}
        </SectionCard>
      </div>
    </>
  );
}

function KnowledgeView() {
  const [items, setItems] = useState<any[] | null>(null);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [error, setError] = useState('');
  const load = () =>
    api<any>('/v1/knowledge')
      .then((result) => {
        setItems(result.items);
        setError('');
      })
      .catch((reason) => {
        setItems([]);
        setError(reason instanceof Error ? reason.message : 'Knowledge library unavailable.');
      });
  useEffect(() => {
    void load();
  }, []);
  const filtered = (items ?? []).filter(
    (item) =>
      (!search || item.title.toLowerCase().includes(search.toLowerCase())) &&
      (!type || item.type === type),
  );
  return (
    <>
      <PageHeader
        eyebrow="OPERATIONAL KNOWLEDGE"
        title="Knowledge"
        description="Approved runbooks, procedures, known issues, and guidance available to operators and OpsAI."
      />
      <FilterBar search={search} onSearch={setSearch} placeholder="Search knowledge">
        <select aria-label="Knowledge type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option>
          <option>runbook</option>
          <option>sop</option>
          <option>known_issue</option>
          <option>resolution</option>
          <option>guidance</option>
        </select>
      </FilterBar>
      <SectionCard>
        {error ? (
          <ErrorState message={error} onRetry={load} />
        ) : !items ? (
          <LoadingState />
        ) : filtered.length ? (
          <DataTable label="Knowledge library">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Status</th>
                <th>Source</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.title}</strong>
                  </td>
                  <td>{item.type.replaceAll('_', ' ')}</td>
                  <td>
                    <StatusBadge value={item.status} />
                  </td>
                  <td>{item.source}</td>
                  <td>{formatDate(item.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : (
          <EmptyState
            title="No matching knowledge"
            description="No approved knowledge items match the selected search and type."
          />
        )}
      </SectionCard>
    </>
  );
}

function TopologyRoute({
  initialAssetId,
  initialServiceId,
  navigate,
}: {
  initialAssetId?: string;
  initialServiceId?: string;
  navigate: Navigate;
}) {
  const [data, setData] = useState<any>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>(initialAssetId);
  const [error, setError] = useState('');
  const load = async () => {
    try {
      const [assets, sites, service, relationships] = await Promise.all([
        api<any>(`/v1/assets?includeDemo=${includeDemo()}&limit=100`),
        api<any>('/v1/sites'),
        initialServiceId
          ? api<any>(`/v1/services/${initialServiceId}`).catch(() => null)
          : Promise.resolve(null),
        api<any>('/v1/topology/relationships').catch(() => ({ items: [] })),
      ]);
      let scopedAssets = assets.items;
      if (service?.components?.length) {
        const included = new Set<string>(service.components.map((item: any) => item.assetId));
        const byId = new Map<string, any>(assets.items.map((item: any) => [item.id, item]));
        for (const id of [...included]) {
          let current = byId.get(id);
          while (current?.parentAssetId && byId.has(current.parentAssetId)) {
            included.add(current.parentAssetId);
            current = byId.get(current.parentAssetId);
          }
        }
        scopedAssets = assets.items.filter((item: any) => included.has(item.id));
      }
      setData({
        assets: scopedAssets,
        sites: sites.items,
        serviceName: service?.service?.name,
        relationships: relationships.items,
        loadedAt: new Date().toISOString(),
      });
      setError('');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Topology data unavailable.');
    }
  };
  useEffect(() => {
    void load();
  }, [initialServiceId]);
  useEffect(() => {
    setSelectedAssetId(initialAssetId);
  }, [initialAssetId]);
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <LoadingState rows={8} />;
  return (
    <TopologyView
      assets={data.assets}
      sites={data.sites}
      selectedAssetId={selectedAssetId}
      serviceName={data.serviceName}
      relationships={data.relationships}
      onSelectAsset={(id) => {
        setSelectedAssetId(id);
        navigate('Topology', {
          topologyAssetId: id,
          topologyServiceId: initialServiceId,
          query: { mode: data.serviceName ? 'Service' : undefined },
          replace: true,
        });
      }}
      onOpenAsset={(id) => navigate('Assets', { detailId: id })}
      onNavigate={(view, question) => navigate(view as View, question ? { question } : undefined)}
    />
  );
}

function AotOperationsView({
  initialCustomerId,
  navigate,
  role,
}: {
  initialCustomerId?: string;
  navigate: Navigate;
  role: string;
}) {
  const [dashboard, setDashboard] = useState<any>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [creatingCustomer,setCreatingCustomer] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [tab, setTab] = useState('Overview');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(
    new URLSearchParams(window.location.search).get('status') ?? '',
  );
  const [error, setError] = useState('');
  const [loadedAt, setLoadedAt] = useState('');
  const load = async () => {
    try {
      const [summary, list] = await Promise.all([
        api<any>('/v1/aot/dashboard'),
        api<any>('/v1/aot/tenants'),
      ]);
      setDashboard(summary);
      setCustomers(list.items);
      setLoadedAt(new Date().toISOString());
      setError('');
      if (initialCustomerId) await openCustomer(initialCustomerId, false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'AOT operations data unavailable.');
    }
  };
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, [status]);
  async function openCustomer(id: string, updateRoute = true) {
    setSelected(await api<any>(`/v1/aot/tenants/${id}`));
    setTab('Overview');
    if (updateRoute) navigate('AOT Operations', { detailId: id });
  }
  const filtered = customers.filter(
    (item) =>
      (!search ||
        `${item.displayName} ${item.name}`.toLowerCase().includes(search.toLowerCase())) &&
      (!status || item.status === status),
  );
  const tenantCount =
    dashboard?.tenants.reduce((sum: number, item: any) => sum + Number(item.count), 0) ?? 0;
  const agentCount =
    dashboard?.agents.reduce((sum: number, item: any) => sum + Number(item.count), 0) ?? 0;
  const openIncidentCount =
    dashboard?.incidents.reduce((sum: number, item: any) => sum + Number(item.count), 0) ?? 0;
  return (
    <>
      <PageHeader
        eyebrow="AOT OPERATIONS"
        title="Managed customer operations"
        description="Authorized cross-tenant operational posture, onboarding attention, service health, and customer detail."
        actions={
          <div className="page-action-stack">
            {role === 'aot_admin' && <button className="button primary" onClick={() => setCreatingCustomer(true)}>Create Customer</button>}
            {role !== 'aot_viewer' && (
              <button className="button primary" onClick={() => navigate('Customer Onboarding')}>
                Onboard Customer
              </button>
            )}
            <HealthIndicator status="healthy" label="AOT scoped" />
            <FreshnessIndicator value={loadedAt} onRefresh={load} />
          </div>
        }
      />
      {error && <ErrorState message={error} onRetry={load} />}
      {creatingCustomer && <CreateCustomerForm onCancel={()=>setCreatingCustomer(false)} onCreated={id=>{setCreatingCustomer(false);void load();void openCustomer(id);setTab('Users & Access');}} />}
      {dashboard && (
        <section className="scorecard operations-scorecard">
          <MetricCard
            label="Tenants"
            value={tenantCount}
            detail={dashboard.tenants
              .map((item: any) => `${item.count} ${item.status}`)
              .join(' · ')}
            href={portalPath('AOT Operations')}
            onNavigate={() => navigate('AOT Operations')}
          />
          <MetricCard label="Sites" value={dashboard.sites} />
          <MetricCard
            label="Edge agents"
            value={agentCount}
            detail={`${dashboard.agents.filter((item: any) => item.status === 'online').reduce((sum: number, item: any) => sum + Number(item.count), 0)} online`}
          />
          <MetricCard label="Assets" value={dashboard.assets} />
          <MetricCard
            label="Open incidents"
            value={openIncidentCount}
            status={openIncidentCount ? 'critical' : 'healthy'}
          />
          <MetricCard
            label="Onboarding attention"
            value={customers.filter((item) => item.status === 'trial').length}
            status="warning"
            href={portalPath('AOT Operations', undefined, undefined, { status: 'trial' })}
            onNavigate={() => {
              setStatus('trial');
              navigate('AOT Operations', { query: { status: 'trial' } });
            }}
          />
          <MetricCard
            label="Notification failures"
            value={dashboard.notificationFailures}
            status={dashboard.notificationFailures ? 'critical' : 'healthy'}
          />
          <MetricCard label="Report failures" value="0" />
          <MetricCard label="AI usage" value={dashboard.aiQueries} detail="Current month" />
        </section>
      )}
      <FilterBar search={search} onSearch={setSearch} placeholder="Search customer">
        <select
          aria-label="Customer status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All states</option>
          <option>active</option>
          <option>trial</option>
          <option>suspended</option>
          <option>archived</option>
        </select>
        <SavedViewMenu
          storageKey="aot_my_views_customers"
          filters={{ search, status }}
          onApply={(saved) => {
            setStatus(saved.status ?? '');
            setSearch('');
          }}
        />
      </FilterBar>
      <SectionCard
        title="Customer portfolio"
        description="Customer accounts available to your authorized AOT operations role."
      >
        {filtered.length ? (
          <DataTable label="Customers">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Status</th>
                <th>Plan</th>
                <th>Sites</th>
                <th>Assets</th>
                <th>Health</th>
                <th>Onboarding</th>
                <th>Last activity</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id}>
                  <td>
                    <span className="asset-identity">
                      <AssetVisual
                        asset={{ assetType: 'site', model: item.displayName }}
                        size={28}
                        status={item.lastHeartbeatAt ? 'healthy' : 'stale'}
                      />
                      <strong>{item.displayName}</strong>
                    </span>
                    {item.demo ? (
                      <small>
                        <StatusBadge value="informational" label="DEMO" />
                      </small>
                    ) : null}
                  </td>
                  <td>
                    <StatusBadge value={item.status} />
                  </td>
                  <td>{item.plan ?? 'Unassigned'}</td>
                  <td>{item.siteCount}</td>
                  <td>{item.assetCount}</td>
                  <td>
                    <HealthIndicator
                      status={item.lastHeartbeatAt ? 'healthy' : 'stale'}
                      label={item.lastHeartbeatAt ? 'Reporting' : 'No heartbeat'}
                    />
                  </td>
                  <td>{item.status === 'trial' ? 'Needs review' : 'Operational'}</td>
                  <td>{relativeTime(item.lastHeartbeatAt)}</td>
                  <td>
                    <button className="row-action" onClick={() => openCustomer(item.id)}>
                      Open <ChevronRight size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : (
          <EmptyState
            title="No matching customers"
            description="No customer records match the selected filters."
          />
        )}
      </SectionCard>
      {selected && (
        <CustomerDetail
          data={selected}
          role={role}
          tab={tab}
          setTab={setTab}
          navigate={navigate}
          close={() => {
            setSelected(null);
            navigate('AOT Operations', { replace: true });
          }}
        />
      )}
    </>
  );
}

function CustomerDetail({ data, tab, setTab, close, navigate, role }: any) {
  const tabs = [
    'Overview',
    'Services',
    'Sites',
    'Users & Access',
    'Agents',
    'Assets',
    'Incidents',
    'Capacity',
    'Usage',
    'Reports',
    'Domains/Branding',
    'Audit',
  ];
  return (
    <SectionCard
      className="detail-panel"
      title={data.customer.display_name ?? data.customer.name}
      description={`${data.customer.plan ?? 'Unassigned'} plan · ${data.onboarding.status} onboarding`}
      action={
        <div className="detail-actions">
          <FreshnessIndicator
            value={data.agents[0]?.lastHeartbeatAt}
            state={data.agents[0]?.status}
          />
          <ContextActionMenu label="Customer actions">
            <button onClick={() => setTab('Sites')}>View sites</button>
            <button onClick={() => setTab('Incidents')}>View incidents</button>
            <button onClick={() => setTab('Reports')}>View reports</button>
            <MaintenanceAction onClick={() => navigate('Settings')} />
          </ContextActionMenu>
          <button className="button secondary" onClick={close}>
            Close
          </button>
        </div>
      }
    >
      <Tabs items={tabs} active={tab} onChange={setTab} label="Customer detail" />
      {tab === 'Users & Access' && <UsersAccess tenantId={data.customer.id} role={role} />}
      {tab === 'Overview' && (
        <>
          <HealthBreakdown
            health={
              data.incidents.some(
                (item: any) => item.status !== 'recovered' && item.severity === 'critical',
              )
                ? 'critical'
                : data.incidents.some((item: any) => item.status !== 'recovered')
                  ? 'warning'
                  : data.agents.some((item: any) => item.status !== 'online')
                    ? 'stale'
                    : 'healthy'
            }
            factors={deriveHealthFactors({
              incidents: data.incidents,
              agents: data.agents,
            })}
          />
          <section className="scorecard compact">
            <MetricCard label="Sites" value={data.sites.length} />
            <MetricCard label="Agents" value={data.agents.length} />
            <MetricCard
              label="Open incidents"
              value={data.incidents.filter((item: any) => item.status !== 'recovered').length}
            />
            <MetricCard label="Reports" value={data.reports.length} />
          </section>
          <div className="readiness-grid">
            {Object.entries(data.onboarding.checks).map(([key, value]) => (
              <div key={key} className={value ? 'complete' : ''}>
                <span>{value ? '✓' : '○'}</span>
                {key.replace(/([A-Z])/g, ' $1')}
              </div>
            ))}
          </div>
        </>
      )}
      {tab === 'Sites' && (
        <DataTable label="Customer sites">
          <thead>
            <tr>
              <th>Site</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.sites.map((item: any) => (
              <tr key={item.id}>
                <td>
                  <strong>{customerFacingSiteName(item)}</strong>
                </td>
                <td>
                  <StatusBadge value={item.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {tab === 'Services' && (
        <DataTable label="Customer business services">
          <thead>
            <tr>
              <th>Service</th>
              <th>Health</th>
              <th>Criticality</th>
              <th>Owner</th>
              <th>Components</th>
            </tr>
          </thead>
          <tbody>
            {data.services.map((item: any) => (
              <tr key={item.id}>
                <td>
                  <strong>{item.name}</strong>
                </td>
                <td>
                  <StatusBadge value={item.healthState} />
                </td>
                <td>{item.criticality}</td>
                <td>{item.ownerTeam ?? 'Unassigned'}</td>
                <td>{item.componentCount}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {tab === 'Users' && (
        <DataTable label="Customer users">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((item: any) => (
              <tr key={item.id}>
                <td>
                  <span className="asset-identity">
                    <AssetVisual asset={item} size={28} status={item.health} />
                    <strong>{item.displayName}</strong>
                  </span>
                  <small>{item.email}</small>
                </td>
                <td>{item.role.replaceAll('_', ' ')}</td>
                <td>
                  <StatusBadge value={item.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {tab === 'Agents' && (
        <DataTable label="Customer Edge agents">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Status</th>
              <th>Version</th>
              <th>Upgrade</th>
              <th>Heartbeat</th>
            </tr>
          </thead>
          <tbody>
            {data.agents.map((item: any) => (
              <tr key={item.id}>
                <td>
                  <strong>{customerFacingEdgeName(item)}</strong>
                </td>
                <td>
                  <StatusBadge value={item.status} />
                </td>
                <td>{item.version}</td>
                <td>
                  <StatusBadge value={item.upgradeState} />
                </td>
                <td>{relativeTime(item.lastHeartbeatAt)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {tab === 'Assets' && (
        <DataTable label="Customer assets">
          <thead>
            <tr>
              <th>Asset</th>
              <th>Type</th>
              <th>Site</th>
              <th>Health</th>
              <th>Status</th>
              <th>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {data.assets.map((item: any) => (
              <tr key={item.id}>
                <td>
                  <strong>
                    {customerFacingEntityName(item.displayName, item.source === 'demo')}
                  </strong>
                </td>
                <td>{item.assetType}</td>
                <td>
                  {item.siteDisplayName ??
                    customerFacingSiteName({ name: item.siteName ?? 'Unassigned' })}
                </td>
                <td>
                  <StatusBadge value={item.health} />
                </td>
                <td>{item.status}</td>
                <td>{relativeTime(item.lastSeenAt)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {tab === 'Incidents' && <CompactIncidentTable items={data.incidents} />}
      {tab === 'Capacity' &&
        (data.capacity.length ? (
          <DataTable label="Customer capacity findings">
            <thead>
              <tr>
                <th>Finding</th>
                <th>Asset</th>
                <th>Metric</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Last detected</th>
              </tr>
            </thead>
            <tbody>
              {data.capacity.map((item: any) => (
                <tr key={item.id}>
                  <td>
                    <strong>{customerFacingEntityName(item.title)}</strong>
                  </td>
                  <td>{customerFacingEntityName(item.assetName)}</td>
                  <td>{metricLabel(item.metricName)}</td>
                  <td>
                    <StatusBadge value={item.severity} />
                  </td>
                  <td>{item.status}</td>
                  <td>{relativeTime(item.lastDetectedAt)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : (
          <EmptyState
            title="No capacity findings"
            description="No open or historical capacity findings are reported for this customer."
          />
        ))}
      {tab === 'Usage' && (
        <div className="usage-grid">
          <UsageMeter label="Sites" data={data.usage.sites} />
          <UsageMeter label="Assets" data={data.usage.assets} />
          <UsageMeter label="OpsAI" data={data.usage.ai} />
          <MetricCard label="Reports generated" value={data.usage.reportsGenerated} />
          <MetricCard
            label="History stored"
            value={`${Math.round(data.usage.historyBytes / 1024)} KiB`}
          />
        </div>
      )}
      {tab === 'Reports' && (
        <DataTable label="Customer reports">
          <thead>
            <tr>
              <th>Type</th>
              <th>Status</th>
              <th>Generated</th>
            </tr>
          </thead>
          <tbody>
            {data.reports.map((item: any) => (
              <tr key={item.id}>
                <td>{item.reportType}</td>
                <td>
                  <StatusBadge value={item.status} />
                </td>
                <td>{formatDate(item.generatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {tab === 'Domains/Branding' && (
        <div className="customer-branding-grid">
          <SectionCard
            title="Branding"
            description="Customer-facing identity currently configured."
          >
            {data.branding ? (
              <dl className="detail-list">
                <div>
                  <dt>Display name</dt>
                  <dd>{data.branding.displayName}</dd>
                </div>
                <div>
                  <dt>Primary color</dt>
                  <dd>{data.branding.primaryColor}</dd>
                </div>
                <div>
                  <dt>Attribution</dt>
                  <dd>
                    {data.branding.showPoweredByAot
                      ? 'Powered by AOT shown'
                      : 'Hidden by entitlement'}
                  </dd>
                </div>
              </dl>
            ) : (
              <EmptyState
                title="Official AOT branding"
                description="No customer-specific branding profile is configured."
              />
            )}
          </SectionCard>
          <SectionCard title="Domains" description="Verified customer portal routing posture.">
            {data.domains.length ? (
              <div className="record-list">
                {data.domains.map((item: any) => (
                  <div key={item.hostname}>
                    <span>
                      <strong>{item.hostname}</strong>
                      <small>
                        {item.domainType.replaceAll('_', ' ')}
                        {item.isPrimary ? ' · Primary' : ''}
                      </small>
                    </span>
                    <StatusBadge value={item.status} />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No customer domain"
                description="No custom hostname is configured."
              />
            )}
          </SectionCard>
        </div>
      )}
      {tab === 'Audit' && (
        <DataTable label="Customer audit">
          <thead>
            <tr>
              <th>Action</th>
              <th>Outcome</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {data.audits.map((item: any, index: number) => (
              <tr key={`${item.action}-${item.createdAt}-${index}`}>
                <td>{item.action}</td>
                <td>
                  <StatusBadge value={item.outcome} />
                </td>
                <td>{formatDate(item.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </SectionCard>
  );
}

function NocWallboardView({
  isAotOperator,
  tenantName,
  onExit,
}: {
  isAotOperator: boolean;
  tenantName: string;
  onExit: () => void;
}) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('OVERVIEW');
  const [autoCycle, setAutoCycle] = useState(false);
  const [refreshMode, setRefreshMode] = useState<RefreshMode>('Auto');
  const [clock, setClock] = useState(new Date());
  const load = useCallback(async () => {
    try {
      if (isAotOperator) setData(await api<any>('/v1/aot/wallboard'));
      else {
        const [dashboard, sites, incidents, services, capacity, agents] = await Promise.all([
          api<any>('/v1/dashboard'),
          api<any>('/v1/sites'),
          api<any>('/v1/incidents'),
          api<any>('/v1/services'),
          api<any>('/v1/capacity/findings').catch(() => ({ items: [] })),
          api<any>('/v1/operations/agents'),
        ]);
        setData({
          generatedAt: new Date().toISOString(),
          platform: {
            customers: 1,
            sites: sites.items?.length ?? 0,
            assets: dashboard.assetCount ?? 0,
            edgeOnline: agents.items?.filter((item: any) => item.status === 'online').length ?? 0,
            edgeStale: agents.items?.filter((item: any) => item.status === 'stale').length ?? 0,
            edgeOffline:
              agents.items?.filter((item: any) => !['online', 'stale'].includes(item.status))
                .length ?? 0,
            criticalIncidents:
              incidents.items?.filter((item: any) => item.severity === 'critical').length ?? 0,
            warningIncidents:
              incidents.items?.filter((item: any) => item.severity === 'warning').length ?? 0,
          },
          incidents: incidents.items ?? [],
          criticalServices: (services.items ?? []).filter((item: any) =>
            ['critical', 'degraded', 'unknown'].includes(item.healthState),
          ),
          serviceHealth: [],
          errorBudgets: [],
          capacity: capacity.items ?? [],
          behavior: dashboard.findings ?? [],
          sites: sites.items ?? [],
          edges: agents.items ?? [],
          network: [],
          infrastructure: [],
          facilities: [],
        });
      }
      setError('');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Wallboard unavailable.');
      throw reason;
    }
  }, [isAotOperator]);
  const wallboardRefresh = useLiveRefresh('NOC Wallboard', load, { mode: refreshMode });
  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);
  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const modes = [
    'OVERVIEW',
    'NETWORK',
    'INFRASTRUCTURE',
    'SERVICES',
    ...((data?.facilities?.length ?? 0) ? ['FACILITIES'] : []),
  ];
  useEffect(() => {
    if (!autoCycle) return;
    const timer = window.setInterval(() => {
      setMode((current) => modes[(modes.indexOf(current) + 1) % modes.length] || 'OVERVIEW');
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [autoCycle, modes.join('|')]);
  const incidents = (data?.incidents ?? []).slice(0, 5);
  const services = (data?.criticalServices ?? []).slice(0, 5);
  const sites = (data?.sites ?? [])
    .filter((item: any) => !['healthy', 'active'].includes(item.health ?? item.status))
    .slice(0, 5);
  const capacity = (data?.capacity ?? []).slice(0, 5);
  const anomalies = (data?.behavior ?? []).slice(0, 5);
  const edges = (data?.edges ?? []).slice(0, 6);
  const critical = Number(data?.platform?.criticalIncidents ?? 0);
  const degradedSites = sites.length;
  const updatedSeconds = data?.generatedAt
    ? Math.max(0, Math.floor((clock.getTime() - new Date(data.generatedAt).getTime()) / 1_000))
    : null;
  const list = (
    items: any[],
    kind: 'incident' | 'service' | 'capacity' | 'anomaly' | 'site' | 'edge',
  ) =>
    items.length ? (
      <div className="noc-list">
        {items.map((item: any, index: number) => (
          <article key={item.id ?? `${kind}-${index}`}>
            <StatusBadge
              value={item.severity ?? item.state ?? item.health ?? item.status ?? 'unknown'}
            />
            <span>
              <strong>
                {kind === 'edge'
                  ? customerFacingEdgeName(item)
                  : kind === 'site'
                    ? customerFacingSiteName({
                        name: item.siteName ?? item.name,
                        displayName: item.siteDisplayName ?? item.displayName,
                      })
                    : customerFacingEntityName(
                        item.title ?? item.name ?? item.summary ?? item.assetName,
                        Boolean(item.demo) || item.source === 'demo',
                      )}
              </strong>
              <small>
                {[
                  item.tenantName,
                  item.siteDisplayName ?? customerFacingSiteName({ name: item.siteName }),
                  item.serviceName,
                  item.assetName,
                ]
                  .filter(Boolean)
                  .map((value) => customerFacingEntityName(value))
                  .join(' · ') ||
                  (kind === 'edge'
                    ? `Version ${item.version || 'unknown'} · ${relativeTime(item.lastHeartbeatAt)}`
                    : 'Current scope')}
              </small>
            </span>
            <time>
              {relativeTime(
                item.openedAt ?? item.lastDetectedAt ?? item.calculatedAt ?? item.lastHeartbeatAt,
              )}
            </time>
          </article>
        ))}
      </div>
    ) : (
      <p className="noc-clear-state">
        <ShieldCheck size={22} /> No current exception requires wallboard attention.
      </p>
    );
  return (
    <div className="noc-screen" data-mode={mode.toLowerCase()}>
      <header className="noc-header">
        <div className="noc-brand">
          <AotProductLockup compact />
          <span>NOC</span>
          <i>LIVE</i>
        </div>
        <div className="noc-scope">
          <strong>{tenantName}</strong>
          <small>{isAotOperator ? 'Cross-customer operations' : 'Tenant operations'}</small>
        </div>
        <div className="noc-clock">
          <strong>
            {clock.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </strong>
          <small>
            {clock.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
          </small>
        </div>
        <div className="noc-actions">
          <span className={wallboardRefresh.delayed || (updatedSeconds ?? 999) > 90 ? 'stale' : ''}>
            {wallboardRefresh.delayed ? 'Data refresh delayed · ' : ''}Updated{' '}
            {updatedSeconds == null ? '—' : `${updatedSeconds}s ago`}
          </span>
          <label className="noc-refresh-control">
            Refresh
            <select
              value={refreshMode}
              onChange={(event) => setRefreshMode(event.target.value as RefreshMode)}
            >
              {(['Auto', '15s', '30s', '1m', '5m', 'Paused'] as RefreshMode[]).map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <button onClick={() => document.documentElement.requestFullscreen?.()}>
            <Maximize2 size={18} /> Full Screen
          </button>
          <button onClick={onExit}>Exit NOC</button>
        </div>
      </header>
      <nav className="noc-modes" aria-label="NOC mode">
        {modes.map((item) => (
          <button
            key={item}
            className={mode === item ? 'active' : ''}
            onClick={() => setMode(item)}
          >
            {item}
          </button>
        ))}
        <label>
          <input
            type="checkbox"
            checked={autoCycle}
            onChange={(event) => setAutoCycle(event.target.checked)}
          />{' '}
          Auto cycle · 60s
        </label>
      </nav>
      {error && !data && <ErrorState message={error} onRetry={load} />}
      {!data ? (
        !error && <LoadingState rows={8} />
      ) : (
        <main className="noc-content">
          <section className="noc-kpis">
            <article className={`tone-${critical ? 'critical' : 'healthy'}`}>
              <span>Overall Health</span>
              <strong>{critical ? 'CRITICAL' : 'STABLE'}</strong>
              <small>{data.platform?.assets ?? 0} monitored assets</small>
            </article>
            <article>
              <span>Open Critical</span>
              <strong>{critical}</strong>
              <small>{data.platform?.warningIncidents ?? 0} warning</small>
            </article>
            <article>
              <span>Open Incidents</span>
              <strong>{data.incidents?.length ?? 0}</strong>
              <small>Highest priority shown</small>
            </article>
            <article>
              <span>Services At Risk</span>
              <strong>{data.criticalServices?.length ?? 0}</strong>
              <small>{data.errorBudgets?.length ?? 0} SLO risks</small>
            </article>
            <article>
              <span>Sites Degraded</span>
              <strong>{degradedSites}</strong>
              <small>{data.platform?.sites ?? 0} total sites</small>
            </article>
            <article>
              <span>Edges Online</span>
              <strong>{data.platform?.edgeOnline ?? 0}</strong>
              <small>
                {data.platform?.edgeStale ?? 0} stale · {data.platform?.edgeOffline ?? 0} offline
              </small>
            </article>
          </section>
          <section className="noc-panels">
            {(mode === 'OVERVIEW' || mode === 'SERVICES') && (
              <article className="noc-panel critical-panel">
                <header>
                  <h2>Critical Incidents</h2>
                  <span>{incidents.length}</span>
                </header>
                {list(incidents, 'incident')}
              </article>
            )}
            {(mode === 'OVERVIEW' || mode === 'SERVICES') && (
              <article className="noc-panel">
                <header>
                  <h2>Business Service Health</h2>
                  <span>{services.length} at risk</span>
                </header>
                {list(services, 'service')}
              </article>
            )}
            {mode === 'OVERVIEW' && (
              <article className="noc-panel">
                <header>
                  <h2>Site Health</h2>
                  <span>{degradedSites} degraded</span>
                </header>
                {list(sites, 'site')}
              </article>
            )}
            {(mode === 'OVERVIEW' || mode === 'NETWORK') && (
              <article className="noc-panel">
                <header>
                  <h2>Top Bandwidth & Link Failures</h2>
                  <span>NETWORK</span>
                </header>
                {list((data.network ?? []).slice(0, 5), 'anomaly')}
              </article>
            )}
            {(mode === 'OVERVIEW' || mode === 'INFRASTRUCTURE') && (
              <article className="noc-panel">
                <header>
                  <h2>Predicted Risks</h2>
                  <span>{capacity.length}</span>
                </header>
                {list(capacity, 'capacity')}
              </article>
            )}
            {(mode === 'OVERVIEW' || mode === 'INFRASTRUCTURE') && (
              <article className="noc-panel">
                <header>
                  <h2>Edge Health</h2>
                  <span>{data.platform?.edgeOnline ?? 0} online</span>
                </header>
                {list(edges, 'edge')}
              </article>
            )}
            {mode === 'NETWORK' && (
              <article className="noc-panel">
                <header>
                  <h2>Network Incidents</h2>
                  <span>LIVE</span>
                </header>
                {list(
                  incidents.filter((item: any) =>
                    /network|link|wan|packet|interface/i.test(`${item.title} ${item.type}`),
                  ),
                  'incident',
                )}
              </article>
            )}
            {mode === 'NETWORK' && (
              <article className="noc-panel">
                <header>
                  <h2>Packet Errors</h2>
                  <span>SUMMARY</span>
                </header>
                {list((data.networkErrors ?? []).slice(0, 5), 'anomaly')}
              </article>
            )}
            {mode === 'INFRASTRUCTURE' && (
              <article className="noc-panel">
                <header>
                  <h2>CPU · Memory · Storage</h2>
                  <span>HOTSPOTS</span>
                </header>
                {list((data.infrastructure ?? []).slice(0, 5), 'anomaly')}
              </article>
            )}
            {mode === 'INFRASTRUCTURE' && (
              <article className="noc-panel">
                <header>
                  <h2>Active Anomalies</h2>
                  <span>{anomalies.length}</span>
                </header>
                {list(anomalies, 'anomaly')}
              </article>
            )}
            {mode === 'SERVICES' && (
              <article className="noc-panel">
                <header>
                  <h2>SLO & Error Budget Risk</h2>
                  <span>{data.errorBudgets?.length ?? 0}</span>
                </header>
                {list((data.errorBudgets ?? []).slice(0, 5), 'service')}
              </article>
            )}
            {mode === 'FACILITIES' &&
              (data.facilities ?? []).map((group: any) => (
                <article className="noc-panel" key={group.kind ?? group.id}>
                  <header>
                    <h2>{group.title ?? 'Facility / Environmental Warnings'}</h2>
                    <span>FACILITIES</span>
                  </header>
                  {list(group.items ?? [group], 'anomaly')}
                </article>
              ))}
          </section>
        </main>
      )}
    </div>
  );
}

function RegionalTimeSettings({
  session,
  onSessionRefresh,
}: {
  session: Session;
  onSessionRefresh: () => Promise<void>;
}) {
  const [regional, setRegional] = useState<any>(null);
  const [message, setMessage] = useState('');
  const canManageWorkspace = ['tenant_owner', 'tenant_admin', 'aot_admin'].includes(session.user.role);
  useEffect(() => {
    void api<any>('/v1/settings/regional-time')
      .then(setRegional)
      .catch((reason) =>
        setMessage(reason instanceof Error ? reason.message : 'Regional settings unavailable.'),
      );
  }, []);
  if (!regional) return message ? <ErrorState message={message} /> : <LoadingState rows={3} />;
  const zones = [
    'Asia/Manila',
    'Asia/Tokyo',
    'America/New_York',
    'America/Los_Angeles',
    'Europe/London',
    'UTC',
  ];
  async function saveUser(event: React.FormEvent) {
    event.preventDefault();
    await api('/v1/settings/regional-time/user', {
      method: 'PUT',
      body: JSON.stringify({
        timezoneMode: regional.timezoneMode,
        timezone: regional.timezoneMode === 'SPECIFIC' ? regional.timeZone : null,
      }),
    });
    await onSessionRefresh();
    setMessage('Your timezone preference was saved.');
  }
  async function saveWorkspace() {
    await api('/v1/settings/regional-time/workspace', {
      method: 'PUT',
      body: JSON.stringify({ timeZone: regional.workspaceTimeZone }),
    });
    await onSessionRefresh();
    setMessage('Workspace operational timezone was saved.');
  }
  return (
    <div className="regional-time-settings">
      {message && (
        <p className="toast" role="status">
          {message}
        </p>
      )}
      <form className="settings-form" onSubmit={saveUser}>
        <div>
          <p className="eyebrow">DISPLAY TIME</p>
          <h2>Regional &amp; Time</h2>
          <p>
            Operational timestamps are stored in UTC and displayed in your selected IANA timezone.
          </p>
        </div>
        <label>
          Your timezone preference
          <select
            value={regional.timezoneMode}
            onChange={(event) => setRegional({ ...regional, timezoneMode: event.target.value })}
          >
            <option value="AUTO_BROWSER">Auto — Browser</option>
            <option value="WORKSPACE_DEFAULT">Workspace default</option>
            <option value="SPECIFIC">Specific timezone</option>
          </select>
        </label>
        {regional.timezoneMode === 'SPECIFIC' && (
          <label>
            Specific timezone
            <select
              value={regional.timeZone ?? 'UTC'}
              onChange={(event) => setRegional({ ...regional, timeZone: event.target.value })}
            >
              {zones.map((zone) => (
                <option key={zone}>{zone}</option>
              ))}
            </select>
          </label>
        )}
        <p className="theme-storage-note">
          Resolved timezone:{' '}
          <strong>
            {
              createTimezoneState({
                timezoneMode: regional.timezoneMode,
                timeZone: regional.timeZone,
                workspaceTimeZone: regional.workspaceTimeZone,
              }).resolvedTimeZone
            }
          </strong>
        </p>
        <button className="button primary">Save my preference</button>
      </form>
      <div className="settings-form workspace-timezone-card">
        <div>
          <p className="eyebrow">WORKSPACE SCHEDULES</p>
          <h3>Workspace operational timezone</h3>
          <p>
            Used for workspace schedules and inherited by Sites unless a Site override is
            configured.
          </p>
        </div>
        <label>
          Workspace timezone
          <select
            disabled={!canManageWorkspace}
            value={regional.workspaceTimeZone}
            onChange={(event) =>
              setRegional({ ...regional, workspaceTimeZone: event.target.value })
            }
          >
            {zones.map((zone) => (
              <option key={zone}>{zone}</option>
            ))}
          </select>
        </label>
        {canManageWorkspace && (
          <button type="button" className="button secondary" onClick={() => void saveWorkspace()}>
            Save workspace timezone
          </button>
        )}
      </div>
    </div>
  );
}

function SettingsView({
  session,
  refreshVersion = 0,
  themePreference,
  resolvedTheme,
  onThemePreferenceChange,
  onSignedOut,
  onSessionRefresh,
  onBrandingChanged,
}: {
  session: Session;
  refreshVersion?: number;
  themePreference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  onThemePreferenceChange: (preference: ThemePreference) => void;
  onSignedOut: () => void;
  onSessionRefresh: () => Promise<void>;
  onBrandingChanged: () => void;
}) {
  const [settings, setSettings] = useState<any>(null);
  const [agents, setAgents] = useState<any[] | null>(null);
  const requestedSection = new URLSearchParams(window.location.search).get('section');
  const [tab, setTab] = useState(
    session.security?.mfaEnrollmentRequired ? 'Security' : requestedSection || 'Organization',
  );
  const [message, setMessage] = useState('');
  useEffect(() => {
    api('/v1/notifications/settings')
      .then(setSettings)
      .catch(() => setMessage('Notification settings are unavailable.'));
    api<any>('/v1/operations/agents')
      .then((result) => setAgents(result.items))
      .catch(() => setAgents([]));
  }, []);
  async function save(event: React.FormEvent) {
    event.preventDefault();
    await api('/v1/notifications/settings', { method: 'PUT', body: JSON.stringify(settings) });
    setMessage('Notification policy saved.');
  }
  async function sendNotificationTest() {
    await api('/v1/notifications/test', { method: 'POST' });
    setMessage('Branded notification test accepted by the configured provider.');
  }
  const sections = [
    'Organization',
    ...(['tenant_owner','tenant_admin','aot_admin'].includes(session.user.role) ? ['Users & Access'] : []),
    'Regional & Time',
    'Security',
    'Appearance',
    'Branding',
    'Domains',
    'Reports Branding',
    'Preview',
    'Notifications',
    'Edge Agents',
  ];
  useEffect(() => {
    if (!sections.includes(tab)) setTab('Organization');
    const params = new URLSearchParams();
    if (tab !== 'Organization') params.set('section', tab);
    window.history.replaceState(null, '', `/settings${params.size ? `?${params}` : ''}`);
  }, [tab]);
  return (
    <>
      <PageHeader
        eyebrow="WORKSPACE ADMINISTRATION"
        title="Settings"
        description="Organization, notifications, monitoring policy, maintenance, plan usage, and Edge administration."
      />
      {message && (
        <p className="toast" role="status">
          {message}
        </p>
      )}
      <SectionCard className="settings-shell">
        <Tabs items={sections} active={tab} onChange={setTab} label="Settings sections" />
        {tab === 'Organization' && (
          <div className="detail-summary">
            <div>
              <span>Workspace</span>
              <strong>
                {session.user.role.startsWith('aot_') ? 'AOT Operations' : 'Customer workspace'}
              </strong>
            </div>
            <div>
              <span>Signed in as</span>
              <strong>{session.user.email}</strong>
            </div>
            <div>
              <span>Role</span>
              <strong>{roleLabel(session.user.role)}</strong>
            </div>
            <div>
              <span>OpsAI autonomy</span>
              <strong>Read-only recommendations</strong>
            </div>
          </div>
        )}
        {tab === 'Regional & Time' && (
          <RegionalTimeSettings session={session} onSessionRefresh={onSessionRefresh} />
        )}
        {tab === 'Users & Access' && <UsersAccess key={session.tenant.id} tenantId={session.tenant.id} role={session.user.role} actorUserId={session.user.id} refreshVersion={refreshVersion} />}
        {tab === 'Appearance' && (
          <div className="appearance-settings">
            <div>
              <p className="eyebrow">AOT OPSAI THEME</p>
              <h2>Appearance</h2>
              <p>Use your operating-system preference or keep this browser in a specific theme.</p>
            </div>
            <fieldset className="theme-choice-group">
              <legend>Theme preference</legend>
              {(['system', 'light', 'dark'] as ThemePreference[]).map((preference) => (
                <label key={preference}>
                  <input
                    type="radio"
                    name="theme-preference"
                    value={preference}
                    checked={themePreference === preference}
                    onChange={() => onThemePreferenceChange(preference)}
                  />
                  <span>
                    <strong>{preference.charAt(0).toUpperCase() + preference.slice(1)}</strong>
                    <small>
                      {preference === 'system'
                        ? `Follow this device (currently ${resolvedTheme})`
                        : `Always use the ${preference} OpsAI theme`}
                    </small>
                  </span>
                </label>
              ))}
            </fieldset>
            <p className="theme-storage-note">
              Saved only in this browser. No account or infrastructure setting is changed.
            </p>
          </div>
        )}
        {tab === 'Security' && (
          <Suspense fallback={<LoadingState />}>
            <PendingMemberships />
            <SecuritySettings
              role={session.user.role}
              onSignedOut={onSignedOut}
              onSessionRefresh={onSessionRefresh}
            />
          </Suspense>
        )}
        {(tab === 'Branding' ||
          tab === 'Domains' ||
          tab === 'Reports Branding' ||
          tab === 'Preview') && (
          <Suspense fallback={<LoadingState />}>
            <WhiteLabelSettings
              initialTab={tab}
              onBrandingChanged={onBrandingChanged}
              canManage={
                session.user.role === 'tenant_owner' ||
                session.user.role === 'tenant_admin' ||
                session.user.role === 'aot_admin' ||
                session.user.role === 'aot_operator'
              }
            />
          </Suspense>
        )}
        {tab === 'Notifications' && settings && (
          <form className="settings-form" onSubmit={save}>
            <label className="toggle-row">
              <span>
                <strong>Email incident alerts</strong>
                <small>Enable delivery using the configured provider.</small>
              </span>
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              />
            </label>
            <label>
              Minimum severity
              <select
                value={settings.minimumSeverity}
                onChange={(e) => setSettings({ ...settings, minimumSeverity: e.target.value })}
              >
                <option>info</option>
                <option>warning</option>
                <option>critical</option>
              </select>
            </label>
            <label>
              Recipients
              <input
                value={(settings.recipients ?? []).join(', ')}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    recipients: e.target.value
                      .split(',')
                      .map((value: string) => value.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="operations@example.com"
              />
            </label>
            <div className="form-actions">
              <button className="button primary">Save notification policy</button>
              <button className="button subtle" type="button" onClick={sendNotificationTest}>
                Send test notification
              </button>
            </div>
          </form>
        )}
        {tab === 'Edge Agents' &&
          (agents === null ? (
            <LoadingState rows={3} />
          ) : agents.length ? (
            <DataTable label="Edge agents">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>State</th>
                  <th>Version</th>
                  <th>Configuration</th>
                  <th>Last heartbeat</th>
                  <th>Clock / timezone</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent) => (
                  <tr key={agent.id}>
                    <td>
                      <strong>{customerFacingEdgeName(agent)}</strong>
                    </td>
                    <td>
                      <StatusBadge value={agent.status} />
                    </td>
                    <td>{agent.version ?? 'Not reported'}</td>
                    <td>
                      <StatusBadge
                        value={agent.configError ? 'warning' : 'healthy'}
                        label={agent.configError ? 'Attention required' : 'Synchronized'}
                      />
                    </td>
                    <td>{relativeTime(agent.lastHeartbeatAt)}</td>
                    <td>
                      <strong>
                        {Math.abs(Number(agent.clockOffsetMs ?? 0)) > 5_000
                          ? 'Clock warning'
                          : 'Synchronized'}
                      </strong>
                      <small>
                        {Number(agent.clockOffsetMs ?? 0) >= 0 ? '+' : ''}
                        {Number(agent.clockOffsetMs ?? 0)} ms · {agent.operationalTimezone ?? 'UTC'}
                      </small>
                    </td>
                    <td>
                      {agent.configurationStatus === 'rejected' && agent.lastGoodVersion ? (
                        <button
                          className="button subtle"
                          onClick={async () => {
                            await api(`/v1/operations/agents/${agent.id}/configuration/rollback`, {
                              method: 'POST',
                            });
                            setMessage('Rollback to the last known good configuration was queued.');
                            const result = await api<any>('/v1/operations/agents');
                            setAgents(result.items);
                          }}
                        >
                          Rollback
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <EmptyState
              title="No Edge agents enrolled"
              description="Enroll an authorized Edge agent to establish outbound monitoring connectivity."
            />
          ))}
      </SectionCard>
    </>
  );
}

function CompactAssetTable({ items }: { items: any[] }) {
  return items.length ? (
    <DataTable label="Site assets">
      <thead>
        <tr>
          <th>Asset</th>
          <th>Type</th>
          <th>Platform</th>
          <th>Health</th>
          <th>Last seen</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id}>
            <td>
              <span className="asset-identity">
                <AssetVisual asset={item} size={26} status={item.health} />
                <strong>{item.displayName ?? item.name}</strong>
              </span>
            </td>
            <td>{item.assetType}</td>
            <td>
              <AssetPlatform item={item} />
            </td>
            <td>
              <StatusBadge value={item.health} />
            </td>
            <td>{relativeTime(item.lastSeenAt)}</td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  ) : (
    <EmptyState
      title="No infrastructure connected"
      description="Connect an authorized Edge collector to populate this site."
    />
  );
}
function CompactIncidentTable({ items }: { items: any[] }) {
  return items.length ? (
    <DataTable label="Operational incidents">
      <thead>
        <tr>
          <th>Severity</th>
          <th>Summary</th>
          <th>Status</th>
          <th>Updated</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id}>
            <td>
              <StatusBadge value={item.severity} />
            </td>
            <td>
              <strong>{customerFacingEntityName(item.title)}</strong>
            </td>
            <td>
              <StatusBadge value={item.status} />
            </td>
            <td>{formatDate(item.updatedAt ?? item.updated_at)}</td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  ) : (
    <EmptyState
      title="No open incidents"
      description="No incident records are associated with this scope."
    />
  );
}
function UsageMeter({ label, data }: any) {
  const width = Math.min(100, (Number(data.used) / Math.max(1, Number(data.limit))) * 100);
  return (
    <div className="usage-meter">
      <div>
        <strong>{label}</strong>
        <span>
          {data.used} / {data.limit}
        </span>
      </div>
      <div>
        <i style={{ width: `${width}%` }} />
      </div>
      <small>{data.state}</small>
    </div>
  );
}
function groupHistory(items: any[]) {
  const groups = new Map<string, any[]>();
  for (const item of items) {
    const metric = canonicalMetricId(String(item.metric));
    const key = `${item.assetId ?? ''}-${metric}`;
    groups.set(key, [...(groups.get(key) ?? []), { ...item, metric }]);
  }
  return [...groups.entries()].map(([key, points]) => ({
    key,
    metric: points[0]?.metric,
    label: `${points[0]?.assetName ? `${points[0].assetName} · ` : ''}${points[0]?.metric}`,
    points,
  }));
}
function HistoryChart({ metric, points }: { metric: string; points: any[] }) {
  const values = points.map((point) => Number(point.avg));
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const span = Math.max(1, maximum - minimum);
  const polyline = values
    .map(
      (value, index) =>
        `${(index / Math.max(1, values.length - 1)) * 300},${82 - ((value - minimum) / span) * 68}`,
    )
    .join(' ');
  return (
    <div className="history-chart">
      <div>
        <strong>{metric}</strong>
        <span>
          {values.at(-1)?.toFixed(1)} {points.at(-1)?.unit}
        </span>
      </div>
      <svg
        role="img"
        aria-label={`${metric} history`}
        viewBox="0 0 300 90"
        preserveAspectRatio="none"
      >
        <line x1="0" y1="82" x2="300" y2="82" />
        <polyline points={polyline} />
      </svg>
    </div>
  );
}
function duration(start: string, end: string) {
  const milliseconds = Math.max(0, new Date(end).getTime() - new Date(start).getTime());
  const minutes = Math.floor(milliseconds / 60_000);
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

function App() {
  const [invitationSignIn, setInvitationSignIn] = useState(false);
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [branding, setBranding] = useState<PublicBranding | undefined>(undefined);
  const [route, setRoute] = useState(() =>
    parsePortalRoute(window.location.pathname, window.location.search),
  );
  const [menu, setMenu] = useState(false);
  const [question, setQuestion] = useState('');
  const [commandOpen, setCommandOpen] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [themePreference, setThemePreference] = useState<ThemePreference>(() =>
    readThemePreference(window.localStorage),
  );
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  );
  const resolvedTheme = resolveTheme(themePreference, systemPrefersDark);
  const timezone = useMemo(
    () =>
      createTimezoneState({
        timezoneMode: session?.user.timezoneMode,
        timeZone: session?.user.timeZone,
        workspaceTimeZone: session?.tenant.timeZone,
      }),
    [session?.user.timezoneMode, session?.user.timeZone, session?.tenant.timeZone],
  );
  setActiveTimeZone(timezone.resolvedTimeZone);
  const liveRefresh = useLiveRefresh(route.view, () => setRefreshVersion((current) => current + 1));
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    return observeSystemTheme(media, setSystemPrefersDark);
  }, []);
  const refreshSession = useCallback(async () => {
    const value = await api<Session>('/v1/auth/me');
    setSession(value);
  }, []);
  useEffect(() => {
    persistThemePreference(themePreference, window.localStorage);
    applyThemeToDocument(themePreference, resolvedTheme);
  }, [themePreference, resolvedTheme]);
  const loadBranding = useCallback(
    () =>
      api<PublicBranding>('/v1/branding/public')
        .then((value) => {
          setBranding(value);
          applyBranding(value);
        })
        .catch(() => {
          setBranding(DEFAULT_BRANDING);
          applyBranding(DEFAULT_BRANDING);
        }),
    [],
  );
  useEffect(() => {
    void loadBranding();
  }, [loadBranding]);
  useEffect(() => {
    const product = branding?.displayName ?? 'AOT OpsAI';
    document.title = session === null ? `${product} — Sign in` : `${product} — ${route.view}`;
  }, [route.view, session, branding]);
  useEffect(() => {
    api<Session>('/v1/auth/me')
      .then(async (value) => {
        if (!sessionStorage.getItem('aot_csrf')) {
          const restored = await api<{ csrfToken: string }>('/v1/auth/csrf', { method: 'POST' });
          sessionStorage.setItem('aot_csrf', restored.csrfToken);
        }
        setSession(value);
      })
      .catch(() => setSession(null));
  }, []);
  useEffect(() => {
    if (!session) return;
    void api<any>('/v1/asset-visual-registry')
      .then((value) => {
        setRuntimeAssetVisualRegistry(value.items ?? []);
        setRefreshVersion((current) => current + 1);
      })
      .catch(() => setRuntimeAssetVisualRegistry([]));
  }, [session?.tenant.id]);
  useEffect(() => {
    const popstate = () => {
      setRoute(parsePortalRoute(window.location.pathname, window.location.search));
      const scrollY = Number(window.history.state?.assetListScrollY);
      if (Number.isFinite(scrollY))
        window.setTimeout(() => window.scrollTo({ top: scrollY, behavior: 'auto' }), 50);
    };
    window.addEventListener('popstate', popstate);
    return () => window.removeEventListener('popstate', popstate);
  }, []);
  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, []);
  useEffect(() => {
    if (
      session &&
      ['AOT Operations', 'Customer Onboarding'].includes(route.view) &&
      !session.user.role.startsWith('aot_')
    ) {
      navigate('Overview', { replace: true });
    }
  }, [session, route.view]);
  useEffect(() => {
    if (session?.security?.mfaEnrollmentRequired && route.view !== 'Settings') {
      navigate('Settings', { replace: true });
    }
  }, [session, route.view]);
  if (session === undefined || branding === undefined)
    return (
      <div className="splash">
        {branding?.tenantDomain ? <TenantBrandLockup branding={branding} /> : <AotProductLockup />}
        <span>Loading operations workspace</span>
      </div>
    );
  if (arrivalInvitationToken && (!session ? !invitationSignIn : !session.security?.mfaEnrollmentRequired))
    return <AcceptInvitation token={arrivalInvitationToken} signedIn={Boolean(session)} onSignIn={() => setInvitationSignIn(true)} />;
  if (!session)
    return (
      <Login
        onLogin={setSession}
        resolvedTheme={resolvedTheme}
        onToggleTheme={() => setThemePreference(nextExplicitTheme(resolvedTheme))}
        branding={branding}
      />
    );
  const view = route.view as View;
  const isAotOperator = session.user.role === 'aot_admin' && session.tenant.accessClass === 'AOT_INTERNAL';
  const tenantBranding = branding.tenantDomain && !isAotOperator;
  const navigation = isAotOperator ? aotNavigation : primaryNavigation;
  function navigate(next: View, options: NavigateOptions = {}) {
    if (
      ['AOT Operations', 'Customer Onboarding'].includes(next) &&
      session &&
      !session.user.role.startsWith('aot_')
    )
      return;
    const nextRoute = {
      view: next,
      detailId: options.detailId,
      topologyAssetId: options.topologyAssetId,
      topologyServiceId: options.topologyServiceId,
    };
    setRoute(nextRoute);
    setQuestion(options.question ?? '');
    const path = portalPath(next, options.detailId, options.topologyAssetId, {
      ...options.query,
      ...(options.context ? { context: options.context } : {}),
      ...(options.topologyServiceId ? { service: options.topologyServiceId } : {}),
    });
    window.history[options.replace ? 'replaceState' : 'pushState'](null, '', path);
    setMenu(false);
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }
  async function logout() {
    await api('/v1/auth/logout', { method: 'POST' }).catch(() => undefined);
    sessionStorage.removeItem('aot_csrf');
    setSession(null);
  }
  if (view === 'NOC Wallboard')
    return (
      <NocWallboardView
        isAotOperator={isAotOperator}
        tenantName={isAotOperator ? 'AOT Operations' : branding.displayName}
        onExit={() => navigate('Overview')}
      />
    );
  return (
    <div className="app-shell">
      <aside className={menu ? 'open' : ''}>
        <div className="aside-brand">
          {tenantBranding ? (
            <TenantBrandLockup branding={branding} dark={resolvedTheme === 'dark'} compact />
          ) : (
            <AotProductLockup compact />
          )}
          <button
            className="icon-button close"
            aria-label="Close navigation"
            onClick={() => setMenu(false)}
          >
            <X size={19} />
          </button>
        </div>
        <nav aria-label="Primary navigation">
          {navigation.map(({ view: destination, label, icon: Icon }) => (
            <a
              className={view === destination ? 'active' : ''}
              key={destination}
              href={portalPath(destination)}
              aria-current={view === destination ? 'page' : undefined}
              onClick={(event) => {
                if (
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                )
                  return;
                event.preventDefault();
                navigate(destination);
              }}
            >
              <Icon size={18} />
              {label ?? destination}
              {view === destination && <ChevronRight size={14} className="nav-chevron" />}
            </a>
          ))}
        </nav>
        <div className="workspace-card">
          <span>{isAotOperator ? 'AOT' : 'CLIENT'}</span>
          <div>
            <strong>{isAotOperator ? 'AOT Operations' : branding.displayName}</strong>
            <small>{roleLabel(session.user.role)}</small>
          </div>
        </div>
        <button className="logout-button" onClick={logout}>
          <LogOut size={17} />
          Sign out
        </button>
      </aside>
      <main className="main">
        <WorkspaceSwitcher tenantId={session.tenant.id} role={session.user.role} name={session.tenant.name ?? 'Workspace'} />
        <header className="global-header">
          <button
            className="icon-button mobile-menu"
            aria-label="Open navigation"
            onClick={() => setMenu(true)}
          >
            <Menu size={20} />
          </button>
          <div className="mobile-product-lockup">
            {tenantBranding ? (
              <TenantBrandLockup branding={branding} dark={resolvedTheme === 'dark'} compact />
            ) : (
              <AotProductLockup compact />
            )}
          </div>
          <div className="header-tenant">
            <span>{isAotOperator ? 'AOT Operations' : branding.displayName}</span>
            <small>Managed infrastructure operations</small>
          </div>
          <div className="header-actions">
            <button
              className="command-trigger"
              aria-label="Search or command"
              onClick={() => setCommandOpen(true)}
            >
              <Search size={15} aria-hidden="true" />
              <span>Search or command</span>
              <kbd>{navigator.platform.includes('Mac') ? '⌘ K' : 'Ctrl K'}</kbd>
            </button>
            <ThemeQuickToggle
              resolvedTheme={resolvedTheme}
              onToggle={() => setThemePreference(nextExplicitTheme(resolvedTheme))}
            />
            <label
              className="timezone-quick-control"
              title={`Timestamps shown in ${timezone.resolvedTimeZone}`}
            >
              <span className="sr-only">Display timezone</span>
              <select
                aria-label="Display timezone"
                value={
                  session.user.timezoneMode === 'SPECIFIC'
                    ? (session.user.timeZone ?? 'UTC')
                    : (session.user.timezoneMode ?? 'AUTO_BROWSER')
                }
                onChange={async (event) => {
                  const value = event.target.value;
                  await api('/v1/settings/regional-time/user', {
                    method: 'PUT',
                    body: JSON.stringify(
                      value === 'AUTO_BROWSER' || value === 'WORKSPACE_DEFAULT'
                        ? { timezoneMode: value, timezone: null }
                        : { timezoneMode: 'SPECIFIC', timezone: value },
                    ),
                  });
                  await refreshSession();
                }}
              >
                <option value="AUTO_BROWSER">Auto · {timezone.browserTimeZone}</option>
                <option value="WORKSPACE_DEFAULT">Workspace · {timezone.workspaceTimeZone}</option>
                <option value="UTC">UTC</option>
                {[
                  timezone.browserTimeZone,
                  timezone.workspaceTimeZone,
                  'Asia/Manila',
                  'Asia/Tokyo',
                  'America/New_York',
                  'America/Los_Angeles',
                  'Europe/London',
                ]
                  .filter((value, index, all) => value !== 'UTC' && all.indexOf(value) === index)
                  .map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
              </select>
            </label>
            <FreshnessIndicator
              value={liveRefresh.lastUpdated}
              state={liveRefresh.state}
              onRefresh={liveRefresh.refresh}
            />
            {isStaging() && <span className="environment-badge">STAGING</span>}
            <div className="user-menu">
              <span>{session.user.email.slice(0, 1).toUpperCase()}</span>
              <div>
                <strong>{session.user.email}</strong>
                <small>{roleLabel(session.user.role)}</small>
              </div>
            </div>
          </div>
        </header>
        <div className="page" key={`${view}-${route.detailId ?? ''}`}>
          <div className="print-brand">
            <AotProductLockup tagline />
          </div>
          {view === 'Overview' && (
            <OverviewView navigate={navigate} refreshVersion={refreshVersion} />
          )}
          {view === 'Sites' && (
            <SitesView
              initialSiteId={route.detailId}
              navigate={navigate}
              role={session.user.role}
              refreshVersion={refreshVersion}
            />
          )}
          {view === 'Edges' && (
            <Suspense fallback={<LoadingState rows={8} />}>
              <EdgesView
                initialEdgeId={route.detailId}
                role={session.user.role}
                refreshVersion={refreshVersion}
              />
            </Suspense>
          )}
          {view === 'Assets' && (
            <AssetsView
              initialAssetId={route.detailId}
              initialSelectedId={
                new URLSearchParams(window.location.search).get('selected') ?? undefined
              }
              navigate={navigate}
              onOpsAI={(value) => navigate('OpsAI', { question: value, context: 'asset' })}
              refreshVersion={refreshVersion}
              role={session.user.role}
            />
          )}
          {view === 'Logs' && (
            <Suspense fallback={<LoadingState rows={8} />}>
              <LogsView refreshVersion={refreshVersion} />
            </Suspense>
          )}
          {view === 'Integrations' && (
            <Suspense fallback={<LoadingState rows={8} />}>
              <IntegrationsView refreshVersion={refreshVersion} role={session.user.role} />
            </Suspense>
          )}
          {view === 'Maps' && (
            <Suspense fallback={<LoadingState rows={8} />}>
              <MapsView role={session.user.role} />
            </Suspense>
          )}
          {view === 'Topology' && (
            <TopologyRoute
              initialAssetId={route.topologyAssetId}
              initialServiceId={route.topologyServiceId}
              navigate={navigate}
            />
          )}
          {view === 'Incidents' && (
            <IncidentsView
              initialIncidentId={route.detailId}
              navigate={navigate}
              refreshVersion={refreshVersion}
              onOpsAI={(value, incidentId) =>
                navigate('OpsAI', {
                  question: value,
                  detailId: incidentId,
                  context: 'incident',
                })
              }
            />
          )}
          {view === 'Services' && (
            <ServicesView
              initialServiceId={route.detailId}
              navigate={navigate}
              refreshVersion={refreshVersion}
            />
          )}
          {view === 'Capacity' && (
            <CapacityView navigate={navigate} refreshVersion={refreshVersion} />
          )}
          {view === 'Reports' && <ReportsView role={session.user.role} />}
          {view === 'OpsAI' && (
            <OpsAIView
              initialQuestion={question}
              incidentId={route.detailId}
              context={
                (new URLSearchParams(window.location.search).get('context') as
                  'dashboard' | 'incident' | 'asset' | 'service' | 'capacity' | null) ??
                (route.detailId ? 'incident' : 'dashboard')
              }
            />
          )}
          {view === 'Knowledge' && <KnowledgeView />}
          {view === 'Settings' && (
            <SettingsView
              session={session}
              refreshVersion={refreshVersion}
              themePreference={themePreference}
              resolvedTheme={resolvedTheme}
              onThemePreferenceChange={setThemePreference}
              onSignedOut={() => setSession(null)}
              onSessionRefresh={refreshSession}
              onBrandingChanged={loadBranding}
            />
          )}
          {view === 'AOT Operations' && isAotOperator && (
            <AotOperationsView
              initialCustomerId={route.detailId}
              navigate={navigate}
              role={session.user.role}
            />
          )}
          {view === 'Customer Onboarding' && isAotOperator && (
            <Suspense fallback={<LoadingState rows={6} />}>
              <CustomerOnboarding
                initialProjectId={route.detailId}
                role={session.user.role}
                onOpenProject={(id) => navigate('Customer Onboarding', { detailId: id })}
                onClose={() => navigate('AOT Operations')}
              />
            </Suspense>
          )}
        </div>
      </main>
      {!isAotOperator && (
        <nav className="mobile-bottom-nav" aria-label="Mobile navigation">
          {primaryNavigation.slice(0, 5).map(({ view: destination, icon: Icon }) => (
            <a
              key={destination}
              href={portalPath(destination)}
              className={view === destination ? 'active' : ''}
              aria-current={view === destination ? 'page' : undefined}
              onClick={(event) => {
                if (
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                )
                  return;
                event.preventDefault();
                navigate(destination);
              }}
            >
              <Icon size={18} aria-hidden="true" />
              <span>{destination}</span>
            </a>
          ))}
        </nav>
      )}
      {menu && (
        <button className="backdrop" aria-label="Close navigation" onClick={() => setMenu(false)} />
      )}
      {commandOpen && (
        <Suspense fallback={null}>
          <CommandPalette
            open
            isAotOperator={isAotOperator}
            onClose={() => setCommandOpen(false)}
            onSelect={(result: CommandResult) =>
              navigate(result.view as View, {
                detailId: result.entityId,
                question: result.question,
              })
            }
          />
        </Suspense>
      )}
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
